
  import { z } from "zod";

// <Schemas>
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;
export const ErrorEnvelope = z.object({ status: z.boolean(), message: z.string(), data: z.null().optional(), timestamp: z.iso.datetime(), code: z.string().optional(), details: z.unknown().optional() });

export type AuthMiddlewareError = z.infer<typeof AuthMiddlewareError>;
export const AuthMiddlewareError = z.object({ message: z.string() });

export type ApiResponse = z.infer<typeof ApiResponse>;
export const ApiResponse = z.object({ status: z.boolean(), message: z.string(), data: z.unknown(), timestamp: z.iso.datetime() });

export type JsonObject = z.infer<typeof JsonObject>;
export const JsonObject = z.record(z.string(), z.unknown());

export type ActivityCategoryDto = z.infer<typeof ActivityCategoryDto>;
export const ActivityCategoryDto = z.object({ description: z.string().nullable(), id: z.string(), isActive: z.boolean(), isDefault: z.boolean(), name: z.string() });

export type ActivityTaskDependencyDto = z.infer<typeof ActivityTaskDependencyDto>;
export const ActivityTaskDependencyDto = z.object({ dependsOnTaskId: z.string(), slackMinutes: z.number(), taskId: z.string() });

export type ActivityTaskStage = z.infer<typeof ActivityTaskStage>;
export const ActivityTaskStage = z.enum(["pre", "exec", "post", "rollback"]);

export type ActivityTaskDto = z.infer<typeof ActivityTaskDto>;
export const ActivityTaskDto = z.object({ activityId: z.string(), assignedUserId: z.string().nullable(), description: z.string().nullable(), durationMinutes: z.number().nullable(), endAt: z.string().nullable(), id: z.string(), isParallel: z.boolean(), parentTaskId: z.string().nullable(), sequence: z.number(), stage: ActivityTaskStage, startAt: z.string().nullable(), title: z.string() });

export type ActivityTaskPlanDto = z.infer<typeof ActivityTaskPlanDto>;
export const ActivityTaskPlanDto = z.object({ activityId: z.string(), dependencies: z.array(ActivityTaskDependencyDto), planningRevision: z.number(), tasks: z.array(ActivityTaskDto) });

export type AssignedRoomClientDto = z.infer<typeof AssignedRoomClientDto>;
export const AssignedRoomClientDto = z.object({ id: z.string(), name: z.string() });

export type AssignedUserSummaryDto = z.infer<typeof AssignedUserSummaryDto>;
export const AssignedUserSummaryDto = z.object({ displayName: z.string(), email: z.string(), id: z.string(), status: z.enum(["active", "deactivated"]), userType: z.enum(["internal", "external"]) });

export type AutoClassifyOutcomeDto = z.infer<typeof AutoClassifyOutcomeDto>;
export const AutoClassifyOutcomeDto = z.object({ autoSignal: z.string().optional(), outcome: z.enum(["assigned", "skipped_not_found", "skipped_missing_tier"]), tierId: z.string().optional(), vmId: z.string() });

export type AvailabilityTierDto = z.infer<typeof AvailabilityTierDto>;
export const AvailabilityTierDto = z.object({ configurationId: z.string(), createdAt: z.string(), criticalityRank: z.number(), id: z.string(), measurementMode: z.enum(["individual", "aggregate"]), name: z.string(), scheduleType: z.enum(["seven_by_twenty_four", "business_hours"]), targetPercentage: z.number(), updatedAt: z.string() });

export type AvailabilityConfigurationDto = z.infer<typeof AvailabilityConfigurationDto>;
export const AvailabilityConfigurationDto = z.object({ clientId: z.string(), createdAt: z.string(), createdBy: z.string().nullable(), id: z.string(), name: z.string(), status: z.enum(["active", "inactive"]), tiers: z.array(AvailabilityTierDto).optional(), updatedAt: z.string(), year: z.number() });

export type AvailabilityHolidayDto = z.infer<typeof AvailabilityHolidayDto>;
export const AvailabilityHolidayDto = z.object({ holidayDate: z.string(), id: z.string(), name: z.string(), year: z.number() });

export type AvailabilityInterruptionDto = z.infer<typeof AvailabilityInterruptionDto>;
export const AvailabilityInterruptionDto = z.object({ configurationId: z.string(), createdAt: z.string(), createdBy: z.string().nullable(), durationMinutes: z.number(), endAt: z.string(), id: z.string(), notes: z.string().nullable(), startAt: z.string(), updatedAt: z.string(), vmId: z.string() });

export type AvailabilityReportDto = z.infer<typeof AvailabilityReportDto>;
export const AvailabilityReportDto = z.object({ calculatedAt: z.string(), calculatedBy: z.string().nullable(), configurationId: z.string(), createdAt: z.string(), holidaysConsidered: z.unknown(), id: z.string(), payload: z.unknown(), status: z.literal("draft"), timezoneUsed: z.string(), updatedAt: z.string(), yearMonth: z.string() });

export type AvailabilityVmAssignmentDto = z.infer<typeof AvailabilityVmAssignmentDto>;
export const AvailabilityVmAssignmentDto = z.object({ autoSignal: z.string().nullable(), basis: z.enum(["manual", "auto"]), configurationId: z.string(), createdAt: z.string(), id: z.string(), tierId: z.string(), updatedAt: z.string(), vmId: z.string() });

export type BaselineActivitySearchItemDto = z.infer<typeof BaselineActivitySearchItemDto>;
export const BaselineActivitySearchItemDto = z.object({ activityText: z.string().nullable(), changeRequestNumber: z.string(), endAt: z.string(), id: z.string(), platformCategoryName: z.string().nullable(), platformName: z.string().nullable(), specialistName: z.string().nullable(), startAt: z.string(), status: z.string(), vmCount: z.number() });

export type Record_string_unknown = z.infer<typeof Record_string_unknown>;
export const Record_string_unknown = z.record(z.string(), z.unknown());

export type StandbyComputedStatusDto = z.infer<typeof StandbyComputedStatusDto>;
export const StandbyComputedStatusDto = z.enum(["active", "scheduled", "completed", "cancelled"]);

export type StandbyAssignmentStatusDto = z.infer<typeof StandbyAssignmentStatusDto>;
export const StandbyAssignmentStatusDto = z.enum(["active", "inactive", "cancelled", "deleted"]);

export type StandbyAssignmentDto = z.infer<typeof StandbyAssignmentDto>;
export const StandbyAssignmentDto = z.object({ assignedBy: z.string(), clientId: z.string().nullable(), clientName: z.string().nullable(), computedStatus: StandbyComputedStatusDto.optional(), createdAt: z.string(), deletedAt: z.string().nullable(), endAt: z.string().nullable(), escalationLevel: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(null)]).nullable(), id: z.string(), notes: z.string().nullable().optional(), platformId: z.number().nullable(), platformName: z.string().nullable(), shiftSummary: z.string().nullable().optional(), startAt: z.string().nullable(), status: StandbyAssignmentStatusDto, updatedAt: z.string(), userDisplayName: z.string(), userId: z.string(), version: z.number() });

export type BulkCreateStandbyAssignmentsResultDto = z.infer<typeof BulkCreateStandbyAssignmentsResultDto>;
export const BulkCreateStandbyAssignmentsResultDto = z.object({ assignments: z.array(StandbyAssignmentDto), created: z.number() });

export type ChangeBatchStatusDto = z.infer<typeof ChangeBatchStatusDto>;
export const ChangeBatchStatusDto = z.enum(["active", "cancelled"]);

export type ChangeBatchDetailDto = z.infer<typeof ChangeBatchDetailDto>;
export const ChangeBatchDetailDto = z.object({ childActivityCount: z.number(), childActivityIds: z.array(z.string()), clientId: z.string(), copyVms: z.boolean(), createdAt: z.string(), createdBy: z.string().nullable(), dates: z.array(z.string()), deriveSupport: z.boolean(), id: z.string(), masterActivityRef: z.string().nullable(), name: z.string(), status: ChangeBatchStatusDto, templateId: z.string().nullable(), updatedAt: z.string(), version: z.number() });

export type ChangeBatchSummaryDto = z.infer<typeof ChangeBatchSummaryDto>;
export const ChangeBatchSummaryDto = z.object({ childActivityCount: z.number(), clientId: z.string(), copyVms: z.boolean(), createdAt: z.string(), createdBy: z.string().nullable(), dates: z.array(z.string()), deriveSupport: z.boolean(), id: z.string(), masterActivityRef: z.string().nullable(), name: z.string(), status: ChangeBatchStatusDto, templateId: z.string().nullable(), updatedAt: z.string() });

export type ChangeTemplateDependencyDto = z.infer<typeof ChangeTemplateDependencyDto>;
export const ChangeTemplateDependencyDto = z.object({ dependsOnTaskId: z.string(), slackMinutes: z.number(), taskId: z.string() });

export type ChangeTemplateTaskDto = z.infer<typeof ChangeTemplateTaskDto>;
export const ChangeTemplateTaskDto = z.object({ assignedUserId: z.string().nullable(), description: z.string().nullable(), durationMinutes: z.number().nullable(), id: z.string(), isParallel: z.boolean(), parentTemplateTaskId: z.string().nullable(), sequence: z.number(), stage: ActivityTaskStage, startOffsetMinutes: z.number().nullable(), taskKey: z.string(), templateId: z.string(), title: z.string() });

export type ChangeTemplateDetailDto = z.infer<typeof ChangeTemplateDetailDto>;
export const ChangeTemplateDetailDto = z.object({ clientId: z.string(), dependencies: z.array(ChangeTemplateDependencyDto), description: z.string().nullable(), id: z.string(), isActive: z.boolean(), masterActivityRef: z.string().nullable(), name: z.string(), platformCategoryRef: z.string().nullable(), rollbackApplicable: z.boolean().nullable(), rollbackJustification: z.string().nullable(), rollbackTrigger: z.string().nullable(), rollbackValidation: z.string().nullable(), sourceActivityId: z.string().nullable(), tasks: z.array(ChangeTemplateTaskDto), updatedAt: z.string(), version: z.number(), virtualMachineIds: z.array(z.string()) });

export type ChangeTemplateSummaryDto = z.infer<typeof ChangeTemplateSummaryDto>;
export const ChangeTemplateSummaryDto = z.object({ clientId: z.string(), description: z.string().nullable(), id: z.string(), isActive: z.boolean(), name: z.string(), updatedAt: z.string(), version: z.number() });

export type ClientApproverDto = z.infer<typeof ClientApproverDto>;
export const ClientApproverDto = z.object({ email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), roleName: z.string().nullable(), username: z.string() });

export type ClientCompanyDetailDto = z.infer<typeof ClientCompanyDetailDto>;
export const ClientCompanyDetailDto = z.object({ clientId: z.string(), datacenterLinkCount: z.number(), description: z.string().nullable(), deviceCount: z.number(), id: z.string(), logoRef: z.string().nullable(), name: z.string(), nit: z.string().nullable(), status: z.string(), version: z.number(), vmCount: z.number() });

export type ClientCompanySummaryDto = z.infer<typeof ClientCompanySummaryDto>;
export const ClientCompanySummaryDto = z.object({ clientId: z.string(), datacenterLinkCount: z.number(), deviceCount: z.number(), id: z.string(), name: z.string(), nit: z.string().nullable(), status: z.string(), version: z.number(), vmCount: z.number() });

export type RoomStatusDto = z.infer<typeof RoomStatusDto>;
export const RoomStatusDto = z.enum(["active", "inactive"]);

export type RoomSummaryDto = z.infer<typeof RoomSummaryDto>;
export const RoomSummaryDto = z.object({ assignedClientCount: z.number(), code: z.string(), datacenterId: z.string(), id: z.string(), isWarehouse: z.boolean(), name: z.string(), rackCount: z.number(), racksPath: z.string(), status: RoomStatusDto });

export type ClientContextRoomsDto = z.infer<typeof ClientContextRoomsDto>;
export const ClientContextRoomsDto = z.object({ items: z.array(RoomSummaryDto) });

export type ClientDeleteImpactDto = z.infer<typeof ClientDeleteImpactDto>;
export const ClientDeleteImpactDto = z.object({ assignedUserCount: z.number(), clientId: z.string(), datacenterAssignmentCount: z.number(), message: z.string() });

export type ClientReferenceDto = z.infer<typeof ClientReferenceDto>;
export const ClientReferenceDto = z.object({ code: z.string().optional(), id: z.string(), name: z.string() });

export type DatacenterAssignmentRoleDto = z.infer<typeof DatacenterAssignmentRoleDto>;
export const DatacenterAssignmentRoleDto = z.enum(["primary", "disaster_recovery", "associated"]);

export type DatacenterAssignmentDto = z.infer<typeof DatacenterAssignmentDto>;
export const DatacenterAssignmentDto = z.object({ datacenterId: z.string(), kind: z.enum(["provider", "client_owned"]).optional(), name: z.string(), role: DatacenterAssignmentRoleDto, status: z.enum(["active", "inactive"]) });

export type DatacenterSummaryDto = z.infer<typeof DatacenterSummaryDto>;
export const DatacenterSummaryDto = z.object({ associatedCount: z.number(), disasterRecoveryCount: z.number(), primaryCount: z.number() });

export type ClientDetailDto = z.infer<typeof ClientDetailDto>;
export const ClientDetailDto = z.object({ address: z.string().nullable(), assignedUserCount: z.number(), assignedUsers: z.array(AssignedUserSummaryDto).optional(), country: ClientReferenceDto, countryCode: z.string().nullable(), countryName: z.string().nullable(), datacenterAssignments: z.array(DatacenterAssignmentDto), datacenterCount: z.number(), datacenterSummary: DatacenterSummaryDto, deliveryUnit: ClientReferenceDto, deliveryUnitId: z.string(), enterprise: ClientReferenceDto, enterpriseId: z.string().nullable(), enterpriseName: z.string().nullable(), id: z.string(), isGroup: z.boolean(), logo: z.string().nullable(), logoRef: z.string().nullable().optional(), name: z.string(), nit: z.string(), practiceUnit: ClientReferenceDto, practiceUnitId: z.string(), status: z.enum(["active", "inactive"]), version: z.number() });

export type ClientOwnedDatacenterDetailDto = z.infer<typeof ClientOwnedDatacenterDetailDto>;
export const ClientOwnedDatacenterDetailDto = z.object({ address: z.string().nullable(), city: z.string().nullable(), clientId: z.string(), clientName: z.string().nullable().optional(), countryCode: z.string().nullable(), countryName: z.string().nullable().optional(), id: z.string(), name: z.string(), notes: z.string().nullable(), role: z.union([z.literal("primary"), z.literal("disaster_recovery"), z.literal("associated"), z.literal(null)]).nullable(), roomCount: z.number().optional(), status: z.string(), vmCount: z.number().optional() });

export type ClientOwnedDatacenterDto = z.infer<typeof ClientOwnedDatacenterDto>;
export const ClientOwnedDatacenterDto = z.object({ address: z.string().nullable(), city: z.string().nullable(), clientId: z.string(), countryCode: z.string().nullable(), id: z.string(), name: z.string(), notes: z.string().nullable(), role: z.union([z.literal("primary"), z.literal("disaster_recovery"), z.literal("associated"), z.literal(null)]).nullable(), roomCount: z.number().optional(), status: z.string(), vmCount: z.number().optional() });

export type ClientOwnedRackDto = z.infer<typeof ClientOwnedRackDto>;
export const ClientOwnedRackDto = z.object({ id: z.string(), name: z.string(), ownedRoomId: z.string(), serial: z.string().nullable(), status: z.string(), units: z.number().nullable() });

export type ClientOwnedRoomDto = z.infer<typeof ClientOwnedRoomDto>;
export const ClientOwnedRoomDto = z.object({ id: z.string(), name: z.string(), notes: z.string().nullable(), ownedDatacenterId: z.string(), status: z.string() });

export type ClientSummaryDto = z.infer<typeof ClientSummaryDto>;
export const ClientSummaryDto = z.object({ address: z.string().nullable().optional(), assignedUserCount: z.number(), country: ClientReferenceDto, countryCode: z.string().nullable(), countryName: z.string().nullable(), datacenterCount: z.number(), datacenterSummary: DatacenterSummaryDto, deliveryUnit: ClientReferenceDto, deliveryUnitId: z.string(), enterprise: ClientReferenceDto, enterpriseId: z.string().nullable(), enterpriseName: z.string().nullable(), id: z.string(), isGroup: z.boolean(), logoRef: z.string().nullable().optional(), name: z.string(), nit: z.string(), practiceUnit: ClientReferenceDto, practiceUnitId: z.string(), status: z.enum(["active", "inactive"]), version: z.number() });

export type ClientToolAssignmentDto = z.infer<typeof ClientToolAssignmentDto>;
export const ClientToolAssignmentDto = z.object({ clientId: z.string(), id: z.string(), isPrimary: z.boolean(), ticketingToolId: z.string(), toolSource: z.string() });

export type CompanyDatacenterLinkDto = z.infer<typeof CompanyDatacenterLinkDto>;
export const CompanyDatacenterLinkDto = z.object({ autoAssignedClientDatacenter: z.boolean(), clientCompanyId: z.string(), clientOwnedDatacenterId: z.string().nullable(), id: z.string(), notes: z.string().nullable(), sharedDatacenterId: z.string().nullable() });

export type CountryHolidayDto = z.infer<typeof CountryHolidayDto>;
export const CountryHolidayDto = z.object({ countryCode: z.string(), holidayDate: z.string(), id: z.string(), isRecurring: z.boolean(), name: z.string(), recurringDay: z.number().nullable(), recurringMonth: z.number().nullable() });

export type SoftwareAssociationCategoryDto = z.infer<typeof SoftwareAssociationCategoryDto>;
export const SoftwareAssociationCategoryDto = z.enum(["subsystem", "database_instance", "sap_instance"]);

export type VirtualMachinePowerStatusDto = z.infer<typeof VirtualMachinePowerStatusDto>;
export const VirtualMachinePowerStatusDto = z.enum(["powered_on", "powered_off"]);

export type CurrentUserDto = z.infer<typeof CurrentUserDto>;
export const CurrentUserDto = z.object({ clientAssignments: z.array(z.object({ id: z.string(), logoRef: z.string().nullable().optional(), name: z.string().optional() })), email: z.string().optional(), firstName: z.string().optional(), forcePasswordChange: z.boolean(), id: z.string(), isExternal: z.boolean(), lastName: z.string().optional(), linkedClientId: z.string().nullable().optional(), permissions: z.array(z.string()), photoRef: z.string().nullable().optional(), preferredLanguage: z.string().nullable().optional(), primaryRole: z.object({ id: z.string(), isPrimary: z.boolean(), name: z.string() }).optional(), roles: z.array(z.object({ id: z.string(), isPrimary: z.boolean(), name: z.string() })), status: z.string(), username: z.string() });

export type DependentInfrastructureSummaryDto = z.infer<typeof DependentInfrastructureSummaryDto>;
export const DependentInfrastructureSummaryDto = z.object({ deviceInstanceCount: z.number(), rackCount: z.number(), salaCount: z.number(), virtualMachineCount: z.number() });

export type DatacenterCatalogItemDto = z.infer<typeof DatacenterCatalogItemDto>;
export const DatacenterCatalogItemDto = z.object({ address: z.string().nullable(), city: z.string(), code: z.string(), countryCode: z.string(), id: z.string(), infrastructureSummary: DependentInfrastructureSummaryDto, name: z.string(), status: z.enum(["planned", "active", "maintenance", "retired"]), supportedClientCount: z.number() });

export type DatacenterCatalogDto = z.infer<typeof DatacenterCatalogDto>;
export const DatacenterCatalogDto = z.object({ items: z.array(DatacenterCatalogItemDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type DatacenterRelationshipRoleDto = z.infer<typeof DatacenterRelationshipRoleDto>;
export const DatacenterRelationshipRoleDto = z.enum(["primary", "disaster_recovery"]);

export type SupportedClientRelationshipDto = z.infer<typeof SupportedClientRelationshipDto>;
export const SupportedClientRelationshipDto = z.object({ clientId: z.string(), clientName: z.string(), role: DatacenterRelationshipRoleDto });

export type DatacenterDetailDto = z.infer<typeof DatacenterDetailDto>;
export const DatacenterDetailDto = z.object({ address: z.string().nullable(), city: z.string(), code: z.string(), countryCode: z.string(), id: z.string(), infrastructureSummary: DependentInfrastructureSummaryDto, name: z.string(), retiredAt: z.string().nullable(), retiredBy: z.string().nullable(), status: z.enum(["planned", "active", "maintenance", "retired"]), supportedClientCount: z.number(), supportedClients: z.array(SupportedClientRelationshipDto), version: z.number() });

export type DeleteCompanyResultDto = z.infer<typeof DeleteCompanyResultDto>;
export const DeleteCompanyResultDto = z.object({ dependentCounts: z.object({ datacenterLinks: z.number(), devices: z.number(), vms: z.number() }), message: z.string() });

export type DeleteDatacenterResultDto = z.infer<typeof DeleteDatacenterResultDto>;
export const DeleteDatacenterResultDto = z.object({ datacenter: DatacenterDetailDto.optional(), deletionMode: z.enum(["soft", "hard"]) });

export type DeleteOwnedDatacenterResultDto = z.infer<typeof DeleteOwnedDatacenterResultDto>;
export const DeleteOwnedDatacenterResultDto = z.object({ companyLinksCleared: z.number(), id: z.string(), roomsCleared: z.number(), vmsCleared: z.number() });

export type DeleteSuccessDto = z.infer<typeof DeleteSuccessDto>;
export const DeleteSuccessDto = z.object({ message: z.string() });

export type DeviceCategorySummaryDto = z.infer<typeof DeviceCategorySummaryDto>;
export const DeviceCategorySummaryDto = z.object({ id: z.string(), isActive: z.boolean(), isServerClass: z.boolean(), name: z.string(), slug: z.string() });

export type NamedContextDto = z.infer<typeof NamedContextDto>;
export const NamedContextDto = z.object({ code: z.string(), id: z.string(), name: z.string() });

export type DeviceModelSummaryDto = z.infer<typeof DeviceModelSummaryDto>;
export const DeviceModelSummaryDto = z.object({ brand: z.string(), categoryId: z.string(), categoryIsServerClass: z.boolean().optional(), categoryName: z.string(), id: z.string(), lifecycleStatus: z.enum(["active", "deprecated", "retired"]), manufacturer: z.string(), model: z.string(), modelIdentifier: z.string(), name: z.string(), updatedAt: z.string(), updatedBy: z.string(), vmHostEligible: z.boolean() });

export type DeviceModelCatalogDto = z.infer<typeof DeviceModelCatalogDto>;
export const DeviceModelCatalogDto = z.object({ items: z.array(DeviceModelSummaryDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type DeviceModelDetailDto = z.infer<typeof DeviceModelDetailDto>;
export const DeviceModelDetailDto = z.object({ brand: z.string(), categoryId: z.string(), categoryIsServerClass: z.boolean().optional(), categoryName: z.string(), createdAt: z.string(), createdBy: z.string(), id: z.string(), lifecycleStatus: z.enum(["active", "deprecated", "retired"]), manufacturer: z.string(), model: z.string(), modelIdentifier: z.string(), name: z.string(), specifications: z.object({ compute: Record_string_unknown, dimensions: Record_string_unknown, networking: Record_string_unknown, power: Record_string_unknown, storage: Record_string_unknown }).partial().nullable(), statusChangedAt: z.string(), statusChangedBy: z.string(), updatedAt: z.string(), updatedBy: z.string(), version: z.number(), vmHostEligible: z.boolean() });

export type EnterpriseCollectionEntryDto = z.infer<typeof EnterpriseCollectionEntryDto>;
export const EnterpriseCollectionEntryDto = z.object({ address: z.string().nullable().optional(), city: z.string().nullable().optional(), code: z.string().nullable().optional(), countryCode: z.string().nullable().optional(), createdAt: z.string().optional(), id: z.string(), name: z.string(), state: z.string().nullable().optional(), status: z.enum(["active", "inactive"]), updatedAt: z.string().optional() });

export type EnterpriseUnitDto = z.infer<typeof EnterpriseUnitDto>;
export const EnterpriseUnitDto = z.object({ code: z.string(), enterpriseId: z.string(), id: z.string(), name: z.string(), status: z.enum(["active", "inactive"]), updatedAt: z.string(), version: z.number() });

export type EnterpriseOrganizationalModelDto = z.infer<typeof EnterpriseOrganizationalModelDto>;
export const EnterpriseOrganizationalModelDto = z.object({ address: z.string().nullable(), branches: z.array(EnterpriseCollectionEntryDto), brandingDisplayName: z.string().nullable(), city: z.string().nullable(), countryCode: z.string().nullable(), countryName: z.string().nullable(), createdAt: z.string(), deliveryUnits: z.array(EnterpriseUnitDto), id: z.string(), logoRef: z.string().nullable(), name: z.string(), nit: z.string().nullable(), organizationalUnits: z.array(EnterpriseCollectionEntryDto), practiceUnits: z.array(EnterpriseUnitDto), state: z.string().nullable(), status: z.enum(["active", "inactive"]), updatedAt: z.string(), version: z.number() });

export type EnterpriseToolAssignmentDto = z.infer<typeof EnterpriseToolAssignmentDto>;
export const EnterpriseToolAssignmentDto = z.object({ enterpriseId: z.string(), id: z.string(), isPrimary: z.boolean(), ticketingToolId: z.string() });

export type Record_string_number = z.infer<typeof Record_string_number>;
export const Record_string_number = z.record(z.string(), z.number());

export type ExecutionActivityDto = z.infer<typeof ExecutionActivityDto>;
export const ExecutionActivityDto = z.object({ clientId: z.string(), endAt: z.string(), id: z.string(), startAt: z.string(), status: z.enum(["approved", "in_progress", "executed", "completed", "failed", "cancelled"]) });

export type ExecutionAuditReferenceDto = z.infer<typeof ExecutionAuditReferenceDto>;
export const ExecutionAuditReferenceDto = z.object({ actionType: z.string(), occurredAt: z.string(), outcome: z.enum(["success", "denied", "failure"]), targetId: z.string(), targetType: z.string() });

export type ExecutionLockDto = z.infer<typeof ExecutionLockDto>;
export const ExecutionLockDto = z.object({ reason: z.string().nullable(), state: z.enum(["editable", "expired", "finalized"]) });

export type ExecutionRollbackDto = z.infer<typeof ExecutionRollbackDto>;
export const ExecutionRollbackDto = z.object({ activated: z.boolean(), decisionAt: z.string().nullable(), decisionBy: z.string().nullable(), reason: z.string().nullable() });

export type ExecutionSessionDto = z.infer<typeof ExecutionSessionDto>;
export const ExecutionSessionDto = z.object({ completedBy: z.string().nullable(), endedAt: z.string().nullable(), failedBy: z.string().nullable(), failureReason: z.string().nullable(), id: z.string(), lockReason: z.string().nullable(), lockState: z.enum(["editable", "expired", "finalized"]), outcomeSummary: z.string().nullable(), rollbackActivated: z.boolean(), rollbackDecisionAt: z.string().nullable(), rollbackDecisionBy: z.string().nullable(), rollbackDecisionReason: z.string().nullable(), scheduledActivityId: z.string(), startedAt: z.string().nullable(), startedBy: z.string().nullable(), status: z.enum(["pending", "in_progress", "completed", "failed"]), version: z.number() });

export type ExecutionEvidenceDto = z.infer<typeof ExecutionEvidenceDto>;
export const ExecutionEvidenceDto = z.object({ details: z.string().nullable(), id: z.string(), reference: z.string().nullable(), taskLogId: z.string(), version: z.number() });

export type ExecutionFindingDto = z.infer<typeof ExecutionFindingDto>;
export const ExecutionFindingDto = z.object({ details: z.string().nullable(), id: z.string(), isMandatory: z.boolean(), resolutionDetails: z.string().nullable(), status: z.enum(["open", "resolved", "waived"]), summary: z.string(), taskLogId: z.string(), version: z.number(), waivedBy: z.string().nullable(), waiverReason: z.string().nullable() });

export type ExecutionTaskLogDto = z.infer<typeof ExecutionTaskLogDto>;
export const ExecutionTaskLogDto = z.object({ elapsedSeconds: z.number().nullable(), executionSessionId: z.string(), id: z.string(), progressPercent: z.number().nullable(), sourceTaskId: z.string(), status: z.enum(["pending", "in_progress", "completed", "failed"]), version: z.number() });

export type ExecutionTimerIntervalDto = z.infer<typeof ExecutionTimerIntervalDto>;
export const ExecutionTimerIntervalDto = z.object({ elapsedSeconds: z.number().nullable(), endedAt: z.string().nullable(), executorUserId: z.string(), id: z.string(), sequence: z.number(), startedAt: z.string(), taskLogId: z.string() });

export type ExecutionContextDto = z.infer<typeof ExecutionContextDto>;
export const ExecutionContextDto = z.object({ activity: ExecutionActivityDto, audit: z.array(ExecutionAuditReferenceDto), lock: ExecutionLockDto, rollback: ExecutionRollbackDto, session: ExecutionSessionDto, tasks: z.array(z.object({ evidence: z.array(ExecutionEvidenceDto), findings: z.array(ExecutionFindingDto), taskLog: ExecutionTaskLogDto, timers: z.array(ExecutionTimerIntervalDto) })) });

export type FlatEnterpriseUnitDto = z.infer<typeof FlatEnterpriseUnitDto>;
export const FlatEnterpriseUnitDto = z.object({ code: z.string(), enterpriseId: z.string(), enterpriseName: z.string(), id: z.string(), name: z.string(), status: z.enum(["active", "inactive"]), updatedAt: z.string().optional(), version: z.number() });

export type PlanValidationFamily = z.infer<typeof PlanValidationFamily>;
export const PlanValidationFamily = z.enum(["VAL-01", "VAL-02", "VAL-03", "VAL-04"]);

export type PlanValidationSeverity = z.infer<typeof PlanValidationSeverity>;
export const PlanValidationSeverity = z.enum(["critical", "high", "medium"]);

export type PlanValidationGapDto = z.infer<typeof PlanValidationGapDto>;
export const PlanValidationGapDto = z.object({ code: z.string(), family: PlanValidationFamily, message: z.string(), severity: PlanValidationSeverity, stage: z.enum(["pre", "exec", "post", "rollback", "process"]).optional(), taskId: z.string().optional() });

export type ValidationGrade = z.infer<typeof ValidationGrade>;
export const ValidationGrade = z.enum(["A", "B", "C", "D", "E", "F"]);

export type ValidationReportDto = z.infer<typeof ValidationReportDto>;
export const ValidationReportDto = z.object({ blockingFindings: z.number(), decision: z.string(), gaps: z.array(PlanValidationGapDto), grade: ValidationGrade, id: z.string(), nonBlockingFindings: z.number(), planningRevision: z.number(), result: z.enum(["passed", "failed"]), scheduledActivityId: z.string(), validatedAt: z.string(), validatedBy: z.string().nullable() });

export type LatestValidationReportDto = z.infer<typeof LatestValidationReportDto>;
export const LatestValidationReportDto = z.object({ approvalReady: z.boolean(), isFresh: z.boolean(), report: ValidationReportDto });

export type LicenseDto = z.infer<typeof LicenseDto>;
export const LicenseDto = z.object({ expiration: z.string().nullable(), id: z.string(), licenseKey: z.string(), licenseType: z.string(), subsystemId: z.string().nullable(), vendor: z.string().nullable() });

export type OperationalPlanStatus = z.infer<typeof OperationalPlanStatus>;
export const OperationalPlanStatus = z.enum(["draft", "active", "completed", "cancelled"]);

export type MaintenanceWindowDto = z.infer<typeof MaintenanceWindowDto>;
export const MaintenanceWindowDto = z.object({ clientId: z.string(), description: z.string().nullable(), endTime: z.string(), groupId: z.string().nullable(), id: z.string(), startTime: z.string(), windowDate: z.string() });

export type OperatingSystemDto = z.infer<typeof OperatingSystemDto>;
export const OperatingSystemDto = z.object({ edition: z.string().nullable(), endOfLife: z.string().nullable(), endOfSupport: z.string().nullable(), family: z.string().nullable(), id: z.string(), name: z.string(), platformId: z.string(), platformName: z.string().nullable(), servicePack: z.string().nullable(), vendor: z.string().nullable(), version: z.string().nullable() });

export type OperationalPlanOrigin = z.infer<typeof OperationalPlanOrigin>;
export const OperationalPlanOrigin = z.enum(["manual", "automatic"]);

export type ScheduledActivityApprovalState = z.infer<typeof ScheduledActivityApprovalState>;
export const ScheduledActivityApprovalState = z.enum(["not_required", "pending", "approved", "rejected"]);

export type ScheduledActivityStatus = z.infer<typeof ScheduledActivityStatus>;
export const ScheduledActivityStatus = z.enum(["draft", "scheduled", "pending_approval", "approved", "in_progress", "completed", "failed", "cancelled", "executed"]);

export type ScheduledActivitySummaryDto = z.infer<typeof ScheduledActivitySummaryDto>;
export const ScheduledActivitySummaryDto = z.object({ approvalState: ScheduledActivityApprovalState, changeRequestNumber: z.string().nullable(), clientId: z.string(), endAt: z.string(), id: z.string(), planId: z.string(), scopeCount: z.number(), startAt: z.string(), status: ScheduledActivityStatus, taskCount: z.number() });

export type OperationalPlanDetailDto = z.infer<typeof OperationalPlanDetailDto>;
export const OperationalPlanDetailDto = z.object({ activityCount: z.number(), clientId: z.string(), clientName: z.string().nullable(), createdAt: z.string(), creationOrigin: OperationalPlanOrigin, id: z.string(), name: z.string(), scheduledActivities: z.array(ScheduledActivitySummaryDto), status: OperationalPlanStatus, updatedAt: z.string(), year: z.number() });

export type OperationalPlanSummaryDto = z.infer<typeof OperationalPlanSummaryDto>;
export const OperationalPlanSummaryDto = z.object({ activityCount: z.number(), clientId: z.string(), clientName: z.string().nullable(), createdAt: z.string(), creationOrigin: OperationalPlanOrigin, id: z.string(), name: z.string(), status: OperationalPlanStatus, updatedAt: z.string(), year: z.number() });

export type PaginatedClientsDto = z.infer<typeof PaginatedClientsDto>;
export const PaginatedClientsDto = z.object({ items: z.array(ClientSummaryDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type UserSummaryDto = z.infer<typeof UserSummaryDto>;
export const UserSummaryDto = z.object({ clientIds: z.array(z.string()), email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), roleIds: z.array(z.string()), status: z.enum(["active", "deactivated"]), userType: z.enum(["internal", "external"]), welcomeEmailStatus: z.enum(["not_requested", "requested", "sent", "failed"]) });

export type PaginatedUsers = z.infer<typeof PaginatedUsers>;
export const PaginatedUsers = z.object({ items: z.array(UserSummaryDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type PlatformCategoryDto = z.infer<typeof PlatformCategoryDto>;
export const PlatformCategoryDto = z.object({ createdAt: z.string(), id: z.string(), name: z.string(), platformId: z.string(), updatedAt: z.string() });

export type PlatformDocumentCategoryDto = z.infer<typeof PlatformDocumentCategoryDto>;
export const PlatformDocumentCategoryDto = z.object({ createdAt: z.string(), description: z.string().nullable(), id: z.string(), isDefault: z.boolean(), name: z.string(), platformId: z.string(), sortOrder: z.number(), updatedAt: z.string() });

export type PlatformDocumentDto = z.infer<typeof PlatformDocumentDto>;
export const PlatformDocumentDto = z.object({ categoryId: z.string(), createdAt: z.string(), description: z.string().nullable(), documentType: z.enum(["link", "file"]), fileName: z.string().nullable(), filePath: z.string().nullable(), fileSize: z.number().nullable(), id: z.string(), platformId: z.string(), title: z.string(), updatedAt: z.string(), uploadedBy: z.string().nullable(), url: z.string().nullable() });

export type PlatformDto = z.infer<typeof PlatformDto>;
export const PlatformDto = z.object({ category: z.string().nullable(), createdAt: z.string(), iconSvg: z.string().nullable(), id: z.string(), isActive: z.boolean(), name: z.string(), updatedAt: z.string(), vendor: z.string().nullable() });

export type ReferenceCatalogItemDto = z.infer<typeof ReferenceCatalogItemDto>;
export const ReferenceCatalogItemDto = z.object({ code: z.string(), createdAt: z.string().optional(), id: z.string(), name: z.string(), updatedAt: z.string().optional(), version: z.number() });

export type ReportPdfDto = z.infer<typeof ReportPdfDto>;
export const ReportPdfDto = z.object({ location: z.string(), message: z.string(), mode: z.literal("external_redirect"), reportId: z.string() });

export type RetirementImpactDto = z.infer<typeof RetirementImpactDto>;
export const RetirementImpactDto = z.object({ datacenterId: z.string(), infrastructureSummary: DependentInfrastructureSummaryDto, message: z.string(), supportedClientCount: z.number() });

export type RoomCatalogDto = z.infer<typeof RoomCatalogDto>;
export const RoomCatalogDto = z.object({ items: z.array(RoomSummaryDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type RoomDetailDto = z.infer<typeof RoomDetailDto>;
export const RoomDetailDto = z.object({ assignedClientCount: z.number(), assignedClients: z.array(AssignedRoomClientDto), code: z.string(), createdAt: z.string(), datacenter: z.object({ code: z.string(), id: z.string(), name: z.string() }), datacenterId: z.string(), id: z.string(), isWarehouse: z.boolean(), name: z.string(), rackCount: z.number(), racksPath: z.string(), status: RoomStatusDto, updatedAt: z.string(), version: z.number() });

export type ScheduledActivityAuditEventDto = z.infer<typeof ScheduledActivityAuditEventDto>;
export const ScheduledActivityAuditEventDto = z.object({ actionType: z.string(), actorUserId: z.string().nullable(), afterState: Record_string_unknown, beforeState: Record_string_unknown, clientId: z.string().nullable(), details: Record_string_unknown, id: z.string(), occurredAt: z.string(), outcome: z.enum(["success", "denied", "failure"]), reason: z.string().nullable(), targetId: z.string(), targetType: z.string() });

export type ScheduledActivityAuditDto = z.infer<typeof ScheduledActivityAuditDto>;
export const ScheduledActivityAuditDto = z.object({ items: z.array(ScheduledActivityAuditEventDto) });

export type ScheduledActivityDetailDto = z.infer<typeof ScheduledActivityDetailDto>;
export const ScheduledActivityDetailDto = z.object({ approvalState: ScheduledActivityApprovalState, changeRequestNumber: z.string().nullable(), clientId: z.string(), description: z.string().nullable(), endAt: z.string(), id: z.string(), masterActivityRef: z.string(), planId: z.string(), planOrigin: z.enum(["manual", "automatic"]).optional(), planningRevision: z.number(), platformCategoryRef: z.string(), rollbackApplicable: z.boolean().nullable(), rollbackJustification: z.string().nullable(), rollbackTrigger: z.string().nullable(), rollbackValidation: z.string().nullable(), scopeCount: z.number(), startAt: z.string(), status: ScheduledActivityStatus, taskCount: z.number(), version: z.number() });

export type ScheduledActivityRcaActivityContextDto = z.infer<typeof ScheduledActivityRcaActivityContextDto>;
export const ScheduledActivityRcaActivityContextDto = z.object({ changeRequestNumber: z.string().nullable(), description: z.string().nullable(), endAt: z.string(), id: z.string(), startAt: z.string(), status: z.string() });

export type ScheduledActivityRcaClientContextDto = z.infer<typeof ScheduledActivityRcaClientContextDto>;
export const ScheduledActivityRcaClientContextDto = z.object({ id: z.string(), name: z.string() });

export type ScheduledActivityRcaPlanContextDto = z.infer<typeof ScheduledActivityRcaPlanContextDto>;
export const ScheduledActivityRcaPlanContextDto = z.object({ id: z.string(), name: z.string(), status: z.string(), year: z.number() });

export type ScheduledActivityRcaDto = z.infer<typeof ScheduledActivityRcaDto>;
export const ScheduledActivityRcaDto = z.object({ completedAt: z.string().nullable(), createdAt: z.string(), executiveSummary: z.string().nullable(), id: z.string(), rootCause: z.string().nullable(), scheduledActivityId: z.string(), status: z.enum(["draft", "completed"]), updatedAt: z.string(), version: z.number(), whyStatements: z.array(z.string()) });

export type ScheduledActivityRcaContextDto = z.infer<typeof ScheduledActivityRcaContextDto>;
export const ScheduledActivityRcaContextDto = z.object({ activity: ScheduledActivityRcaActivityContextDto, client: ScheduledActivityRcaClientContextDto, plan: ScheduledActivityRcaPlanContextDto, rca: ScheduledActivityRcaDto });

export type ScheduledActivityReportItemDto = z.infer<typeof ScheduledActivityReportItemDto>;
export const ScheduledActivityReportItemDto = z.object({ approvalState: ScheduledActivityApprovalState, changeRequestNumber: z.string().nullable(), clientApprovalState: ScheduledActivityApprovalState, clientId: z.string(), endAt: z.string(), executionStatus: z.union([z.literal("pending"), z.literal("in_progress"), z.literal("completed"), z.literal("failed"), z.literal(null)]).nullable(), id: z.string(), internalApprovalState: ScheduledActivityApprovalState, planId: z.string(), planningRevision: z.number(), rollbackApplicable: z.boolean().nullable(), scopeCount: z.number(), startAt: z.string(), status: ScheduledActivityStatus, taskCount: z.number() });

export type ScheduledActivityReportDto = z.infer<typeof ScheduledActivityReportDto>;
export const ScheduledActivityReportDto = z.object({ items: z.array(ScheduledActivityReportItemDto), totals: Record_string_number });

export type SearchResultClientSummaryDto = z.infer<typeof SearchResultClientSummaryDto>;
export const SearchResultClientSummaryDto = z.object({ id: z.string(), name: z.string() });

export type SearchResultHierarchyContextDto = z.infer<typeof SearchResultHierarchyContextDto>;
export const SearchResultHierarchyContextDto = z.object({ datacenter: z.object({ id: z.string(), name: z.string() }), deviceInstance: z.object({ id: z.string(), name: z.string() }), rack: z.object({ id: z.string(), name: z.string() }), room: z.object({ id: z.string(), name: z.string() }), virtualMachine: z.object({ id: z.string(), name: z.string() }) }).partial();

export type UniversalInfrastructureEntityType = z.infer<typeof UniversalInfrastructureEntityType>;
export const UniversalInfrastructureEntityType = z.enum(["datacenter", "room", "rack", "device_instance", "virtual_machine", "subsystem", "database_instance", "sap_instance"]);

export type SearchResultNavigationContextDto = z.infer<typeof SearchResultNavigationContextDto>;
export const SearchResultNavigationContextDto = z.object({ detailPath: z.string(), entityType: UniversalInfrastructureEntityType, resourceId: z.string() });

export type SessionDto = z.infer<typeof SessionDto>;
export const SessionDto = z.object({ accessToken: z.string(), accessTokenExpiresAt: z.string(), refreshToken: z.string(), refreshTokenExpiresAt: z.string() });

export type SoftwareAssociationContextDto = z.infer<typeof SoftwareAssociationContextDto>;
export const SoftwareAssociationContextDto = z.object({ catalogSelectionLabel: z.string() }).partial().catchall(z.unknown());

export type SoftwareAssociationDto = z.infer<typeof SoftwareAssociationDto>;
export const SoftwareAssociationDto = z.object({ attributes: Record_string_unknown, catalogSelectionId: z.string(), category: SoftwareAssociationCategoryDto, context: SoftwareAssociationContextDto.optional(), createdAt: z.string(), id: z.string(), updatedAt: z.string(), version: z.number(), virtualMachineId: z.string() });

export type SoftwareAssociationListDto = z.infer<typeof SoftwareAssociationListDto>;
export const SoftwareAssociationListDto = z.object({ items: z.array(SoftwareAssociationDto) });

export type StandbyAssignmentListDto = z.infer<typeof StandbyAssignmentListDto>;
export const StandbyAssignmentListDto = z.object({ items: z.array(StandbyAssignmentDto) });

export type StandbyAssignmentPlanningContextDto = z.infer<typeof StandbyAssignmentPlanningContextDto>;
export const StandbyAssignmentPlanningContextDto = z.object({ activeCount: z.number(), assignments: z.array(StandbyAssignmentDto), establishedEscalationLevel: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(null)]).nullable(), inactiveCount: z.number(), nextAssignment: StandbyAssignmentDto, userId: z.string() });

export type StandbyAssignmentStatisticsDto = z.infer<typeof StandbyAssignmentStatisticsDto>;
export const StandbyAssignmentStatisticsDto = z.object({ active: z.number(), byEscalationLevel: Record_string_number, clientCount: z.number(), current: z.number(), expired: z.number(), future: z.number(), inactive: z.number(), platformCount: z.number(), total: z.number(), userCount: z.number() });

export type TicketingToolDto = z.infer<typeof TicketingToolDto>;
export const TicketingToolDto = z.object({ description: z.string().nullable(), id: z.string(), isActive: z.boolean(), name: z.string(), url: z.string().nullable(), vendor: z.string().nullable() });

export type TimezonePolicy = z.infer<typeof TimezonePolicy>;
export const TimezonePolicy = z.enum(["customer", "bootstrap_default"]);

export type UnifiedFacilityKindDto = z.infer<typeof UnifiedFacilityKindDto>;
export const UnifiedFacilityKindDto = z.enum(["kyndryl", "client_owned"]);

export type UnifiedClientDatacenterLinkedCompanyDto = z.infer<typeof UnifiedClientDatacenterLinkedCompanyDto>;
export const UnifiedClientDatacenterLinkedCompanyDto = z.object({ id: z.string(), name: z.string() });

export type UnifiedClientDatacenterDto = z.infer<typeof UnifiedClientDatacenterDto>;
export const UnifiedClientDatacenterDto = z.object({ id: z.string(), kind: UnifiedFacilityKindDto, linkedCompanies: z.array(UnifiedClientDatacenterLinkedCompanyDto), name: z.string(), role: DatacenterAssignmentRoleDto, roomCount: z.number(), status: z.string(), vmCount: z.number() });

export type UniversalInfrastructureSearchResultDto = z.infer<typeof UniversalInfrastructureSearchResultDto>;
export const UniversalInfrastructureSearchResultDto = z.object({ client: SearchResultClientSummaryDto, displayName: z.string(), entityType: UniversalInfrastructureEntityType, hierarchy: SearchResultHierarchyContextDto, id: z.string(), navigation: SearchResultNavigationContextDto, summary: Record_string_unknown });

export type UniversalInfrastructureSearchResultGroupDto = z.infer<typeof UniversalInfrastructureSearchResultGroupDto>;
export const UniversalInfrastructureSearchResultGroupDto = z.object({ entityType: UniversalInfrastructureEntityType, items: z.array(UniversalInfrastructureSearchResultDto) });

export type UniversalInfrastructureSearchResponseDto = z.infer<typeof UniversalInfrastructureSearchResponseDto>;
export const UniversalInfrastructureSearchResponseDto = z.object({ complete: z.literal(true), groups: z.array(UniversalInfrastructureSearchResultGroupDto), query: z.string() });

export type UserDetailsDto = z.infer<typeof UserDetailsDto>;
export const UserDetailsDto = z.object({ clientAssignments: z.array(z.object({ clientId: z.string(), dedicationPercentage: z.number() })), clientIds: z.array(z.string()), email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), roleIds: z.array(z.string()), roles: z.array(z.object({ id: z.string(), name: z.string() })), status: z.enum(["active", "deactivated"]), userType: z.enum(["internal", "external"]), wbsAssignments: z.array(z.object({ wbsItemId: z.string() })), welcomeEmailStatus: z.enum(["not_requested", "requested", "sent", "failed"]) });

export type ValidationReportSummaryDto = z.infer<typeof ValidationReportSummaryDto>;
export const ValidationReportSummaryDto = z.object({ blockingFindings: z.number(), decision: z.string(), grade: ValidationGrade, id: z.string(), nonBlockingFindings: z.number(), planningRevision: z.number(), result: z.enum(["passed", "failed"]), scheduledActivityId: z.string(), validatedAt: z.string(), validatedBy: z.string().nullable() });

export type VirtualMachineDeviceInstanceContextDto = z.infer<typeof VirtualMachineDeviceInstanceContextDto>;
export const VirtualMachineDeviceInstanceContextDto = z.object({ assetTag: z.string(), id: z.string() });

export type VirtualMachineNavigationContextDto = z.infer<typeof VirtualMachineNavigationContextDto>;
export const VirtualMachineNavigationContextDto = z.object({ client: z.object({ code: z.string(), id: z.string(), name: z.string() }).nullable(), cloudAccount: z.object({ alias: z.string().nullable(), id: z.string() }).nullable().optional(), datacenter: NamedContextDto.optional(), deviceInstance: VirtualMachineDeviceInstanceContextDto.optional(), ownedDatacenter: z.object({ id: z.string(), name: z.string() }).nullable().optional(), rack: NamedContextDto.optional(), room: NamedContextDto.optional() });

export type VirtualMachineSummaryDto = z.infer<typeof VirtualMachineSummaryDto>;
export const VirtualMachineSummaryDto = z.object({ cloudAccountId: z.string().nullable(), deviceInstanceId: z.string().nullable(), environment: z.string().nullable(), id: z.string(), name: z.string(), navigation: VirtualMachineNavigationContextDto.optional(), operatingSystemId: z.string(), origin: z.enum(["cloud", "on_premises"]), ownedDatacenterId: z.string().nullable(), platformId: z.string().nullable(), powerStatus: VirtualMachinePowerStatusDto, status: VirtualMachinePowerStatusDto.optional(), updatedAt: z.string() });

export type VirtualMachineCatalogDto = z.infer<typeof VirtualMachineCatalogDto>;
export const VirtualMachineCatalogDto = z.object({ items: z.array(VirtualMachineSummaryDto), page: z.number(), pageSize: z.number(), total: z.number() });

export type VirtualMachineDetailDto = z.infer<typeof VirtualMachineDetailDto>;
export const VirtualMachineDetailDto = z.object({ cloudAccountId: z.string().nullable(), createdAt: z.string(), deploymentMetadata: Record_string_unknown, deviceInstanceId: z.string().nullable(), environment: z.string().nullable(), id: z.string(), name: z.string(), navigation: VirtualMachineNavigationContextDto.optional(), operatingSystemId: z.string(), origin: z.enum(["cloud", "on_premises"]), ownedDatacenterId: z.string().nullable(), platformId: z.string().nullable(), powerStatus: VirtualMachinePowerStatusDto, sapInstanceCount: z.number().optional(), status: VirtualMachinePowerStatusDto.optional(), updatedAt: z.string(), version: z.number(), volumeCount: z.number().optional() });

export type VmAssignOutcomeDto = z.infer<typeof VmAssignOutcomeDto>;
export const VmAssignOutcomeDto = z.object({ autoSignal: z.string().nullable().optional(), basis: z.enum(["manual", "auto"]).optional(), outcome: z.enum(["assigned", "skipped_not_found", "reassigned"]), tierId: z.string().optional(), vmId: z.string() });

export type WelcomeEmailResultDto = z.infer<typeof WelcomeEmailResultDto>;
export const WelcomeEmailResultDto = z.object({ failureReason: z.string().optional(), providerReference: z.string().optional(), status: z.enum(["requested", "sent", "failed"]), userId: z.string() });

export type WindowGroupDto = z.infer<typeof WindowGroupDto>;
export const WindowGroupDto = z.object({ clientId: z.string(), description: z.string().nullable(), displayOrder: z.number(), id: z.string(), name: z.string() });

export type WorkScheduleDto = z.infer<typeof WorkScheduleDto>;
export const WorkScheduleDto = z.object({ clientId: z.string(), isDefault: z.boolean(), lunchEnd: z.string(), lunchStart: z.string(), timezone: z.string(), timezonePolicy: TimezonePolicy, weeklyHours: z.number(), workDays: z.array(z.number()), workdayEnd: z.string(), workdayStart: z.string() });

export type HealthPayload = z.infer<typeof HealthPayload>;
export const HealthPayload = z.object({ status: z.enum(["ok", "degraded"]), service: z.string(), uptime: z.number().int(), timestamp: z.iso.datetime(), memory: z.object({ rss: z.number(), heapUsed: z.number(), heapTotal: z.number() }), connections: z.object({ socketio: z.number().int(), dbPool: z.object({ total: z.number().int(), idle: z.number().int(), waiting: z.number().int() }) }), database: z.object({ status: z.enum(["connected", "disconnected"]), latency: z.number(), error: z.string() }).partial().optional() });

export type PasswordSignInRequest = z.infer<typeof PasswordSignInRequest>;
export const PasswordSignInRequest = z.object({ identifier: z.string().min(1), password: z.string().min(1) });

export type EmailRequest = z.infer<typeof EmailRequest>;
export const EmailRequest = z.object({ email: z.string().min(1) });

export type OtpSignInRequest = z.infer<typeof OtpSignInRequest>;
export const OtpSignInRequest = z.object({ email: z.string().min(1), code: z.string().min(1) });

export type RefreshRequest = z.infer<typeof RefreshRequest>;
export const RefreshRequest = z.object({ refreshToken: z.string().min(1) });

export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;
export const ChangePasswordRequest = z.object({ currentPassword: z.string(), newPassword: z.string().min(8) });

export type CompleteRecoveryRequest = z.infer<typeof CompleteRecoveryRequest>;
export const CompleteRecoveryRequest = z.object({ credential: z.string().min(1), newPassword: z.string().min(8) });

export type ForcePasswordChangeRequest = z.infer<typeof ForcePasswordChangeRequest>;
export const ForcePasswordChangeRequest = z.object({ accountId: z.string(), required: z.boolean() });

export type DatacenterStatus = z.infer<typeof DatacenterStatus>;
export const DatacenterStatus = z.enum(["planned", "active", "maintenance", "retired"]);

export type CreateDatacenterRequest = z.infer<typeof CreateDatacenterRequest>;
export const CreateDatacenterRequest = z.object({ code: z.string().min(1), name: z.string().min(1), countryCode: z.string().min(2).max(2), city: z.string().min(1), address: z.string().nullable().optional(), status: DatacenterStatus });

export type UpdateDatacenterRequest = z.infer<typeof UpdateDatacenterRequest>;
export const UpdateDatacenterRequest = z.object({ code: z.string(), name: z.string(), countryCode: z.string(), city: z.string(), address: z.string().nullable(), status: DatacenterStatus, version: z.number().int() }).partial().refine((obj) => Object.keys(obj).length >= 1, { message: "minProperties" });

export type DeleteDatacenterRequest = z.infer<typeof DeleteDatacenterRequest>;
export const DeleteDatacenterRequest = z.object({ version: z.number().int() }).partial();

export type ApplicationTheme = z.infer<typeof ApplicationTheme>;
export const ApplicationTheme = z.object({ primaryBrandColor: z.string().regex(new RegExp("^#[0-9A-F]{6}$")), source: z.enum(["default", "configured"]), version: z.number().int().min(0), updatedAt: z.iso.datetime() });

export type UpdateApplicationThemeRequest = z.infer<typeof UpdateApplicationThemeRequest>;
export const UpdateApplicationThemeRequest = z.object({ primaryBrandColor: z.string().regex(new RegExp("^#[0-9A-Fa-f]{6}$")), version: z.number().int().min(0), approvalContext: z.unknown().optional() });

// </Schemas>

// <Endpoints>
export type get_GetHealth = typeof get_GetHealth;
export const get_GetHealth = {
  method: z.literal("GET"),
  path: z.literal("/health"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: HealthPayload, 503: HealthPayload },
};

export type get_Get_users_by_platform_platformId = typeof get_Get_users_by_platform_platformId;
export const get_Get_users_by_platform_platformId = {
  method: z.literal("GET"),
  path: z.literal("/users/by-platform/{platformId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ platformId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(z.object({ email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), roleName: z.string().nullable(), username: z.string() })) }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_users_by_context_clientId = typeof get_Get_users_by_context_clientId;
export const get_Get_users_by_context_clientId = {
  method: z.literal("GET"),
  path: z.literal("/users/by-context/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ otherUsers: z.array(z.object({ email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), username: z.string() })), ownUsers: z.array(z.object({ email: z.string(), firstName: z.string(), id: z.string(), lastName: z.string(), username: z.string() })) }) }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_profile = typeof get_Get_profile;
export const get_Get_profile = {
  method: z.literal("GET"),
  path: z.literal("/profile"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type patch_Patch_profile_language = typeof patch_Patch_profile_language;
export const patch_Patch_profile_language = {
  method: z.literal("PATCH"),
  path: z.literal("/profile/language"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type patch_Patch_profile_avatar = typeof patch_Patch_profile_avatar;
export const patch_Patch_profile_avatar = {
  method: z.literal("PATCH"),
  path: z.literal("/profile/avatar"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_admin_role_platforms = typeof get_Get_admin_role_platforms;
export const get_Get_admin_role_platforms = {
  method: z.literal("GET"),
  path: z.literal("/admin/role-platforms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_admin_role_platforms_roleId = typeof get_Get_admin_role_platforms_roleId;
export const get_Get_admin_role_platforms_roleId = {
  method: z.literal("GET"),
  path: z.literal("/admin/role-platforms/{roleId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roleId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_admin_role_platforms_roleId = typeof put_Put_admin_role_platforms_roleId;
export const put_Put_admin_role_platforms_roleId = {
  method: z.literal("PUT"),
  path: z.literal("/admin/role-platforms/{roleId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roleId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_operating_systems = typeof get_Get_operating_systems;
export const get_Get_operating_systems = {
  method: z.literal("GET"),
  path: z.literal("/operating-systems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ platformId: z.string(), family: z.string(), vendor: z.string() }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(OperatingSystemDto) }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_operating_systems = typeof post_Post_operating_systems;
export const post_Post_operating_systems = {
  method: z.literal("POST"),
  path: z.literal("/operating-systems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: z.object({ platformId: z.string(), name: z.string(), version: z.string().nullable().optional(), edition: z.string().nullable().optional(), vendor: z.string().nullable().optional(), family: z.string().nullable().optional(), endOfSupport: z.string().nullable().optional(), endOfLife: z.string().nullable().optional(), servicePack: z.string().nullable().optional() }) },
  responses: { 201: ApiResponse.and(z.object({ data: OperatingSystemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_operating_systems_id = typeof get_Get_operating_systems_id;
export const get_Get_operating_systems_id = {
  method: z.literal("GET"),
  path: z.literal("/operating-systems/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: OperatingSystemDto }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_operating_systems_id = typeof put_Put_operating_systems_id;
export const put_Put_operating_systems_id = {
  method: z.literal("PUT"),
  path: z.literal("/operating-systems/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: z.object({ platformId: z.string(), name: z.string(), version: z.string().nullable(), edition: z.string().nullable(), vendor: z.string().nullable(), family: z.string().nullable(), endOfSupport: z.string().nullable(), endOfLife: z.string().nullable(), servicePack: z.string().nullable() }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: OperatingSystemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_operating_systems_id = typeof delete_Delete_operating_systems_id;
export const delete_Delete_operating_systems_id = {
  method: z.literal("DELETE"),
  path: z.literal("/operating-systems/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_licenses = typeof get_Get_licenses;
export const get_Get_licenses = {
  method: z.literal("GET"),
  path: z.literal("/licenses"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ subsystemId: z.string(), licenseType: z.string(), vendor: z.string() }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(LicenseDto) }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_licenses = typeof post_Post_licenses;
export const post_Post_licenses = {
  method: z.literal("POST"),
  path: z.literal("/licenses"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: z.object({ licenseKey: z.string(), licenseType: z.string(), subsystemId: z.string().nullable().optional(), expiration: z.string().nullable().optional(), vendor: z.string().nullable().optional() }) },
  responses: { 201: ApiResponse.and(z.object({ data: LicenseDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_licenses_id = typeof get_Get_licenses_id;
export const get_Get_licenses_id = {
  method: z.literal("GET"),
  path: z.literal("/licenses/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: LicenseDto }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_licenses_id = typeof put_Put_licenses_id;
export const put_Put_licenses_id = {
  method: z.literal("PUT"),
  path: z.literal("/licenses/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: z.object({ licenseKey: z.string(), licenseType: z.string(), subsystemId: z.string().nullable(), expiration: z.string().nullable(), vendor: z.string().nullable() }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: LicenseDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_licenses_id = typeof delete_Delete_licenses_id;
export const delete_Delete_licenses_id = {
  method: z.literal("DELETE"),
  path: z.literal("/licenses/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_my_tickets = typeof get_Get_shift_tickets_my_tickets;
export const get_Get_shift_tickets_my_tickets = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/my-tickets"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_search_ticketNumber = typeof get_Get_shift_tickets_search_ticketNumber;
export const get_Get_shift_tickets_search_ticketNumber = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/search/{ticketNumber}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketNumber: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_search_users = typeof get_Get_shift_tickets_search_users;
export const get_Get_shift_tickets_search_users = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/search-users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_vms_by_client_clientId = typeof get_Get_shift_tickets_vms_by_client_clientId;
export const get_Get_shift_tickets_vms_by_client_clientId = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/vms-by-client/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type delete_Delete_shift_tickets_relations_relationId = typeof delete_Delete_shift_tickets_relations_relationId;
export const delete_Delete_shift_tickets_relations_relationId = {
  method: z.literal("DELETE"),
  path: z.literal("/shift-tickets/relations/{relationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ relationId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_shift_tickets_ticketId_leave = typeof delete_Delete_shift_tickets_ticketId_leave;
export const delete_Delete_shift_tickets_ticketId_leave = {
  method: z.literal("DELETE"),
  path: z.literal("/shift-tickets/{ticketId}/leave"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_ticketId_relations = typeof get_Get_shift_tickets_ticketId_relations;
export const get_Get_shift_tickets_ticketId_relations = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/{ticketId}/relations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_ticketId_relations = typeof post_Post_shift_tickets_ticketId_relations;
export const post_Post_shift_tickets_ticketId_relations = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/{ticketId}/relations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_incidents_my_incidents = typeof get_Get_incidents_my_incidents;
export const get_Get_incidents_my_incidents = {
  method: z.literal("GET"),
  path: z.literal("/incidents/my-incidents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_incidents_search_ticketNumber = typeof get_Get_incidents_search_ticketNumber;
export const get_Get_incidents_search_ticketNumber = {
  method: z.literal("GET"),
  path: z.literal("/incidents/search/{ticketNumber}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketNumber: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_incidents_search_users = typeof get_Get_incidents_search_users;
export const get_Get_incidents_search_users = {
  method: z.literal("GET"),
  path: z.literal("/incidents/search-users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_incidents_vms_by_clients = typeof get_Get_incidents_vms_by_clients;
export const get_Get_incidents_vms_by_clients = {
  method: z.literal("GET"),
  path: z.literal("/incidents/vms-by-clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_incidents_devices_by_clients = typeof get_Get_incidents_devices_by_clients;
export const get_Get_incidents_devices_by_clients = {
  method: z.literal("GET"),
  path: z.literal("/incidents/devices-by-clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_master_activities = typeof get_Get_master_activities;
export const get_Get_master_activities = {
  method: z.literal("GET"),
  path: z.literal("/master-activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ platformId: z.string() }).partial().optional() },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_master_activities = typeof post_Post_master_activities;
export const post_Post_master_activities = {
  method: z.literal("POST"),
  path: z.literal("/master-activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_master_activities_id = typeof get_Get_master_activities_id;
export const get_Get_master_activities_id = {
  method: z.literal("GET"),
  path: z.literal("/master-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_master_activities_id = typeof put_Put_master_activities_id;
export const put_Put_master_activities_id = {
  method: z.literal("PUT"),
  path: z.literal("/master-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_master_activities_id = typeof delete_Delete_master_activities_id;
export const delete_Delete_master_activities_id = {
  method: z.literal("DELETE"),
  path: z.literal("/master-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_pending_approvals = typeof get_Get_scheduled_activities_pending_approvals;
export const get_Get_scheduled_activities_pending_approvals = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/pending-approvals"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_my_changes = typeof get_Get_scheduled_activities_my_changes;
export const get_Get_scheduled_activities_my_changes = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/my-changes"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_id_approval_evidence = typeof get_Get_scheduled_activities_id_approval_evidence;
export const get_Get_scheduled_activities_id_approval_evidence = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{id}/approval-evidence"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_approval_evidence = typeof post_Post_scheduled_activities_id_approval_evidence;
export const post_Post_scheduled_activities_id_approval_evidence = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/approval-evidence"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_id_approval_evidence_evidenceId = typeof delete_Delete_scheduled_activities_id_approval_evidence_evidenceId;
export const delete_Delete_scheduled_activities_id_approval_evidence_evidenceId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{id}/approval-evidence/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string(), evidenceId: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_check_task_dates = typeof post_Post_scheduled_activities_id_check_task_dates;
export const post_Post_scheduled_activities_id_check_task_dates = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/check-task-dates"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_adjust_task_dates = typeof post_Post_scheduled_activities_id_adjust_task_dates;
export const post_Post_scheduled_activities_id_adjust_task_dates = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/adjust-task-dates"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_id_extend_execution = typeof put_Put_scheduled_activities_id_extend_execution;
export const put_Put_scheduled_activities_id_extend_execution = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{id}/extend-execution"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_tasks = typeof post_Post_scheduled_activities_activityId_execution_tasks;
export const post_Post_scheduled_activities_activityId_execution_tasks = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_activityId_execution_tasks_taskLogId = typeof put_Put_scheduled_activities_activityId_execution_tasks_taskLogId;
export const put_Put_scheduled_activities_activityId_execution_tasks_taskLogId = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_activityId_execution_tasks_taskLogId = typeof delete_Delete_scheduled_activities_activityId_execution_tasks_taskLogId;
export const delete_Delete_scheduled_activities_activityId_execution_tasks_taskLogId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_overtime_rate_types = typeof get_Get_overtime_rate_types;
export const get_Get_overtime_rate_types = {
  method: z.literal("GET"),
  path: z.literal("/overtime/rate-types"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_overtime_entries = typeof post_Post_overtime_entries;
export const post_Post_overtime_entries = {
  method: z.literal("POST"),
  path: z.literal("/overtime/entries"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type delete_Delete_overtime_entries_id = typeof delete_Delete_overtime_entries_id;
export const delete_Delete_overtime_entries_id = {
  method: z.literal("DELETE"),
  path: z.literal("/overtime/entries/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_planned_interruptions = typeof get_Get_planned_interruptions;
export const get_Get_planned_interruptions = {
  method: z.literal("GET"),
  path: z.literal("/planned-interruptions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_bcp_tasks_taskId_vms = typeof get_Get_bcp_tasks_taskId_vms;
export const get_Get_bcp_tasks_taskId_vms = {
  method: z.literal("GET"),
  path: z.literal("/bcp-tasks/{taskId}/vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string() }) },
  responses: { 200: ApiResponse, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_tasks_taskId_vms = typeof post_Post_bcp_tasks_taskId_vms;
export const post_Post_bcp_tasks_taskId_vms = {
  method: z.literal("POST"),
  path: z.literal("/bcp-tasks/{taskId}/vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type delete_Delete_bcp_tasks_taskId_vms_vmId = typeof delete_Delete_bcp_tasks_taskId_vms_vmId;
export const delete_Delete_bcp_tasks_taskId_vms_vmId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-tasks/{taskId}/vms/{vmId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string(), vmId: z.string() }) },
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_audit_logs = typeof get_Get_audit_logs;
export const get_Get_audit_logs = {
  method: z.literal("GET"),
  path: z.literal("/audit-logs"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_audit_logs_id = typeof get_Get_audit_logs_id;
export const get_Get_audit_logs_id = {
  method: z.literal("GET"),
  path: z.literal("/audit-logs/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_notifications_unread_count = typeof get_Get_notifications_unread_count;
export const get_Get_notifications_unread_count = {
  method: z.literal("GET"),
  path: z.literal("/notifications/unread-count"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type put_Put_notifications_read_all = typeof put_Put_notifications_read_all;
export const put_Put_notifications_read_all = {
  method: z.literal("PUT"),
  path: z.literal("/notifications/read-all"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_notifications = typeof get_Get_notifications;
export const get_Get_notifications = {
  method: z.literal("GET"),
  path: z.literal("/notifications"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type put_Put_notifications_id_read = typeof put_Put_notifications_id_read;
export const put_Put_notifications_id_read = {
  method: z.literal("PUT"),
  path: z.literal("/notifications/{id}/read"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_backup_policies_export = typeof get_Get_backup_policies_export;
export const get_Get_backup_policies_export = {
  method: z.literal("GET"),
  path: z.literal("/backup-policies/export"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type post_Post_backup_policies_alerts_run = typeof post_Post_backup_policies_alerts_run;
export const post_Post_backup_policies_alerts_run = {
  method: z.literal("POST"),
  path: z.literal("/backup-policies/alerts/run"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_backup_policies_client_clientId = typeof get_Get_backup_policies_client_clientId;
export const get_Get_backup_policies_client_clientId = {
  method: z.literal("GET"),
  path: z.literal("/backup-policies/client/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_backup_policies = typeof get_Get_backup_policies;
export const get_Get_backup_policies = {
  method: z.literal("GET"),
  path: z.literal("/backup-policies"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_backup_policies = typeof post_Post_backup_policies;
export const post_Post_backup_policies = {
  method: z.literal("POST"),
  path: z.literal("/backup-policies"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_backup_policies_policyId = typeof get_Get_backup_policies_policyId;
export const get_Get_backup_policies_policyId = {
  method: z.literal("GET"),
  path: z.literal("/backup-policies/{policyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ policyId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_backup_policies_policyId = typeof put_Put_backup_policies_policyId;
export const put_Put_backup_policies_policyId = {
  method: z.literal("PUT"),
  path: z.literal("/backup-policies/{policyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ policyId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_backup_policies_policyId = typeof delete_Delete_backup_policies_policyId;
export const delete_Delete_backup_policies_policyId = {
  method: z.literal("DELETE"),
  path: z.literal("/backup-policies/{policyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ policyId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_backup_policies_policyId_document = typeof put_Put_backup_policies_policyId_document;
export const put_Put_backup_policies_policyId_document = {
  method: z.literal("PUT"),
  path: z.literal("/backup-policies/{policyId}/document"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ policyId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_backup_policies_policyId_documents = typeof get_Get_backup_policies_policyId_documents;
export const get_Get_backup_policies_policyId_documents = {
  method: z.literal("GET"),
  path: z.literal("/backup-policies/{policyId}/documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ policyId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_backup_policies_policyId_documents = typeof post_Post_backup_policies_policyId_documents;
export const post_Post_backup_policies_policyId_documents = {
  method: z.literal("POST"),
  path: z.literal("/backup-policies/{policyId}/documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ policyId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_backup_policies_documents_documentId = typeof delete_Delete_backup_policies_documents_documentId;
export const delete_Delete_backup_policies_documents_documentId = {
  method: z.literal("DELETE"),
  path: z.literal("/backup-policies/documents/{documentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ documentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_PasswordSignIn = typeof post_PasswordSignIn;
export const post_PasswordSignIn = {
  method: z.literal("POST"),
  path: z.literal("/auth/password/sign-in"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: PasswordSignInRequest },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ session: SessionDto, user: CurrentUserDto }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_RequestOtp = typeof post_RequestOtp;
export const post_RequestOtp = {
  method: z.literal("POST"),
  path: z.literal("/auth/otp/request"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: EmailRequest },
  responses: { 202: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 400: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_OtpSignIn = typeof post_OtpSignIn;
export const post_OtpSignIn = {
  method: z.literal("POST"),
  path: z.literal("/auth/otp/sign-in"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: OtpSignInRequest },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ session: SessionDto, user: CurrentUserDto }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_ChangePassword = typeof post_ChangePassword;
export const post_ChangePassword = {
  method: z.literal("POST"),
  path: z.literal("/auth/password/change"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: ChangePasswordRequest },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_GetCurrentUser = typeof get_GetCurrentUser;
export const get_GetCurrentUser = {
  method: z.literal("GET"),
  path: z.literal("/auth/me"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApiResponse.and(z.object({ data: CurrentUserDto }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_RefreshSession = typeof post_RefreshSession;
export const post_RefreshSession = {
  method: z.literal("POST"),
  path: z.literal("/auth/session/refresh"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: RefreshRequest },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ accessToken: z.string(), accessTokenExpiresAt: z.string(), refreshToken: z.string(), refreshTokenExpiresAt: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_SignOut = typeof post_SignOut;
export const post_SignOut = {
  method: z.literal("POST"),
  path: z.literal("/auth/session/sign-out"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 204: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_RequestPasswordRecovery = typeof post_RequestPasswordRecovery;
export const post_RequestPasswordRecovery = {
  method: z.literal("POST"),
  path: z.literal("/auth/password/recovery/request"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: EmailRequest },
  responses: { 202: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 400: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_CompletePasswordRecovery = typeof post_CompletePasswordRecovery;
export const post_CompletePasswordRecovery = {
  method: z.literal("POST"),
  path: z.literal("/auth/password/recovery/complete"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: CompleteRecoveryRequest },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_ForcePasswordChange = typeof post_ForcePasswordChange;
export const post_ForcePasswordChange = {
  method: z.literal("POST"),
  path: z.literal("/auth/password/force-change"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: ForcePasswordChangeRequest },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_clients = typeof get_Get_clients;
export const get_Get_clients = {
  method: z.literal("GET"),
  path: z.literal("/clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: PaginatedClientsDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_clients = typeof post_Post_clients;
export const post_Post_clients = {
  method: z.literal("POST"),
  path: z.literal("/clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId = typeof patch_Patch_clients_clientId;
export const patch_Patch_clients_clientId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ClientDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId = typeof get_Get_clients_clientId;
export const get_Get_clients_clientId = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ClientDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId = typeof delete_Delete_clients_clientId;
export const delete_Delete_clients_clientId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_users = typeof get_Get_clients_clientId_users;
export const get_Get_clients_clientId_users = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(z.object({ displayName: z.string(), email: z.string(), id: z.string(), status: z.enum(["active", "deactivated"]), userType: z.enum(["internal", "external"]) })), page: z.number(), pageSize: z.number(), total: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_assignment_context = typeof get_Get_clients_clientId_assignment_context;
export const get_Get_clients_clientId_assignment_context = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/assignment-context"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_datacenter_assignments = typeof get_Get_clients_clientId_datacenter_assignments;
export const get_Get_clients_clientId_datacenter_assignments = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/datacenter-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(DatacenterAssignmentDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_clients_clientId_datacenter_assignments = typeof put_Put_clients_clientId_datacenter_assignments;
export const put_Put_clients_clientId_datacenter_assignments = {
  method: z.literal("PUT"),
  path: z.literal("/clients/{clientId}/datacenter-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(DatacenterAssignmentDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_datacenter_assignments = typeof post_Post_clients_clientId_datacenter_assignments;
export const post_Post_clients_clientId_datacenter_assignments = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/datacenter-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: DatacenterAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_datacenter_assignments_datacenterId = typeof patch_Patch_clients_clientId_datacenter_assignments_datacenterId;
export const patch_Patch_clients_clientId_datacenter_assignments_datacenterId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/datacenter-assignments/{datacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), datacenterId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: DatacenterAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_datacenter_assignments_datacenterId = typeof delete_Delete_clients_clientId_datacenter_assignments_datacenterId;
export const delete_Delete_clients_clientId_datacenter_assignments_datacenterId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/datacenter-assignments/{datacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), datacenterId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_datacenters_unified = typeof get_Get_clients_clientId_datacenters_unified;
export const get_Get_clients_clientId_datacenters_unified = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/datacenters/unified"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(UnifiedClientDatacenterDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_delete_impact = typeof get_Get_clients_clientId_delete_impact;
export const get_Get_clients_clientId_delete_impact = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/delete-impact"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ClientDeleteImpactDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_deactivate = typeof post_Post_clients_clientId_deactivate;
export const post_Post_clients_clientId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_enterprises = typeof get_Get_enterprises;
export const get_Get_enterprises = {
  method: z.literal("GET"),
  path: z.literal("/enterprises"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(EnterpriseOrganizationalModelDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_enterprises = typeof post_Post_enterprises;
export const post_Post_enterprises = {
  method: z.literal("POST"),
  path: z.literal("/enterprises"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: EnterpriseOrganizationalModelDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_enterprises_enterpriseId = typeof get_Get_enterprises_enterpriseId;
export const get_Get_enterprises_enterpriseId = {
  method: z.literal("GET"),
  path: z.literal("/enterprises/{enterpriseId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: EnterpriseOrganizationalModelDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_enterprises_enterpriseId = typeof patch_Patch_enterprises_enterpriseId;
export const patch_Patch_enterprises_enterpriseId = {
  method: z.literal("PATCH"),
  path: z.literal("/enterprises/{enterpriseId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: EnterpriseOrganizationalModelDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_enterprises_enterpriseId = typeof delete_Delete_enterprises_enterpriseId;
export const delete_Delete_enterprises_enterpriseId = {
  method: z.literal("DELETE"),
  path: z.literal("/enterprises/{enterpriseId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_deactivate = typeof post_Post_enterprises_enterpriseId_deactivate;
export const post_Post_enterprises_enterpriseId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: EnterpriseOrganizationalModelDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_enterprises_enterpriseId_delivery_units = typeof get_Get_enterprises_enterpriseId_delivery_units;
export const get_Get_enterprises_enterpriseId_delivery_units = {
  method: z.literal("GET"),
  path: z.literal("/enterprises/{enterpriseId}/delivery-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ enterpriseId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_delivery_units = typeof post_Post_enterprises_enterpriseId_delivery_units;
export const post_Post_enterprises_enterpriseId_delivery_units = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/delivery-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_enterprises_enterpriseId_practice_units = typeof get_Get_enterprises_enterpriseId_practice_units;
export const get_Get_enterprises_enterpriseId_practice_units = {
  method: z.literal("GET"),
  path: z.literal("/enterprises/{enterpriseId}/practice-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ enterpriseId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_practice_units = typeof post_Post_enterprises_enterpriseId_practice_units;
export const post_Post_enterprises_enterpriseId_practice_units = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/practice-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_enterprises_enterpriseId_delivery_units_unitId = typeof patch_Patch_enterprises_enterpriseId_delivery_units_unitId;
export const patch_Patch_enterprises_enterpriseId_delivery_units_unitId = {
  method: z.literal("PATCH"),
  path: z.literal("/enterprises/{enterpriseId}/delivery-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string(), unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_enterprises_enterpriseId_practice_units_unitId = typeof patch_Patch_enterprises_enterpriseId_practice_units_unitId;
export const patch_Patch_enterprises_enterpriseId_practice_units_unitId = {
  method: z.literal("PATCH"),
  path: z.literal("/enterprises/{enterpriseId}/practice-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string(), unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_delivery_units_unitId_deactivate = typeof post_Post_enterprises_enterpriseId_delivery_units_unitId_deactivate;
export const post_Post_enterprises_enterpriseId_delivery_units_unitId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/delivery-units/{unitId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string(), unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_practice_units_unitId_deactivate = typeof post_Post_enterprises_enterpriseId_practice_units_unitId_deactivate;
export const post_Post_enterprises_enterpriseId_practice_units_unitId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/practice-units/{unitId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string(), unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_delivery_units = typeof get_Get_delivery_units;
export const get_Get_delivery_units = {
  method: z.literal("GET"),
  path: z.literal("/delivery-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(FlatEnterpriseUnitDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_delivery_units = typeof post_Post_delivery_units;
export const post_Post_delivery_units = {
  method: z.literal("POST"),
  path: z.literal("/delivery-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_delivery_units_unitId = typeof get_Get_delivery_units_unitId;
export const get_Get_delivery_units_unitId = {
  method: z.literal("GET"),
  path: z.literal("/delivery-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_delivery_units_unitId = typeof patch_Patch_delivery_units_unitId;
export const patch_Patch_delivery_units_unitId = {
  method: z.literal("PATCH"),
  path: z.literal("/delivery-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_delivery_units_unitId = typeof delete_Delete_delivery_units_unitId;
export const delete_Delete_delivery_units_unitId = {
  method: z.literal("DELETE"),
  path: z.literal("/delivery-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_practice_units = typeof get_Get_practice_units;
export const get_Get_practice_units = {
  method: z.literal("GET"),
  path: z.literal("/practice-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(FlatEnterpriseUnitDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_practice_units = typeof post_Post_practice_units;
export const post_Post_practice_units = {
  method: z.literal("POST"),
  path: z.literal("/practice-units"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_practice_units_unitId = typeof get_Get_practice_units_unitId;
export const get_Get_practice_units_unitId = {
  method: z.literal("GET"),
  path: z.literal("/practice-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_practice_units_unitId = typeof patch_Patch_practice_units_unitId;
export const patch_Patch_practice_units_unitId = {
  method: z.literal("PATCH"),
  path: z.literal("/practice-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: FlatEnterpriseUnitDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_practice_units_unitId = typeof delete_Delete_practice_units_unitId;
export const delete_Delete_practice_units_unitId = {
  method: z.literal("DELETE"),
  path: z.literal("/practice-units/{unitId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ unitId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_companies = typeof get_Get_clients_clientId_companies;
export const get_Get_clients_clientId_companies = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/companies"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientCompanySummaryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_companies = typeof post_Post_clients_clientId_companies;
export const post_Post_clients_clientId_companies = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/companies"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientCompanyDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_companies_companyId = typeof get_Get_clients_clientId_companies_companyId;
export const get_Get_clients_clientId_companies_companyId = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/companies/{companyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), companyId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ClientCompanyDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_companies_companyId = typeof patch_Patch_clients_clientId_companies_companyId;
export const patch_Patch_clients_clientId_companies_companyId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/companies/{companyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), companyId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ClientCompanyDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_companies_companyId = typeof delete_Delete_clients_clientId_companies_companyId;
export const delete_Delete_clients_clientId_companies_companyId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/companies/{companyId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), companyId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeleteCompanyResultDto }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_companies_companyId_datacenter_links = typeof post_Post_clients_clientId_companies_companyId_datacenter_links;
export const post_Post_clients_clientId_companies_companyId_datacenter_links = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/companies/{companyId}/datacenter-links"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), companyId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: CompanyDatacenterLinkDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_owned_datacenters = typeof get_Get_clients_clientId_owned_datacenters;
export const get_Get_clients_clientId_owned_datacenters = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/owned-datacenters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientOwnedDatacenterDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_owned_datacenters = typeof post_Post_clients_clientId_owned_datacenters;
export const post_Post_clients_clientId_owned_datacenters = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/owned-datacenters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientOwnedDatacenterDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_owned_datacenters_ownedDatacenterId = typeof get_Get_clients_clientId_owned_datacenters_ownedDatacenterId;
export const get_Get_clients_clientId_owned_datacenters_ownedDatacenterId = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ClientOwnedDatacenterDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId = typeof patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId;
export const patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ClientOwnedDatacenterDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId = typeof delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId;
export const delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeleteOwnedDatacenterResultDto }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms = typeof get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms;
export const get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), ownedDatacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientOwnedRoomDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms = typeof post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms;
export const post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientOwnedRoomDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId = typeof patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId;
export const patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ClientOwnedRoomDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId = typeof delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId;
export const delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks = typeof get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks;
export const get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientOwnedRackDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks = typeof post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks;
export const post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientOwnedRackDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId = typeof patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId;
export const patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks/{rackId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string(), rackId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ClientOwnedRackDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId = typeof delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId;
export const delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks/{rackId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), ownedDatacenterId: z.string(), roomId: z.string(), rackId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_windows = typeof get_Get_clients_clientId_windows;
export const get_Get_clients_clientId_windows = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/windows"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(MaintenanceWindowDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_windows = typeof post_Post_clients_clientId_windows;
export const post_Post_clients_clientId_windows = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/windows"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: MaintenanceWindowDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_windows_windowId = typeof patch_Patch_clients_clientId_windows_windowId;
export const patch_Patch_clients_clientId_windows_windowId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/windows/{windowId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), windowId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: MaintenanceWindowDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_windows_windowId = typeof delete_Delete_clients_clientId_windows_windowId;
export const delete_Delete_clients_clientId_windows_windowId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/windows/{windowId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), windowId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_window_groups = typeof get_Get_clients_clientId_window_groups;
export const get_Get_clients_clientId_window_groups = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/window-groups"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(WindowGroupDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_window_groups = typeof post_Post_clients_clientId_window_groups;
export const post_Post_clients_clientId_window_groups = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/window-groups"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: WindowGroupDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_window_groups_groupId = typeof patch_Patch_clients_clientId_window_groups_groupId;
export const patch_Patch_clients_clientId_window_groups_groupId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/window-groups/{groupId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), groupId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: WindowGroupDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_window_groups_groupId = typeof delete_Delete_clients_clientId_window_groups_groupId;
export const delete_Delete_clients_clientId_window_groups_groupId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/window-groups/{groupId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), groupId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_roles = typeof get_Get_roles;
export const get_Get_roles = {
  method: z.literal("GET"),
  path: z.literal("/roles"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_roles = typeof post_Post_roles;
export const post_Post_roles = {
  method: z.literal("POST"),
  path: z.literal("/roles"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_roles_catalog = typeof get_Get_roles_catalog;
export const get_Get_roles_catalog = {
  method: z.literal("GET"),
  path: z.literal("/roles/catalog"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_roles_roleId = typeof get_Get_roles_roleId;
export const get_Get_roles_roleId = {
  method: z.literal("GET"),
  path: z.literal("/roles/{roleId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roleId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_roles_roleId = typeof patch_Patch_roles_roleId;
export const patch_Patch_roles_roleId = {
  method: z.literal("PATCH"),
  path: z.literal("/roles/{roleId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roleId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_roles_roleId = typeof delete_Delete_roles_roleId;
export const delete_Delete_roles_roleId = {
  method: z.literal("DELETE"),
  path: z.literal("/roles/{roleId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roleId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_roles_roleId_users = typeof get_Get_roles_roleId_users;
export const get_Get_roles_roleId_users = {
  method: z.literal("GET"),
  path: z.literal("/roles/{roleId}/users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ roleId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_permissions_catalog = typeof get_Get_permissions_catalog;
export const get_Get_permissions_catalog = {
  method: z.literal("GET"),
  path: z.literal("/permissions/catalog"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_permissions_matrix = typeof get_Get_permissions_matrix;
export const get_Get_permissions_matrix = {
  method: z.literal("GET"),
  path: z.literal("/permissions/matrix"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_user_client_assignments = typeof get_Get_user_client_assignments;
export const get_Get_user_client_assignments = {
  method: z.literal("GET"),
  path: z.literal("/user-client-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_users = typeof post_Post_users;
export const post_Post_users = {
  method: z.literal("POST"),
  path: z.literal("/users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users = typeof get_Get_users;
export const get_Get_users = {
  method: z.literal("GET"),
  path: z.literal("/users"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: PaginatedUsers }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_users_userId = typeof get_Get_users_userId;
export const get_Get_users_userId = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: UserDetailsDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_users_userId = typeof put_Put_users_userId;
export const put_Put_users_userId = {
  method: z.literal("PUT"),
  path: z.literal("/users/{userId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ success: z.object({ valueOf: z.unknown() }) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_users_userId_deactivate = typeof post_Post_users_userId_deactivate;
export const post_Post_users_userId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/users/{userId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ success: z.object({ valueOf: z.unknown() }) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_users_userId_reactivate = typeof post_Post_users_userId_reactivate;
export const post_Post_users_userId_reactivate = {
  method: z.literal("POST"),
  path: z.literal("/users/{userId}/reactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ success: z.object({ valueOf: z.unknown() }) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users_userId_client_assignments = typeof get_Get_users_userId_client_assignments;
export const get_Get_users_userId_client_assignments = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}/client-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_users_userId_client_assignments = typeof put_Put_users_userId_client_assignments;
export const put_Put_users_userId_client_assignments = {
  method: z.literal("PUT"),
  path: z.literal("/users/{userId}/client-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users_userId_client_assignments_clientId = typeof get_Get_users_userId_client_assignments_clientId;
export const get_Get_users_userId_client_assignments_clientId = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}/client-assignments/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string(), clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_users_userId_client_assignments_clientId = typeof put_Put_users_userId_client_assignments_clientId;
export const put_Put_users_userId_client_assignments_clientId = {
  method: z.literal("PUT"),
  path: z.literal("/users/{userId}/client-assignments/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string(), clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_users_userId_client_assignments_clientId = typeof delete_Delete_users_userId_client_assignments_clientId;
export const delete_Delete_users_userId_client_assignments_clientId = {
  method: z.literal("DELETE"),
  path: z.literal("/users/{userId}/client-assignments/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string(), clientId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_users_userId_roles = typeof put_Put_users_userId_roles;
export const put_Put_users_userId_roles = {
  method: z.literal("PUT"),
  path: z.literal("/users/{userId}/roles"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_users_userId_wbs_assignments = typeof put_Put_users_userId_wbs_assignments;
export const put_Put_users_userId_wbs_assignments = {
  method: z.literal("PUT"),
  path: z.literal("/users/{userId}/wbs-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users_userId_wbs_coding_assignments = typeof get_Get_users_userId_wbs_coding_assignments;
export const get_Get_users_userId_wbs_coding_assignments = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}/wbs-coding-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_users_userId_wbs_coding_assignments = typeof post_Post_users_userId_wbs_coding_assignments;
export const post_Post_users_userId_wbs_coding_assignments = {
  method: z.literal("POST"),
  path: z.literal("/users/{userId}/wbs-coding-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_users_userId_wbs_coding_assignments_assignmentId = typeof patch_Patch_users_userId_wbs_coding_assignments_assignmentId;
export const patch_Patch_users_userId_wbs_coding_assignments_assignmentId = {
  method: z.literal("PATCH"),
  path: z.literal("/users/{userId}/wbs-coding-assignments/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string(), assignmentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_users_userId_wbs_coding_assignments_assignmentId = typeof delete_Delete_users_userId_wbs_coding_assignments_assignmentId;
export const delete_Delete_users_userId_wbs_coding_assignments_assignmentId = {
  method: z.literal("DELETE"),
  path: z.literal("/users/{userId}/wbs-coding-assignments/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string(), assignmentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_users_userId_welcome_email = typeof post_Post_users_userId_welcome_email;
export const post_Post_users_userId_welcome_email = {
  method: z.literal("POST"),
  path: z.literal("/users/{userId}/welcome-email"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 202: ApiResponse.and(z.object({ data: WelcomeEmailResultDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunity_catalogs_phases = typeof get_Get_opportunity_catalogs_phases;
export const get_Get_opportunity_catalogs_phases = {
  method: z.literal("GET"),
  path: z.literal("/opportunity-catalogs/phases"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_opportunity_catalogs_step_templates = typeof get_Get_opportunity_catalogs_step_templates;
export const get_Get_opportunity_catalogs_step_templates = {
  method: z.literal("GET"),
  path: z.literal("/opportunity-catalogs/step-templates"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_opportunity_catalogs_practices = typeof get_Get_opportunity_catalogs_practices;
export const get_Get_opportunity_catalogs_practices = {
  method: z.literal("GET"),
  path: z.literal("/opportunity-catalogs/practices"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_opportunity_catalogs_deal_types = typeof get_Get_opportunity_catalogs_deal_types;
export const get_Get_opportunity_catalogs_deal_types = {
  method: z.literal("GET"),
  path: z.literal("/opportunity-catalogs/deal-types"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_opportunity_catalogs_offering_codes = typeof get_Get_opportunity_catalogs_offering_codes;
export const get_Get_opportunity_catalogs_offering_codes = {
  method: z.literal("GET"),
  path: z.literal("/opportunity-catalogs/offering-codes"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_opportunities = typeof get_Get_clients_clientId_opportunities;
export const get_Get_clients_clientId_opportunities = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/opportunities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_opportunities = typeof post_Post_clients_clientId_opportunities;
export const post_Post_clients_clientId_opportunities = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/opportunities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities = typeof get_Get_opportunities;
export const get_Get_opportunities = {
  method: z.literal("GET"),
  path: z.literal("/opportunities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId = typeof get_Get_opportunities_opportunityId;
export const get_Get_opportunities_opportunityId = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId = typeof put_Put_opportunities_opportunityId;
export const put_Put_opportunities_opportunityId = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId = typeof delete_Delete_opportunities_opportunityId;
export const delete_Delete_opportunities_opportunityId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_export_excel = typeof get_Get_opportunities_opportunityId_export_excel;
export const get_Get_opportunities_opportunityId_export_excel = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/export-excel"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_opportunities_opportunityId_steps = typeof get_Get_opportunities_opportunityId_steps;
export const get_Get_opportunities_opportunityId_steps = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/steps"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_opportunities_opportunityId_steps = typeof post_Post_opportunities_opportunityId_steps;
export const post_Post_opportunities_opportunityId_steps = {
  method: z.literal("POST"),
  path: z.literal("/opportunities/{opportunityId}/steps"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId_steps_stepId = typeof put_Put_opportunities_opportunityId_steps_stepId;
export const put_Put_opportunities_opportunityId_steps_stepId = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}/steps/{stepId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), stepId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId_steps_stepId = typeof delete_Delete_opportunities_opportunityId_steps_stepId;
export const delete_Delete_opportunities_opportunityId_steps_stepId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}/steps/{stepId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), stepId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_action_items = typeof get_Get_opportunities_opportunityId_action_items;
export const get_Get_opportunities_opportunityId_action_items = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/action-items"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_opportunities_opportunityId_action_items = typeof post_Post_opportunities_opportunityId_action_items;
export const post_Post_opportunities_opportunityId_action_items = {
  method: z.literal("POST"),
  path: z.literal("/opportunities/{opportunityId}/action-items"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId_action_items_itemId = typeof put_Put_opportunities_opportunityId_action_items_itemId;
export const put_Put_opportunities_opportunityId_action_items_itemId = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}/action-items/{itemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), itemId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId_action_items_itemId = typeof delete_Delete_opportunities_opportunityId_action_items_itemId;
export const delete_Delete_opportunities_opportunityId_action_items_itemId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}/action-items/{itemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), itemId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_reviews = typeof get_Get_opportunities_opportunityId_reviews;
export const get_Get_opportunities_opportunityId_reviews = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/reviews"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_opportunities_opportunityId_reviews = typeof post_Post_opportunities_opportunityId_reviews;
export const post_Post_opportunities_opportunityId_reviews = {
  method: z.literal("POST"),
  path: z.literal("/opportunities/{opportunityId}/reviews"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId_reviews_reviewId = typeof put_Put_opportunities_opportunityId_reviews_reviewId;
export const put_Put_opportunities_opportunityId_reviews_reviewId = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}/reviews/{reviewId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), reviewId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId_reviews_reviewId = typeof delete_Delete_opportunities_opportunityId_reviews_reviewId;
export const delete_Delete_opportunities_opportunityId_reviews_reviewId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}/reviews/{reviewId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), reviewId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_costs = typeof get_Get_opportunities_opportunityId_costs;
export const get_Get_opportunities_opportunityId_costs = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/costs"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_opportunities_opportunityId_costs = typeof post_Post_opportunities_opportunityId_costs;
export const post_Post_opportunities_opportunityId_costs = {
  method: z.literal("POST"),
  path: z.literal("/opportunities/{opportunityId}/costs"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId_costs_costId = typeof put_Put_opportunities_opportunityId_costs_costId;
export const put_Put_opportunities_opportunityId_costs_costId = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}/costs/{costId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), costId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId_costs_costId = typeof delete_Delete_opportunities_opportunityId_costs_costId;
export const delete_Delete_opportunities_opportunityId_costs_costId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}/costs/{costId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), costId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_practices = typeof get_Get_opportunities_opportunityId_practices;
export const get_Get_opportunities_opportunityId_practices = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/practices"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_opportunities_opportunityId_practices = typeof put_Put_opportunities_opportunityId_practices;
export const put_Put_opportunities_opportunityId_practices = {
  method: z.literal("PUT"),
  path: z.literal("/opportunities/{opportunityId}/practices"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_opportunities_opportunityId_attachments = typeof get_Get_opportunities_opportunityId_attachments;
export const get_Get_opportunities_opportunityId_attachments = {
  method: z.literal("GET"),
  path: z.literal("/opportunities/{opportunityId}/attachments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ opportunityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_opportunities_opportunityId_attachments = typeof post_Post_opportunities_opportunityId_attachments;
export const post_Post_opportunities_opportunityId_attachments = {
  method: z.literal("POST"),
  path: z.literal("/opportunities/{opportunityId}/attachments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_opportunities_opportunityId_attachments_attachmentId = typeof delete_Delete_opportunities_opportunityId_attachments_attachmentId;
export const delete_Delete_opportunities_opportunityId_attachments_attachmentId = {
  method: z.literal("DELETE"),
  path: z.literal("/opportunities/{opportunityId}/attachments/{attachmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ opportunityId: z.string(), attachmentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_plans = typeof get_Get_bcp_plans;
export const get_Get_bcp_plans = {
  method: z.literal("GET"),
  path: z.literal("/bcp-plans"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_plans = typeof post_Post_bcp_plans;
export const post_Post_bcp_plans = {
  method: z.literal("POST"),
  path: z.literal("/bcp-plans"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_plans_planId = typeof get_Get_bcp_plans_planId;
export const get_Get_bcp_plans_planId = {
  method: z.literal("GET"),
  path: z.literal("/bcp-plans/{planId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ planId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_bcp_plans_planId = typeof put_Put_bcp_plans_planId;
export const put_Put_bcp_plans_planId = {
  method: z.literal("PUT"),
  path: z.literal("/bcp-plans/{planId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ planId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_bcp_plans_planId = typeof delete_Delete_bcp_plans_planId;
export const delete_Delete_bcp_plans_planId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-plans/{planId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ planId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_tests = typeof get_Get_bcp_tests;
export const get_Get_bcp_tests = {
  method: z.literal("GET"),
  path: z.literal("/bcp-tests"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_tests = typeof post_Post_bcp_tests;
export const post_Post_bcp_tests = {
  method: z.literal("POST"),
  path: z.literal("/bcp-tests"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_tests_testId = typeof get_Get_bcp_tests_testId;
export const get_Get_bcp_tests_testId = {
  method: z.literal("GET"),
  path: z.literal("/bcp-tests/{testId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ testId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_bcp_tests_testId = typeof put_Put_bcp_tests_testId;
export const put_Put_bcp_tests_testId = {
  method: z.literal("PUT"),
  path: z.literal("/bcp-tests/{testId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ testId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_bcp_tests_testId = typeof delete_Delete_bcp_tests_testId;
export const delete_Delete_bcp_tests_testId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-tests/{testId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ testId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_tasks = typeof get_Get_bcp_tasks;
export const get_Get_bcp_tasks = {
  method: z.literal("GET"),
  path: z.literal("/bcp-tasks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_tasks = typeof post_Post_bcp_tasks;
export const post_Post_bcp_tasks = {
  method: z.literal("POST"),
  path: z.literal("/bcp-tasks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_tasks_taskId = typeof get_Get_bcp_tasks_taskId;
export const get_Get_bcp_tasks_taskId = {
  method: z.literal("GET"),
  path: z.literal("/bcp-tasks/{taskId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_bcp_tasks_taskId = typeof put_Put_bcp_tasks_taskId;
export const put_Put_bcp_tasks_taskId = {
  method: z.literal("PUT"),
  path: z.literal("/bcp-tasks/{taskId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_bcp_tasks_taskId = typeof delete_Delete_bcp_tasks_taskId;
export const delete_Delete_bcp_tasks_taskId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-tasks/{taskId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ taskId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_evidences = typeof get_Get_bcp_evidences;
export const get_Get_bcp_evidences = {
  method: z.literal("GET"),
  path: z.literal("/bcp-evidences"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_evidences = typeof post_Post_bcp_evidences;
export const post_Post_bcp_evidences = {
  method: z.literal("POST"),
  path: z.literal("/bcp-evidences"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_evidences_evidenceId = typeof get_Get_bcp_evidences_evidenceId;
export const get_Get_bcp_evidences_evidenceId = {
  method: z.literal("GET"),
  path: z.literal("/bcp-evidences/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ evidenceId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_bcp_evidences_evidenceId = typeof put_Put_bcp_evidences_evidenceId;
export const put_Put_bcp_evidences_evidenceId = {
  method: z.literal("PUT"),
  path: z.literal("/bcp-evidences/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ evidenceId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_bcp_evidences_evidenceId = typeof delete_Delete_bcp_evidences_evidenceId;
export const delete_Delete_bcp_evidences_evidenceId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-evidences/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ evidenceId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_findings = typeof get_Get_bcp_findings;
export const get_Get_bcp_findings = {
  method: z.literal("GET"),
  path: z.literal("/bcp-findings"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_bcp_findings = typeof post_Post_bcp_findings;
export const post_Post_bcp_findings = {
  method: z.literal("POST"),
  path: z.literal("/bcp-findings"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_bcp_findings_findingId = typeof get_Get_bcp_findings_findingId;
export const get_Get_bcp_findings_findingId = {
  method: z.literal("GET"),
  path: z.literal("/bcp-findings/{findingId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ findingId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_bcp_findings_findingId = typeof put_Put_bcp_findings_findingId;
export const put_Put_bcp_findings_findingId = {
  method: z.literal("PUT"),
  path: z.literal("/bcp-findings/{findingId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ findingId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_bcp_findings_findingId = typeof delete_Delete_bcp_findings_findingId;
export const delete_Delete_bcp_findings_findingId = {
  method: z.literal("DELETE"),
  path: z.literal("/bcp-findings/{findingId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ findingId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_shift_tickets = typeof get_Get_shift_tickets;
export const get_Get_shift_tickets = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_shift_tickets = typeof post_Post_shift_tickets;
export const post_Post_shift_tickets = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_shift_tickets_activities_activityId_end = typeof put_Put_shift_tickets_activities_activityId_end;
export const put_Put_shift_tickets_activities_activityId_end = {
  method: z.literal("PUT"),
  path: z.literal("/shift-tickets/activities/{activityId}/end"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_ticketId = typeof get_Get_shift_tickets_ticketId;
export const get_Get_shift_tickets_ticketId = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/{ticketId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_ticketId_enroll = typeof post_Post_shift_tickets_ticketId_enroll;
export const post_Post_shift_tickets_ticketId_enroll = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/{ticketId}/enroll"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_ticketId_activities = typeof post_Post_shift_tickets_ticketId_activities;
export const post_Post_shift_tickets_ticketId_activities = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/{ticketId}/activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_shift_tickets_ticketId_close = typeof put_Put_shift_tickets_ticketId_close;
export const put_Put_shift_tickets_ticketId_close = {
  method: z.literal("PUT"),
  path: z.literal("/shift-tickets/{ticketId}/close"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_shift_tickets_ticketId_cancel = typeof put_Put_shift_tickets_ticketId_cancel;
export const put_Put_shift_tickets_ticketId_cancel = {
  method: z.literal("PUT"),
  path: z.literal("/shift-tickets/{ticketId}/cancel"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_shift_tickets_ticketId_reopen = typeof put_Put_shift_tickets_ticketId_reopen;
export const put_Put_shift_tickets_ticketId_reopen = {
  method: z.literal("PUT"),
  path: z.literal("/shift-tickets/{ticketId}/reopen"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_shift_tickets_ticketId_rca = typeof get_Get_shift_tickets_ticketId_rca;
export const get_Get_shift_tickets_ticketId_rca = {
  method: z.literal("GET"),
  path: z.literal("/shift-tickets/{ticketId}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ ticketId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_ticketId_rca = typeof post_Post_shift_tickets_ticketId_rca;
export const post_Post_shift_tickets_ticketId_rca = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/{ticketId}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_ticketId_rca_generate_pdf = typeof post_Post_shift_tickets_ticketId_rca_generate_pdf;
export const post_Post_shift_tickets_ticketId_rca_generate_pdf = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/{ticketId}/rca/generate-pdf"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ ticketId: z.string() }) },
  responses: { 201: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 201: z.object({ "Content-Disposition": z.string() }) },
};

export type post_Post_shift_tickets_rca_rcaId_findings = typeof post_Post_shift_tickets_rca_rcaId_findings;
export const post_Post_shift_tickets_rca_rcaId_findings = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/rca/{rcaId}/findings"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rcaId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_shift_tickets_rca_findings_findingId_actions = typeof post_Post_shift_tickets_rca_findings_findingId_actions;
export const post_Post_shift_tickets_rca_findings_findingId_actions = {
  method: z.literal("POST"),
  path: z.literal("/shift-tickets/rca/findings/{findingId}/actions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ findingId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_incidents_sla_config_clientId = typeof get_Get_incidents_sla_config_clientId;
export const get_Get_incidents_sla_config_clientId = {
  method: z.literal("GET"),
  path: z.literal("/incidents/sla-config/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_sla_config_clientId = typeof put_Put_incidents_sla_config_clientId;
export const put_Put_incidents_sla_config_clientId = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/sla-config/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_activities_activityId_end = typeof put_Put_incidents_activities_activityId_end;
export const put_Put_incidents_activities_activityId_end = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/activities/{activityId}/end"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_incidents = typeof get_Get_incidents;
export const get_Get_incidents = {
  method: z.literal("GET"),
  path: z.literal("/incidents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_incidents = typeof post_Post_incidents;
export const post_Post_incidents = {
  method: z.literal("POST"),
  path: z.literal("/incidents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_incidents_incidentId = typeof get_Get_incidents_incidentId;
export const get_Get_incidents_incidentId = {
  method: z.literal("GET"),
  path: z.literal("/incidents/{incidentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_incidentId_participate = typeof post_Post_incidents_incidentId_participate;
export const post_Post_incidents_incidentId_participate = {
  method: z.literal("POST"),
  path: z.literal("/incidents/{incidentId}/participate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_incidentId_end_participation = typeof put_Put_incidents_incidentId_end_participation;
export const put_Put_incidents_incidentId_end_participation = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/{incidentId}/end-participation"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_incidentId_activities = typeof post_Post_incidents_incidentId_activities;
export const post_Post_incidents_incidentId_activities = {
  method: z.literal("POST"),
  path: z.literal("/incidents/{incidentId}/activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_incidentId_resolve = typeof put_Put_incidents_incidentId_resolve;
export const put_Put_incidents_incidentId_resolve = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/{incidentId}/resolve"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_incidentId_close = typeof put_Put_incidents_incidentId_close;
export const put_Put_incidents_incidentId_close = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/{incidentId}/close"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_incidentId_impact_clock_start = typeof put_Put_incidents_incidentId_impact_clock_start;
export const put_Put_incidents_incidentId_impact_clock_start = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/{incidentId}/impact-clock/start"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_incidents_incidentId_impact_clock_stop = typeof put_Put_incidents_incidentId_impact_clock_stop;
export const put_Put_incidents_incidentId_impact_clock_stop = {
  method: z.literal("PUT"),
  path: z.literal("/incidents/{incidentId}/impact-clock/stop"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_incidents_incidentId_rca = typeof get_Get_incidents_incidentId_rca;
export const get_Get_incidents_incidentId_rca = {
  method: z.literal("GET"),
  path: z.literal("/incidents/{incidentId}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ incidentId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_incidentId_rca = typeof post_Post_incidents_incidentId_rca;
export const post_Post_incidents_incidentId_rca = {
  method: z.literal("POST"),
  path: z.literal("/incidents/{incidentId}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_incidentId_rca_generate_pdf = typeof post_Post_incidents_incidentId_rca_generate_pdf;
export const post_Post_incidents_incidentId_rca_generate_pdf = {
  method: z.literal("POST"),
  path: z.literal("/incidents/{incidentId}/rca/generate-pdf"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ incidentId: z.string() }) },
  responses: { 201: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 201: z.object({ "Content-Disposition": z.string() }) },
};

export type post_Post_incidents_rca_rcaId_findings = typeof post_Post_incidents_rca_rcaId_findings;
export const post_Post_incidents_rca_rcaId_findings = {
  method: z.literal("POST"),
  path: z.literal("/incidents/rca/{rcaId}/findings"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rcaId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_rca_findings_findingId_actions = typeof post_Post_incidents_rca_findings_findingId_actions;
export const post_Post_incidents_rca_findings_findingId_actions = {
  method: z.literal("POST"),
  path: z.literal("/incidents/rca/findings/{findingId}/actions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ findingId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_rca_rcaId_contributing_factors = typeof post_Post_incidents_rca_rcaId_contributing_factors;
export const post_Post_incidents_rca_rcaId_contributing_factors = {
  method: z.literal("POST"),
  path: z.literal("/incidents/rca/{rcaId}/contributing-factors"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rcaId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_rca_rcaId_lessons = typeof post_Post_incidents_rca_rcaId_lessons;
export const post_Post_incidents_rca_rcaId_lessons = {
  method: z.literal("POST"),
  path: z.literal("/incidents/rca/{rcaId}/lessons"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rcaId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_incidents_rca_rcaId_versions = typeof post_Post_incidents_rca_rcaId_versions;
export const post_Post_incidents_rca_rcaId_versions = {
  method: z.literal("POST"),
  path: z.literal("/incidents/rca/{rcaId}/versions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rcaId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_standby_assignments = typeof get_Get_clients_clientId_standby_assignments;
export const get_Get_clients_clientId_standby_assignments = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/standby-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentListDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users_userId_standby_assignments = typeof get_Get_users_userId_standby_assignments;
export const get_Get_users_userId_standby_assignments = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}/standby-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentListDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_users_userId_standby_context = typeof get_Get_users_userId_standby_context;
export const get_Get_users_userId_standby_context = {
  method: z.literal("GET"),
  path: z.literal("/users/{userId}/standby-context"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentPlanningContextDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_standby_assignments_stats = typeof get_Get_standby_assignments_stats;
export const get_Get_standby_assignments_stats = {
  method: z.literal("GET"),
  path: z.literal("/standby-assignments/stats"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentStatisticsDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_standby_assignments_bulk = typeof post_Post_standby_assignments_bulk;
export const post_Post_standby_assignments_bulk = {
  method: z.literal("POST"),
  path: z.literal("/standby-assignments/bulk"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: BulkCreateStandbyAssignmentsResultDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_standby_assignments_weekly_copy = typeof post_Post_standby_assignments_weekly_copy;
export const post_Post_standby_assignments_weekly_copy = {
  method: z.literal("POST"),
  path: z.literal("/standby-assignments/weekly-copy"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: BulkCreateStandbyAssignmentsResultDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_standby_assignments_week_copy_draft = typeof post_Post_standby_assignments_week_copy_draft;
export const post_Post_standby_assignments_week_copy_draft = {
  method: z.literal("POST"),
  path: z.literal("/standby-assignments/week-copy-draft"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(z.object({ clientId: z.string().nullable().optional(), endAt: z.string().nullable().optional(), escalationLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable().optional(), isActive: z.union([z.object({ valueOf: z.unknown() }), z.object({ valueOf: z.unknown() })]).nullable().optional(), notes: z.string().nullable().optional(), platformId: z.number().nullable().optional(), shiftSummary: z.string().nullable().optional(), sourceAssignmentId: z.string(), startAt: z.string().nullable().optional(), userId: z.string() })) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_standby_assignments_export = typeof get_Get_standby_assignments_export;
export const get_Get_standby_assignments_export = {
  method: z.literal("GET"),
  path: z.literal("/standby-assignments/export"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_standby_assignments_export_report = typeof get_Get_standby_assignments_export_report;
export const get_Get_standby_assignments_export_report = {
  method: z.literal("GET"),
  path: z.literal("/standby-assignments/export-report"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type post_Post_standby_assignments = typeof post_Post_standby_assignments;
export const post_Post_standby_assignments = {
  method: z.literal("POST"),
  path: z.literal("/standby-assignments"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: StandbyAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_standby_assignments_assignmentId_toggle = typeof post_Post_standby_assignments_assignmentId_toggle;
export const post_Post_standby_assignments_assignmentId_toggle = {
  method: z.literal("POST"),
  path: z.literal("/standby-assignments/{assignmentId}/toggle"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ assignmentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_standby_assignments_assignmentId = typeof get_Get_standby_assignments_assignmentId;
export const get_Get_standby_assignments_assignmentId = {
  method: z.literal("GET"),
  path: z.literal("/standby-assignments/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ assignmentId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_standby_assignments_assignmentId = typeof patch_Patch_standby_assignments_assignmentId;
export const patch_Patch_standby_assignments_assignmentId = {
  method: z.literal("PATCH"),
  path: z.literal("/standby-assignments/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ assignmentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: StandbyAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_standby_assignments_assignmentId = typeof delete_Delete_standby_assignments_assignmentId;
export const delete_Delete_standby_assignments_assignmentId = {
  method: z.literal("DELETE"),
  path: z.literal("/standby-assignments/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ assignmentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_ticketing_tools = typeof get_Get_ticketing_tools;
export const get_Get_ticketing_tools = {
  method: z.literal("GET"),
  path: z.literal("/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(TicketingToolDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_ticketing_tools = typeof post_Post_ticketing_tools;
export const post_Post_ticketing_tools = {
  method: z.literal("POST"),
  path: z.literal("/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: TicketingToolDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_ticketing_tools_toolId = typeof patch_Patch_ticketing_tools_toolId;
export const patch_Patch_ticketing_tools_toolId = {
  method: z.literal("PATCH"),
  path: z.literal("/ticketing-tools/{toolId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ toolId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: TicketingToolDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_ticketing_tools_toolId = typeof delete_Delete_ticketing_tools_toolId;
export const delete_Delete_ticketing_tools_toolId = {
  method: z.literal("DELETE"),
  path: z.literal("/ticketing-tools/{toolId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ toolId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeleteSuccessDto }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_enterprises_enterpriseId_ticketing_tools = typeof get_Get_enterprises_enterpriseId_ticketing_tools;
export const get_Get_enterprises_enterpriseId_ticketing_tools = {
  method: z.literal("GET"),
  path: z.literal("/enterprises/{enterpriseId}/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ enterpriseId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(EnterpriseToolAssignmentDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_enterprises_enterpriseId_ticketing_tools = typeof post_Post_enterprises_enterpriseId_ticketing_tools;
export const post_Post_enterprises_enterpriseId_ticketing_tools = {
  method: z.literal("POST"),
  path: z.literal("/enterprises/{enterpriseId}/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: EnterpriseToolAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_enterprises_enterpriseId_ticketing_tools_assignmentId = typeof delete_Delete_enterprises_enterpriseId_ticketing_tools_assignmentId;
export const delete_Delete_enterprises_enterpriseId_ticketing_tools_assignmentId = {
  method: z.literal("DELETE"),
  path: z.literal("/enterprises/{enterpriseId}/ticketing-tools/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ enterpriseId: z.string(), assignmentId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeleteSuccessDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_ticketing_tools = typeof get_Get_clients_clientId_ticketing_tools;
export const get_Get_clients_clientId_ticketing_tools = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientToolAssignmentDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_ticketing_tools = typeof post_Post_clients_clientId_ticketing_tools;
export const post_Post_clients_clientId_ticketing_tools = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/ticketing-tools"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ClientToolAssignmentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_ticketing_tools_assignmentId = typeof delete_Delete_clients_clientId_ticketing_tools_assignmentId;
export const delete_Delete_clients_clientId_ticketing_tools_assignmentId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/ticketing-tools/{assignmentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), assignmentId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeleteSuccessDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_activity_categories = typeof get_Get_activity_categories;
export const get_Get_activity_categories = {
  method: z.literal("GET"),
  path: z.literal("/activity-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ActivityCategoryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_activity_categories = typeof post_Post_activity_categories;
export const post_Post_activity_categories = {
  method: z.literal("POST"),
  path: z.literal("/activity-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ActivityCategoryDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_activity_categories_categoryId = typeof patch_Patch_activity_categories_categoryId;
export const patch_Patch_activity_categories_categoryId = {
  method: z.literal("PATCH"),
  path: z.literal("/activity-categories/{categoryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ categoryId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityCategoryDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_infrastructure_baseline = typeof get_Get_clients_clientId_infrastructure_baseline;
export const get_Get_clients_clientId_infrastructure_baseline = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/infrastructure-baseline"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_baseline_history = typeof get_Get_clients_clientId_baseline_history;
export const get_Get_clients_clientId_baseline_history = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/baseline-history"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_baseline_history_periods = typeof get_Get_clients_clientId_baseline_history_periods;
export const get_Get_clients_clientId_baseline_history_periods = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/baseline-history/periods"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_baseline_snapshots_id_download = typeof get_Get_baseline_snapshots_id_download;
export const get_Get_baseline_snapshots_id_download = {
  method: z.literal("GET"),
  path: z.literal("/baseline-snapshots/{id}/download"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ id: z.string() }) },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_resource_snapshots = typeof get_Get_resource_snapshots;
export const get_Get_resource_snapshots = {
  method: z.literal("GET"),
  path: z.literal("/resource-snapshots"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_resource_snapshots = typeof post_Post_resource_snapshots;
export const post_Post_resource_snapshots = {
  method: z.literal("POST"),
  path: z.literal("/resource-snapshots"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_resource_snapshots_bulk = typeof post_Post_resource_snapshots_bulk;
export const post_Post_resource_snapshots_bulk = {
  method: z.literal("POST"),
  path: z.literal("/resource-snapshots/bulk"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_resource_snapshots_id = typeof delete_Delete_resource_snapshots_id;
export const delete_Delete_resource_snapshots_id = {
  method: z.literal("DELETE"),
  path: z.literal("/resource-snapshots/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_snapshots = typeof get_Get_device_snapshots;
export const get_Get_device_snapshots = {
  method: z.literal("GET"),
  path: z.literal("/device-snapshots"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_device_snapshots = typeof post_Post_device_snapshots;
export const post_Post_device_snapshots = {
  method: z.literal("POST"),
  path: z.literal("/device-snapshots"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_device_snapshots_bulk = typeof post_Post_device_snapshots_bulk;
export const post_Post_device_snapshots_bulk = {
  method: z.literal("POST"),
  path: z.literal("/device-snapshots/bulk"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_device_snapshots_id = typeof delete_Delete_device_snapshots_id;
export const delete_Delete_device_snapshots_id = {
  method: z.literal("DELETE"),
  path: z.literal("/device-snapshots/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_cloud_providers = typeof get_Get_cloud_providers;
export const get_Get_cloud_providers = {
  method: z.literal("GET"),
  path: z.literal("/cloud-providers"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_cloud_accounts = typeof get_Get_cloud_accounts;
export const get_Get_cloud_accounts = {
  method: z.literal("GET"),
  path: z.literal("/cloud-accounts"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_cloud_accounts = typeof post_Post_cloud_accounts;
export const post_Post_cloud_accounts = {
  method: z.literal("POST"),
  path: z.literal("/cloud-accounts"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_cloud_accounts_accountId = typeof get_Get_cloud_accounts_accountId;
export const get_Get_cloud_accounts_accountId = {
  method: z.literal("GET"),
  path: z.literal("/cloud-accounts/{accountId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ accountId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_cloud_accounts_accountId = typeof patch_Patch_cloud_accounts_accountId;
export const patch_Patch_cloud_accounts_accountId = {
  method: z.literal("PATCH"),
  path: z.literal("/cloud-accounts/{accountId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ accountId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_cloud_accounts_accountId = typeof delete_Delete_cloud_accounts_accountId;
export const delete_Delete_cloud_accounts_accountId = {
  method: z.literal("DELETE"),
  path: z.literal("/cloud-accounts/{accountId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ accountId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_cloud_services = typeof get_Get_cloud_services;
export const get_Get_cloud_services = {
  method: z.literal("GET"),
  path: z.literal("/cloud-services"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_cloud_services = typeof post_Post_cloud_services;
export const post_Post_cloud_services = {
  method: z.literal("POST"),
  path: z.literal("/cloud-services"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_cloud_services_serviceId = typeof patch_Patch_cloud_services_serviceId;
export const patch_Patch_cloud_services_serviceId = {
  method: z.literal("PATCH"),
  path: z.literal("/cloud-services/{serviceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ serviceId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_cloud_services_serviceId = typeof delete_Delete_cloud_services_serviceId;
export const delete_Delete_cloud_services_serviceId = {
  method: z.literal("DELETE"),
  path: z.literal("/cloud-services/{serviceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ serviceId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_cloud_volumes = typeof get_Get_cloud_volumes;
export const get_Get_cloud_volumes = {
  method: z.literal("GET"),
  path: z.literal("/cloud-volumes"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_cloud_volumes = typeof post_Post_cloud_volumes;
export const post_Post_cloud_volumes = {
  method: z.literal("POST"),
  path: z.literal("/cloud-volumes"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_cloud_volumes_bulk_upsert = typeof post_Post_cloud_volumes_bulk_upsert;
export const post_Post_cloud_volumes_bulk_upsert = {
  method: z.literal("POST"),
  path: z.literal("/cloud-volumes/bulk-upsert"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_cloud_volumes_volumeId = typeof patch_Patch_cloud_volumes_volumeId;
export const patch_Patch_cloud_volumes_volumeId = {
  method: z.literal("PATCH"),
  path: z.literal("/cloud-volumes/{volumeId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ volumeId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_cloud_volumes_volumeId = typeof delete_Delete_cloud_volumes_volumeId;
export const delete_Delete_cloud_volumes_volumeId = {
  method: z.literal("DELETE"),
  path: z.literal("/cloud-volumes/{volumeId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ volumeId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_cloud_summary = typeof get_Get_clients_clientId_cloud_summary;
export const get_Get_clients_clientId_cloud_summary = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/cloud-summary"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_consumption_conflicts = typeof get_Get_consumption_conflicts;
export const get_Get_consumption_conflicts = {
  method: z.literal("GET"),
  path: z.literal("/consumption-conflicts"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_consumption_conflicts = typeof post_Post_consumption_conflicts;
export const post_Post_consumption_conflicts = {
  method: z.literal("POST"),
  path: z.literal("/consumption-conflicts"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_consumption_conflicts_id = typeof get_Get_consumption_conflicts_id;
export const get_Get_consumption_conflicts_id = {
  method: z.literal("GET"),
  path: z.literal("/consumption-conflicts/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_consumption_conflicts_id_resolve = typeof put_Put_consumption_conflicts_id_resolve;
export const put_Put_consumption_conflicts_id_resolve = {
  method: z.literal("PUT"),
  path: z.literal("/consumption-conflicts/{id}/resolve"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_ListDatacenters = typeof get_ListDatacenters;
export const get_ListDatacenters = {
  method: z.literal("GET"),
  path: z.literal("/datacenters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), status: z.enum(["planned", "active", "maintenance", "retired", "all"]), countryCode: z.string().min(2).max(2), city: z.string(), clientId: z.uuid(), includeRetired: z.coerce.boolean().default(false), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: DatacenterCatalogDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_CreateDatacenter = typeof post_CreateDatacenter;
export const post_CreateDatacenter = {
  method: z.literal("POST"),
  path: z.literal("/datacenters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: CreateDatacenterRequest },
  responses: { 201: ApiResponse.and(z.object({ data: DatacenterDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_GetDatacenter = typeof get_GetDatacenter;
export const get_GetDatacenter = {
  method: z.literal("GET"),
  path: z.literal("/datacenters/{datacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ datacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DatacenterDetailDto }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_UpdateDatacenter = typeof patch_UpdateDatacenter;
export const patch_UpdateDatacenter = {
  method: z.literal("PATCH"),
  path: z.literal("/datacenters/{datacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ datacenterId: z.string() }), body: UpdateDatacenterRequest },
  responses: { 200: ApiResponse.and(z.object({ data: DatacenterDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_DeleteDatacenter = typeof delete_DeleteDatacenter;
export const delete_DeleteDatacenter = {
  method: z.literal("DELETE"),
  path: z.literal("/datacenters/{datacenterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ datacenterId: z.string() }), body: DeleteDatacenterRequest },
  responses: { 200: ApiResponse.and(z.object({ data: z.union([DatacenterDetailDto, DeleteDatacenterResultDto]) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_GetDatacenterRetirementImpact = typeof get_GetDatacenterRetirementImpact;
export const get_GetDatacenterRetirementImpact = {
  method: z.literal("GET"),
  path: z.literal("/datacenters/{datacenterId}/retirement-impact"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ datacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: RetirementImpactDto }).partial()), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_models = typeof get_Get_device_models;
export const get_Get_device_models = {
  method: z.literal("GET"),
  path: z.literal("/device-models"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: DeviceModelCatalogDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_device_models = typeof post_Post_device_models;
export const post_Post_device_models = {
  method: z.literal("POST"),
  path: z.literal("/device-models"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: DeviceModelDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_models_deviceModelId = typeof get_Get_device_models_deviceModelId;
export const get_Get_device_models_deviceModelId = {
  method: z.literal("GET"),
  path: z.literal("/device-models/{deviceModelId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceModelId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: DeviceModelDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_device_models_deviceModelId = typeof patch_Patch_device_models_deviceModelId;
export const patch_Patch_device_models_deviceModelId = {
  method: z.literal("PATCH"),
  path: z.literal("/device-models/{deviceModelId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceModelId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: DeviceModelDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_device_models_deviceModelId = typeof delete_Delete_device_models_deviceModelId;
export const delete_Delete_device_models_deviceModelId = {
  method: z.literal("DELETE"),
  path: z.literal("/device-models/{deviceModelId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceModelId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_categories = typeof get_Get_device_categories;
export const get_Get_device_categories = {
  method: z.literal("GET"),
  path: z.literal("/device-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(DeviceCategorySummaryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_device_instances = typeof get_Get_device_instances;
export const get_Get_device_instances = {
  method: z.literal("GET"),
  path: z.literal("/device-instances"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_device_instances = typeof post_Post_device_instances;
export const post_Post_device_instances = {
  method: z.literal("POST"),
  path: z.literal("/device-instances"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_instances_deviceInstanceId = typeof get_Get_device_instances_deviceInstanceId;
export const get_Get_device_instances_deviceInstanceId = {
  method: z.literal("GET"),
  path: z.literal("/device-instances/{deviceInstanceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_device_instances_deviceInstanceId = typeof patch_Patch_device_instances_deviceInstanceId;
export const patch_Patch_device_instances_deviceInstanceId = {
  method: z.literal("PATCH"),
  path: z.literal("/device-instances/{deviceInstanceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_device_instances_deviceInstanceId = typeof delete_Delete_device_instances_deviceInstanceId;
export const delete_Delete_device_instances_deviceInstanceId = {
  method: z.literal("DELETE"),
  path: z.literal("/device-instances/{deviceInstanceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_device_instances_deviceInstanceId_move = typeof post_Post_device_instances_deviceInstanceId_move;
export const post_Post_device_instances_deviceInstanceId_move = {
  method: z.literal("POST"),
  path: z.literal("/device-instances/{deviceInstanceId}/move"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_racks_rackId_device_instances = typeof get_Get_racks_rackId_device_instances;
export const get_Get_racks_rackId_device_instances = {
  method: z.literal("GET"),
  path: z.literal("/racks/{rackId}/device-instances"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ rackId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks_rackId_device_instances = typeof get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks_rackId_device_instances;
export const get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks_rackId_device_instances = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/datacenters/{datacenterId}/rooms/{roomId}/racks/{rackId}/device-instances"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), datacenterId: z.string(), roomId: z.string(), rackId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_racks = typeof get_Get_racks;
export const get_Get_racks = {
  method: z.literal("GET"),
  path: z.literal("/racks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_racks = typeof post_Post_racks;
export const post_Post_racks = {
  method: z.literal("POST"),
  path: z.literal("/racks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_racks_rackId = typeof get_Get_racks_rackId;
export const get_Get_racks_rackId = {
  method: z.literal("GET"),
  path: z.literal("/racks/{rackId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rackId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_racks_rackId = typeof patch_Patch_racks_rackId;
export const patch_Patch_racks_rackId = {
  method: z.literal("PATCH"),
  path: z.literal("/racks/{rackId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rackId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_racks_rackId = typeof delete_Delete_racks_rackId;
export const delete_Delete_racks_rackId = {
  method: z.literal("DELETE"),
  path: z.literal("/racks/{rackId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rackId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_racks_rackId_status = typeof patch_Patch_racks_rackId_status;
export const patch_Patch_racks_rackId_status = {
  method: z.literal("PATCH"),
  path: z.literal("/racks/{rackId}/status"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rackId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_racks_rackId_clients = typeof put_Put_racks_rackId_clients;
export const put_Put_racks_rackId_clients = {
  method: z.literal("PUT"),
  path: z.literal("/racks/{rackId}/clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ rackId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks = typeof get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks;
export const get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/datacenters/{datacenterId}/rooms/{roomId}/racks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), datacenterId: z.string(), roomId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.unknown() }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_rooms = typeof get_Get_rooms;
export const get_Get_rooms = {
  method: z.literal("GET"),
  path: z.literal("/rooms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: RoomCatalogDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_rooms = typeof post_Post_rooms;
export const post_Post_rooms = {
  method: z.literal("POST"),
  path: z.literal("/rooms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: RoomDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_rooms_roomId = typeof get_Get_rooms_roomId;
export const get_Get_rooms_roomId = {
  method: z.literal("GET"),
  path: z.literal("/rooms/{roomId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roomId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: RoomDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_rooms_roomId = typeof patch_Patch_rooms_roomId;
export const patch_Patch_rooms_roomId = {
  method: z.literal("PATCH"),
  path: z.literal("/rooms/{roomId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roomId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: RoomDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_rooms_roomId = typeof delete_Delete_rooms_roomId;
export const delete_Delete_rooms_roomId = {
  method: z.literal("DELETE"),
  path: z.literal("/rooms/{roomId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roomId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_rooms_roomId_status = typeof patch_Patch_rooms_roomId_status;
export const patch_Patch_rooms_roomId_status = {
  method: z.literal("PATCH"),
  path: z.literal("/rooms/{roomId}/status"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roomId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: RoomDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_rooms_roomId_clients = typeof put_Put_rooms_roomId_clients;
export const put_Put_rooms_roomId_clients = {
  method: z.literal("PUT"),
  path: z.literal("/rooms/{roomId}/clients"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ roomId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: RoomDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_datacenters_datacenterId_rooms = typeof get_Get_clients_clientId_datacenters_datacenterId_rooms;
export const get_Get_clients_clientId_datacenters_datacenterId_rooms = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/datacenters/{datacenterId}/rooms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), datacenterId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ClientContextRoomsDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_subsystems = typeof get_Get_subsystems;
export const get_Get_subsystems = {
  method: z.literal("GET"),
  path: z.literal("/subsystems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_subsystems = typeof post_Post_subsystems;
export const post_Post_subsystems = {
  method: z.literal("POST"),
  path: z.literal("/subsystems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_subsystems_subsystemId = typeof patch_Patch_subsystems_subsystemId;
export const patch_Patch_subsystems_subsystemId = {
  method: z.literal("PATCH"),
  path: z.literal("/subsystems/{subsystemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ subsystemId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_subsystems_subsystemId = typeof delete_Delete_subsystems_subsystemId;
export const delete_Delete_subsystems_subsystemId = {
  method: z.literal("DELETE"),
  path: z.literal("/subsystems/{subsystemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ subsystemId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_database_systems = typeof get_Get_database_systems;
export const get_Get_database_systems = {
  method: z.literal("GET"),
  path: z.literal("/database-systems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_database_systems = typeof post_Post_database_systems;
export const post_Post_database_systems = {
  method: z.literal("POST"),
  path: z.literal("/database-systems"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_database_systems_databaseSystemId = typeof patch_Patch_database_systems_databaseSystemId;
export const patch_Patch_database_systems_databaseSystemId = {
  method: z.literal("PATCH"),
  path: z.literal("/database-systems/{databaseSystemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ databaseSystemId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_database_systems_databaseSystemId = typeof delete_Delete_database_systems_databaseSystemId;
export const delete_Delete_database_systems_databaseSystemId = {
  method: z.literal("DELETE"),
  path: z.literal("/database-systems/{databaseSystemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ databaseSystemId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_sap_products = typeof get_Get_sap_products;
export const get_Get_sap_products = {
  method: z.literal("GET"),
  path: z.literal("/sap-products"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_sap_products = typeof post_Post_sap_products;
export const post_Post_sap_products = {
  method: z.literal("POST"),
  path: z.literal("/sap-products"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_sap_products_sapProductId = typeof patch_Patch_sap_products_sapProductId;
export const patch_Patch_sap_products_sapProductId = {
  method: z.literal("PATCH"),
  path: z.literal("/sap-products/{sapProductId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ sapProductId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_sap_products_sapProductId = typeof delete_Delete_sap_products_sapProductId;
export const delete_Delete_sap_products_sapProductId = {
  method: z.literal("DELETE"),
  path: z.literal("/sap-products/{sapProductId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ sapProductId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_vm_clusters = typeof get_Get_clients_clientId_vm_clusters;
export const get_Get_clients_clientId_vm_clusters = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/vm-clusters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_vm_clusters = typeof post_Post_clients_clientId_vm_clusters;
export const post_Post_clients_clientId_vm_clusters = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/vm-clusters"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_vm_clusters_clusterId = typeof get_Get_clients_clientId_vm_clusters_clusterId;
export const get_Get_clients_clientId_vm_clusters_clusterId = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_vm_clusters_clusterId = typeof patch_Patch_clients_clientId_vm_clusters_clusterId;
export const patch_Patch_clients_clientId_vm_clusters_clusterId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_vm_clusters_clusterId = typeof delete_Delete_clients_clientId_vm_clusters_clusterId;
export const delete_Delete_clients_clientId_vm_clusters_clusterId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_vm_clusters_clusterId_context = typeof get_Get_clients_clientId_vm_clusters_clusterId_context;
export const get_Get_clients_clientId_vm_clusters_clusterId_context = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}/context"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), clusterId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_vm_clusters_clusterId_available_vms = typeof get_Get_clients_clientId_vm_clusters_clusterId_available_vms;
export const get_Get_clients_clientId_vm_clusters_clusterId_available_vms = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}/available-vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string(), clusterId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_clients_clientId_vm_clusters_clusterId_members = typeof post_Post_clients_clientId_vm_clusters_clusterId_members;
export const post_Post_clients_clientId_vm_clusters_clusterId_members = {
  method: z.literal("POST"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}/members"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_clients_clientId_vm_clusters_clusterId_members_virtualMachineId = typeof patch_Patch_clients_clientId_vm_clusters_clusterId_members_virtualMachineId;
export const patch_Patch_clients_clientId_vm_clusters_clusterId_members_virtualMachineId = {
  method: z.literal("PATCH"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}/members/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string(), virtualMachineId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_clients_clientId_vm_clusters_clusterId_members_virtualMachineId = typeof delete_Delete_clients_clientId_vm_clusters_clusterId_members_virtualMachineId;
export const delete_Delete_clients_clientId_vm_clusters_clusterId_members_virtualMachineId = {
  method: z.literal("DELETE"),
  path: z.literal("/clients/{clientId}/vm-clusters/{clusterId}/members/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string(), clusterId: z.string(), virtualMachineId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_instances_deviceInstanceId_virtual_machines = typeof get_Get_device_instances_deviceInstanceId_virtual_machines;
export const get_Get_device_instances_deviceInstanceId_virtual_machines = {
  method: z.literal("GET"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ deviceInstanceId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: VirtualMachineCatalogDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_device_instances_deviceInstanceId_virtual_machines = typeof post_Post_device_instances_deviceInstanceId_virtual_machines;
export const post_Post_device_instances_deviceInstanceId_virtual_machines = {
  method: z.literal("POST"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = typeof get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId;
export const get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = {
  method: z.literal("GET"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = typeof patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId;
export const patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = {
  method: z.literal("PATCH"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = typeof delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId;
export const delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId = {
  method: z.literal("DELETE"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ softDeleted: z.object({ valueOf: z.unknown() }) }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_virtual_machines = typeof get_Get_virtual_machines;
export const get_Get_virtual_machines = {
  method: z.literal("GET"),
  path: z.literal("/virtual-machines"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(VirtualMachineSummaryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_virtual_machines = typeof post_Post_virtual_machines;
export const post_Post_virtual_machines = {
  method: z.literal("POST"),
  path: z.literal("/virtual-machines"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_virtual_machines_vmId = typeof get_Get_virtual_machines_vmId;
export const get_Get_virtual_machines_vmId = {
  method: z.literal("GET"),
  path: z.literal("/virtual-machines/{vmId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_virtual_machines_vmId = typeof patch_Patch_virtual_machines_vmId;
export const patch_Patch_virtual_machines_vmId = {
  method: z.literal("PATCH"),
  path: z.literal("/virtual-machines/{vmId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: VirtualMachineDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_virtual_machines_vmId = typeof delete_Delete_virtual_machines_vmId;
export const delete_Delete_virtual_machines_vmId = {
  method: z.literal("DELETE"),
  path: z.literal("/virtual-machines/{vmId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ softDeleted: z.object({ valueOf: z.unknown() }) }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations = typeof get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations;
export const get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations = {
  method: z.literal("GET"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: SoftwareAssociationListDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations = typeof post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations;
export const post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations = {
  method: z.literal("POST"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: SoftwareAssociationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId = typeof patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId;
export const patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId = {
  method: z.literal("PATCH"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string(), associationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: SoftwareAssociationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId = typeof delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId;
export const delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId = {
  method: z.literal("DELETE"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string(), associationId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId_version_change = typeof post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId_version_change;
export const post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId_version_change = {
  method: z.literal("POST"),
  path: z.literal("/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}/version-change"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ deviceInstanceId: z.string(), virtualMachineId: z.string(), associationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: SoftwareAssociationDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_vm_dr_relationships = typeof get_Get_vm_dr_relationships;
export const get_Get_vm_dr_relationships = {
  method: z.literal("GET"),
  path: z.literal("/vm-dr-relationships"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_vm_dr_relationships = typeof post_Post_vm_dr_relationships;
export const post_Post_vm_dr_relationships = {
  method: z.literal("POST"),
  path: z.literal("/vm-dr-relationships"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_vm_dr_relationships_relationshipId = typeof get_Get_vm_dr_relationships_relationshipId;
export const get_Get_vm_dr_relationships_relationshipId = {
  method: z.literal("GET"),
  path: z.literal("/vm-dr-relationships/{relationshipId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ relationshipId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_vm_dr_relationships_relationshipId = typeof patch_Patch_vm_dr_relationships_relationshipId;
export const patch_Patch_vm_dr_relationships_relationshipId = {
  method: z.literal("PATCH"),
  path: z.literal("/vm-dr-relationships/{relationshipId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ relationshipId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_vm_dr_relationships_relationshipId = typeof delete_Delete_vm_dr_relationships_relationshipId;
export const delete_Delete_vm_dr_relationships_relationshipId = {
  method: z.literal("DELETE"),
  path: z.literal("/vm-dr-relationships/{relationshipId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ relationshipId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_virtual_machines_vmId_documents = typeof get_Get_virtual_machines_vmId_documents;
export const get_Get_virtual_machines_vmId_documents = {
  method: z.literal("GET"),
  path: z.literal("/virtual-machines/{vmId}/documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ vmId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_virtual_machines_vmId_documents = typeof post_Post_virtual_machines_vmId_documents;
export const post_Post_virtual_machines_vmId_documents = {
  method: z.literal("POST"),
  path: z.literal("/virtual-machines/{vmId}/documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_virtual_machines_vmId_documents_documentId = typeof patch_Patch_virtual_machines_vmId_documents_documentId;
export const patch_Patch_virtual_machines_vmId_documents_documentId = {
  method: z.literal("PATCH"),
  path: z.literal("/virtual-machines/{vmId}/documents/{documentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string(), documentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_virtual_machines_vmId_documents_documentId = typeof delete_Delete_virtual_machines_vmId_documents_documentId;
export const delete_Delete_virtual_machines_vmId_documents_documentId = {
  method: z.literal("DELETE"),
  path: z.literal("/virtual-machines/{vmId}/documents/{documentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ vmId: z.string(), documentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_clients_clientId_work_schedule = typeof get_Get_clients_clientId_work_schedule;
export const get_Get_clients_clientId_work_schedule = {
  method: z.literal("GET"),
  path: z.literal("/clients/{clientId}/work-schedule"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: WorkScheduleDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_clients_clientId_work_schedule = typeof put_Put_clients_clientId_work_schedule;
export const put_Put_clients_clientId_work_schedule = {
  method: z.literal("PUT"),
  path: z.literal("/clients/{clientId}/work-schedule"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: WorkScheduleDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_country_holidays = typeof get_Get_country_holidays;
export const get_Get_country_holidays = {
  method: z.literal("GET"),
  path: z.literal("/country-holidays"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(CountryHolidayDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_country_holidays = typeof post_Post_country_holidays;
export const post_Post_country_holidays = {
  method: z.literal("POST"),
  path: z.literal("/country-holidays"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: CountryHolidayDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_country_holidays_holidayId = typeof delete_Delete_country_holidays_holidayId;
export const delete_Delete_country_holidays_holidayId = {
  method: z.literal("DELETE"),
  path: z.literal("/country-holidays/{holidayId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ holidayId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_holidays = typeof get_Get_availability_holidays;
export const get_Get_availability_holidays = {
  method: z.literal("GET"),
  path: z.literal("/availability-holidays"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityHolidayDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_availability_holidays = typeof post_Post_availability_holidays;
export const post_Post_availability_holidays = {
  method: z.literal("POST"),
  path: z.literal("/availability-holidays"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: AvailabilityHolidayDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_availability_holidays_holidayId = typeof delete_Delete_availability_holidays_holidayId;
export const delete_Delete_availability_holidays_holidayId = {
  method: z.literal("DELETE"),
  path: z.literal("/availability-holidays/{holidayId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ holidayId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_configurations = typeof get_Get_availability_configurations;
export const get_Get_availability_configurations = {
  method: z.literal("GET"),
  path: z.literal("/availability-configurations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityConfigurationDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_availability_configurations = typeof post_Post_availability_configurations;
export const post_Post_availability_configurations = {
  method: z.literal("POST"),
  path: z.literal("/availability-configurations"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: AvailabilityConfigurationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_configurations_configurationId = typeof get_Get_availability_configurations_configurationId;
export const get_Get_availability_configurations_configurationId = {
  method: z.literal("GET"),
  path: z.literal("/availability-configurations/{configurationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityConfigurationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_availability_configurations_configurationId = typeof put_Put_availability_configurations_configurationId;
export const put_Put_availability_configurations_configurationId = {
  method: z.literal("PUT"),
  path: z.literal("/availability-configurations/{configurationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityConfigurationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_availability_configurations_configurationId = typeof delete_Delete_availability_configurations_configurationId;
export const delete_Delete_availability_configurations_configurationId = {
  method: z.literal("DELETE"),
  path: z.literal("/availability-configurations/{configurationId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_configurations_configurationId_activate = typeof post_Post_availability_configurations_configurationId_activate;
export const post_Post_availability_configurations_configurationId_activate = {
  method: z.literal("POST"),
  path: z.literal("/availability-configurations/{configurationId}/activate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityConfigurationDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_configurations_configurationId_deactivate = typeof post_Post_availability_configurations_configurationId_deactivate;
export const post_Post_availability_configurations_configurationId_deactivate = {
  method: z.literal("POST"),
  path: z.literal("/availability-configurations/{configurationId}/deactivate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityConfigurationDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_configurations_configurationId_tiers = typeof get_Get_availability_configurations_configurationId_tiers;
export const get_Get_availability_configurations_configurationId_tiers = {
  method: z.literal("GET"),
  path: z.literal("/availability-configurations/{configurationId}/tiers"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ configurationId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityTierDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_configurations_configurationId_tiers = typeof post_Post_availability_configurations_configurationId_tiers;
export const post_Post_availability_configurations_configurationId_tiers = {
  method: z.literal("POST"),
  path: z.literal("/availability-configurations/{configurationId}/tiers"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: AvailabilityTierDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_configurations_configurationId_auto_classify = typeof post_Post_availability_configurations_configurationId_auto_classify;
export const post_Post_availability_configurations_configurationId_auto_classify = {
  method: z.literal("POST"),
  path: z.literal("/availability-configurations/{configurationId}/auto-classify"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ configurationId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ outcomes: z.array(AutoClassifyOutcomeDto) }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_availability_tiers_tierId = typeof put_Put_availability_tiers_tierId;
export const put_Put_availability_tiers_tierId = {
  method: z.literal("PUT"),
  path: z.literal("/availability-tiers/{tierId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ tierId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityTierDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_availability_tiers_tierId = typeof delete_Delete_availability_tiers_tierId;
export const delete_Delete_availability_tiers_tierId = {
  method: z.literal("DELETE"),
  path: z.literal("/availability-tiers/{tierId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ tierId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_tiers_tierId_vms = typeof get_Get_availability_tiers_tierId_vms;
export const get_Get_availability_tiers_tierId_vms = {
  method: z.literal("GET"),
  path: z.literal("/availability-tiers/{tierId}/vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ tierId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityVmAssignmentDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_tiers_tierId_vms = typeof post_Post_availability_tiers_tierId_vms;
export const post_Post_availability_tiers_tierId_vms = {
  method: z.literal("POST"),
  path: z.literal("/availability-tiers/{tierId}/vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ tierId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ outcomes: z.array(VmAssignOutcomeDto) }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_availability_tiers_tierId_vms_vmId = typeof delete_Delete_availability_tiers_tierId_vms_vmId;
export const delete_Delete_availability_tiers_tierId_vms_vmId = {
  method: z.literal("DELETE"),
  path: z.literal("/availability-tiers/{tierId}/vms/{vmId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ tierId: z.string(), vmId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_interruptions = typeof get_Get_availability_interruptions;
export const get_Get_availability_interruptions = {
  method: z.literal("GET"),
  path: z.literal("/availability-interruptions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityInterruptionDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_availability_interruptions = typeof post_Post_availability_interruptions;
export const post_Post_availability_interruptions = {
  method: z.literal("POST"),
  path: z.literal("/availability-interruptions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: AvailabilityInterruptionDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_availability_interruptions_interruptionId = typeof put_Put_availability_interruptions_interruptionId;
export const put_Put_availability_interruptions_interruptionId = {
  method: z.literal("PUT"),
  path: z.literal("/availability-interruptions/{interruptionId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ interruptionId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityInterruptionDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_availability_interruptions_interruptionId = typeof delete_Delete_availability_interruptions_interruptionId;
export const delete_Delete_availability_interruptions_interruptionId = {
  method: z.literal("DELETE"),
  path: z.literal("/availability-interruptions/{interruptionId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ interruptionId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_availability_reports_calculate = typeof post_Post_availability_reports_calculate;
export const post_Post_availability_reports_calculate = {
  method: z.literal("POST"),
  path: z.literal("/availability-reports/calculate"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityReportDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_reports = typeof get_Get_availability_reports;
export const get_Get_availability_reports = {
  method: z.literal("GET"),
  path: z.literal("/availability-reports"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(AvailabilityReportDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_availability_reports_reportId = typeof get_Get_availability_reports_reportId;
export const get_Get_availability_reports_reportId = {
  method: z.literal("GET"),
  path: z.literal("/availability-reports/{reportId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ reportId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: AvailabilityReportDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_availability_reports_reportId_pdf = typeof get_Get_availability_reports_reportId_pdf;
export const get_Get_availability_reports_reportId_pdf = {
  method: z.literal("GET"),
  path: z.literal("/availability-reports/{reportId}/pdf"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ reportId: z.string() }) },
  responses: { 200: z.unknown(), 302: ApiResponse.and(z.object({ data: ReportPdfDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_scheduled_activities_activityId_execution = typeof get_Get_scheduled_activities_activityId_execution;
export const get_Get_scheduled_activities_activityId_execution = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{activityId}/execution"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ activityId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ExecutionContextDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution = typeof post_Post_scheduled_activities_activityId_execution;
export const post_Post_scheduled_activities_activityId_execution = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), idempotent: z.boolean(), rollbackActivated: z.boolean(), status: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers = typeof post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers;
export const post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/timers"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), sequence: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers_timerId_stop = typeof post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers_timerId_stop;
export const post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers_timerId_stop = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/timers/{timerId}/stop"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string(), timerId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ elapsedSeconds: z.number().nullable(), id: z.string() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_scheduled_activities_activityId_execution_tasks_taskLogId_progress = typeof patch_Patch_scheduled_activities_activityId_execution_tasks_taskLogId_progress;
export const patch_Patch_scheduled_activities_activityId_execution_tasks_taskLogId_progress = {
  method: z.literal("PATCH"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/progress"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), progressPercent: z.number().nullable(), status: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_evidence = typeof post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_evidence;
export const post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_evidence = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/evidence"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), taskLogId: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_activityId_execution_evidence_evidenceId = typeof put_Put_scheduled_activities_activityId_execution_evidence_evidenceId;
export const put_Put_scheduled_activities_activityId_execution_evidence_evidenceId = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{activityId}/execution/evidence/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), evidenceId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), taskLogId: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_activityId_execution_evidence_evidenceId = typeof delete_Delete_scheduled_activities_activityId_execution_evidence_evidenceId;
export const delete_Delete_scheduled_activities_activityId_execution_evidence_evidenceId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{activityId}/execution/evidence/{evidenceId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), evidenceId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_findings = typeof post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_findings;
export const post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_findings = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/findings"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskLogId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), taskLogId: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_activityId_execution_findings_findingId = typeof put_Put_scheduled_activities_activityId_execution_findings_findingId;
export const put_Put_scheduled_activities_activityId_execution_findings_findingId = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{activityId}/execution/findings/{findingId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), findingId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.string(), taskLogId: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_activityId_execution_findings_findingId = typeof delete_Delete_scheduled_activities_activityId_execution_findings_findingId;
export const delete_Delete_scheduled_activities_activityId_execution_findings_findingId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{activityId}/execution/findings/{findingId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), findingId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_rollback = typeof post_Post_scheduled_activities_activityId_execution_rollback;
export const post_Post_scheduled_activities_activityId_execution_rollback = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/rollback"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), rollbackActivated: z.boolean(), version: z.number() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_complete = typeof post_Post_scheduled_activities_activityId_execution_complete;
export const post_Post_scheduled_activities_activityId_execution_complete = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/complete"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.literal("completed"), version: z.number() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_execution_fail = typeof post_Post_scheduled_activities_activityId_execution_fail;
export const post_Post_scheduled_activities_activityId_execution_fail = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/execution/fail"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.literal("failed"), version: z.number() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_activityId_execution_report_url = typeof put_Put_scheduled_activities_activityId_execution_report_url;
export const put_Put_scheduled_activities_activityId_execution_report_url = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{activityId}/execution/report-url"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), reportUrl: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_overtime_calculate_activityId = typeof post_Post_overtime_calculate_activityId;
export const post_Post_overtime_calculate_activityId = {
  method: z.literal("POST"),
  path: z.literal("/overtime/calculate/{activityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_overtime_by_activity_activityId = typeof get_Get_overtime_by_activity_activityId;
export const get_Get_overtime_by_activity_activityId = {
  method: z.literal("GET"),
  path: z.literal("/overtime/by-activity/{activityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_overtime_by_user_userId = typeof get_Get_overtime_by_user_userId;
export const get_Get_overtime_by_user_userId = {
  method: z.literal("GET"),
  path: z.literal("/overtime/by-user/{userId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_overtime_summary = typeof get_Get_overtime_summary;
export const get_Get_overtime_summary = {
  method: z.literal("GET"),
  path: z.literal("/overtime/summary"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_overtime_export_activityId = typeof get_Get_overtime_export_activityId;
export const get_Get_overtime_export_activityId = {
  method: z.literal("GET"),
  path: z.literal("/overtime/export/{activityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }) },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_overtime_by_user_with_standby_userId = typeof get_Get_overtime_by_user_with_standby_userId;
export const get_Get_overtime_by_user_with_standby_userId = {
  method: z.literal("GET"),
  path: z.literal("/overtime/by-user-with-standby/{userId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ userId: z.string() }) },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_overtime_standby_report = typeof get_Get_overtime_standby_report;
export const get_Get_overtime_standby_report = {
  method: z.literal("GET"),
  path: z.literal("/overtime/standby-report"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_id_rca = typeof get_Get_scheduled_activities_id_rca;
export const get_Get_scheduled_activities_id_rca = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{id}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ScheduledActivityRcaContextDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_rca = typeof post_Post_scheduled_activities_id_rca;
export const post_Post_scheduled_activities_id_rca = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_id_rca = typeof put_Put_scheduled_activities_id_rca;
export const put_Put_scheduled_activities_id_rca = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{id}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_id_rca = typeof delete_Delete_scheduled_activities_id_rca;
export const delete_Delete_scheduled_activities_id_rca = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{id}/rca"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_operational_plans = typeof get_Get_operational_plans;
export const get_Get_operational_plans = {
  method: z.literal("GET"),
  path: z.literal("/operational-plans"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(OperationalPlanSummaryDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_operational_plans = typeof post_Post_operational_plans;
export const post_Post_operational_plans = {
  method: z.literal("POST"),
  path: z.literal("/operational-plans"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: OperationalPlanDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_operational_plans_id = typeof get_Get_operational_plans_id;
export const get_Get_operational_plans_id = {
  method: z.literal("GET"),
  path: z.literal("/operational-plans/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: OperationalPlanDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_operational_plans_id = typeof put_Put_operational_plans_id;
export const put_Put_operational_plans_id = {
  method: z.literal("PUT"),
  path: z.literal("/operational-plans/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: OperationalPlanDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_operational_plans_id = typeof delete_Delete_operational_plans_id;
export const delete_Delete_operational_plans_id = {
  method: z.literal("DELETE"),
  path: z.literal("/operational-plans/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities = typeof get_Get_scheduled_activities;
export const get_Get_scheduled_activities = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(ScheduledActivitySummaryDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities = typeof post_Post_scheduled_activities;
export const post_Post_scheduled_activities = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ changeRequestNumber: z.string().nullable(), description: z.string().nullable(), endTimestamp: z.string(), id: z.string(), masterActivityRef: z.string(), planId: z.string(), planOrigin: z.enum(["manual", "automatic"]), planningRevision: z.number(), platformCategoryRef: z.string(), startTimestamp: z.string(), status: z.literal("draft") }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_report = typeof get_Get_scheduled_activities_report;
export const get_Get_scheduled_activities_report = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/report"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: ScheduledActivityReportDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_audit = typeof get_Get_scheduled_activities_audit;
export const get_Get_scheduled_activities_audit = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/audit"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: ScheduledActivityAuditDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_client_approvers_clientId = typeof get_Get_scheduled_activities_client_approvers_clientId;
export const get_Get_scheduled_activities_client_approvers_clientId = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/client-approvers/{clientId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ clientId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ClientApproverDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_search_for_baseline = typeof get_Get_scheduled_activities_search_for_baseline;
export const get_Get_scheduled_activities_search_for_baseline = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/search-for-baseline"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(BaselineActivitySearchItemDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_activityId_export_work_plan = typeof get_Get_scheduled_activities_activityId_export_work_plan;
export const get_Get_scheduled_activities_activityId_export_work_plan = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{activityId}/export-work-plan"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ activityId: z.string() }) },
  responses: { 200: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

export type get_Get_scheduled_activities_id = typeof get_Get_scheduled_activities_id;
export const get_Get_scheduled_activities_id = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ScheduledActivityDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_id = typeof put_Put_scheduled_activities_id;
export const put_Put_scheduled_activities_id = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ clientId: z.string(), id: z.string(), planId: z.string(), planningRevision: z.number(), status: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_id = typeof delete_Delete_scheduled_activities_id;
export const delete_Delete_scheduled_activities_id = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{id}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_scope_items = typeof post_Post_scheduled_activities_activityId_scope_items;
export const post_Post_scheduled_activities_activityId_scope_items = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/scope-items"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), itemId: z.string(), itemType: z.enum(["virtual_machine", "device_instance", "subsystem", "database_instance", "sap_instance"]), parentVirtualMachineId: z.string().nullable(), planningRevision: z.number(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_activityId_scope_items_scopeItemId = typeof delete_Delete_scheduled_activities_activityId_scope_items_scopeItemId;
export const delete_Delete_scheduled_activities_activityId_scope_items_scopeItemId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{activityId}/scope-items/{scopeItemId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), scopeItemId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_transitions = typeof post_Post_scheduled_activities_id_transitions;
export const post_Post_scheduled_activities_id_transitions = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/transitions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.literal("pending_approval"), version: z.number() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_approval_decisions = typeof post_Post_scheduled_activities_id_approval_decisions;
export const post_Post_scheduled_activities_id_approval_decisions = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/approval-decisions"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.enum(["draft", "pending_approval", "approved"]), version: z.number() }) }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_cancel = typeof post_Post_scheduled_activities_id_cancel;
export const post_Post_scheduled_activities_id_cancel = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/cancel"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), status: z.literal("cancelled"), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_id_execution = typeof post_Post_scheduled_activities_id_execution;
export const post_Post_scheduled_activities_id_execution = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{id}/execution"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: z.object({ id: z.string(), rollbackActivated: z.boolean(), status: z.literal("in_progress") }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_scheduled_activities_id_execution = typeof patch_Patch_scheduled_activities_id_execution;
export const patch_Patch_scheduled_activities_id_execution = {
  method: z.literal("PATCH"),
  path: z.literal("/scheduled-activities/{id}/execution"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ id: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ id: z.string(), rollbackActivated: z.boolean(), status: z.enum(["in_progress", "completed", "failed"]) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_activityId_tasks = typeof get_Get_scheduled_activities_activityId_tasks;
export const get_Get_scheduled_activities_activityId_tasks = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{activityId}/tasks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ activityId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityTaskPlanDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_tasks = typeof post_Post_scheduled_activities_activityId_tasks;
export const post_Post_scheduled_activities_activityId_tasks = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/tasks"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ActivityTaskDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_scheduled_activities_activityId_tasks_taskId = typeof put_Put_scheduled_activities_activityId_tasks_taskId;
export const put_Put_scheduled_activities_activityId_tasks_taskId = {
  method: z.literal("PUT"),
  path: z.literal("/scheduled-activities/{activityId}/tasks/{taskId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityTaskDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_scheduled_activities_activityId_tasks_taskId = typeof delete_Delete_scheduled_activities_activityId_tasks_taskId;
export const delete_Delete_scheduled_activities_activityId_tasks_taskId = {
  method: z.literal("DELETE"),
  path: z.literal("/scheduled-activities/{activityId}/tasks/{taskId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string(), taskId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_tasks_reorder = typeof post_Post_scheduled_activities_activityId_tasks_reorder;
export const post_Post_scheduled_activities_activityId_tasks_reorder = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/tasks/reorder"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityTaskPlanDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_tasks_cascade_times = typeof post_Post_scheduled_activities_activityId_tasks_cascade_times;
export const post_Post_scheduled_activities_activityId_tasks_cascade_times = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/tasks/cascade-times"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityTaskPlanDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_change_templates = typeof get_Get_change_templates;
export const get_Get_change_templates = {
  method: z.literal("GET"),
  path: z.literal("/change-templates"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ items: z.array(ChangeTemplateSummaryDto) }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_change_templates = typeof post_Post_change_templates;
export const post_Post_change_templates = {
  method: z.literal("POST"),
  path: z.literal("/change-templates"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ChangeTemplateDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_change_templates_templateId = typeof get_Get_change_templates_templateId;
export const get_Get_change_templates_templateId = {
  method: z.literal("GET"),
  path: z.literal("/change-templates/{templateId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ChangeTemplateDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type put_Put_change_templates_templateId = typeof put_Put_change_templates_templateId;
export const put_Put_change_templates_templateId = {
  method: z.literal("PUT"),
  path: z.literal("/change-templates/{templateId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ChangeTemplateDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_change_templates_templateId = typeof delete_Delete_change_templates_templateId;
export const delete_Delete_change_templates_templateId = {
  method: z.literal("DELETE"),
  path: z.literal("/change-templates/{templateId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_change_templates_templateId_vms = typeof post_Post_change_templates_templateId_vms;
export const post_Post_change_templates_templateId_vms = {
  method: z.literal("POST"),
  path: z.literal("/change-templates/{templateId}/vms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_change_templates_templateId_vms_virtualMachineId = typeof delete_Delete_change_templates_templateId_vms_virtualMachineId;
export const delete_Delete_change_templates_templateId_vms_virtualMachineId = {
  method: z.literal("DELETE"),
  path: z.literal("/change-templates/{templateId}/vms/{virtualMachineId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string(), virtualMachineId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_change_templates_from_activity_activityId = typeof post_Post_change_templates_from_activity_activityId;
export const post_Post_change_templates_from_activity_activityId = {
  method: z.literal("POST"),
  path: z.literal("/change-templates/from-activity/{activityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ChangeTemplateDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_change_templates_templateId_apply_activityId = typeof post_Post_change_templates_templateId_apply_activityId;
export const post_Post_change_templates_templateId_apply_activityId = {
  method: z.literal("POST"),
  path: z.literal("/change-templates/{templateId}/apply/{activityId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ templateId: z.string(), activityId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ActivityTaskPlanDto }).partial()), 201: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_change_batches = typeof get_Get_change_batches;
export const get_Get_change_batches = {
  method: z.literal("GET"),
  path: z.literal("/change-batches"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ChangeBatchSummaryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_change_batches = typeof post_Post_change_batches;
export const post_Post_change_batches = {
  method: z.literal("POST"),
  path: z.literal("/change-batches"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ChangeBatchDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_change_batches_batchId = typeof get_Get_change_batches_batchId;
export const get_Get_change_batches_batchId = {
  method: z.literal("GET"),
  path: z.literal("/change-batches/{batchId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ batchId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ChangeBatchDetailDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_change_batches_batchId = typeof patch_Patch_change_batches_batchId;
export const patch_Patch_change_batches_batchId = {
  method: z.literal("PATCH"),
  path: z.literal("/change-batches/{batchId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ batchId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ChangeBatchSummaryDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_change_batches_batchId_cancel = typeof post_Post_change_batches_batchId_cancel;
export const post_Post_change_batches_batchId_cancel = {
  method: z.literal("POST"),
  path: z.literal("/change-batches/{batchId}/cancel"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ batchId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ childActivityCount: z.number(), childActivityIds: z.array(z.string()), clientId: z.string(), copyVms: z.boolean(), createdAt: z.string(), createdBy: z.string().nullable(), dates: z.array(z.string()), deriveSupport: z.boolean(), draftActivitiesCancelled: z.number(), id: z.string(), masterActivityRef: z.string().nullable(), name: z.string(), status: ChangeBatchStatusDto, templateId: z.string().nullable(), updatedAt: z.string(), version: z.number() }) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_scheduled_activities_activityId_validation_runs = typeof post_Post_scheduled_activities_activityId_validation_runs;
export const post_Post_scheduled_activities_activityId_validation_runs = {
  method: z.literal("POST"),
  path: z.literal("/scheduled-activities/{activityId}/validation-runs"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ activityId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ValidationReportDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_activityId_validation_reports = typeof get_Get_scheduled_activities_activityId_validation_reports;
export const get_Get_scheduled_activities_activityId_validation_reports = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{activityId}/validation-reports"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ activityId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ValidationReportSummaryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_scheduled_activities_activityId_validation_reports_latest = typeof get_Get_scheduled_activities_activityId_validation_reports_latest;
export const get_Get_scheduled_activities_activityId_validation_reports_latest = {
  method: z.literal("GET"),
  path: z.literal("/scheduled-activities/{activityId}/validation-reports/latest"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ activityId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: LatestValidationReportDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_SearchInfrastructure = typeof get_SearchInfrastructure;
export const get_SearchInfrastructure = {
  method: z.literal("GET"),
  path: z.literal("/search"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ q: z.string().min(1), limitPerGroup: z.coerce.number().int().min(1).optional() }) },
  responses: { 200: ApiResponse.and(z.object({ data: UniversalInfrastructureSearchResponseDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type get_GetApplicationTheme = typeof get_GetApplicationTheme;
export const get_GetApplicationTheme = {
  method: z.literal("GET"),
  path: z.literal("/app-settings/theme"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: ApplicationTheme },
};

export type put_PutApplicationTheme = typeof put_PutApplicationTheme;
export const put_PutApplicationTheme = {
  method: z.literal("PUT"),
  path: z.literal("/app-settings/theme"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: UpdateApplicationThemeRequest },
  responses: { 200: ApiResponse, 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_countries = typeof get_Get_countries;
export const get_Get_countries = {
  method: z.literal("GET"),
  path: z.literal("/countries"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ReferenceCatalogItemDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_countries = typeof post_Post_countries;
export const post_Post_countries = {
  method: z.literal("POST"),
  path: z.literal("/countries"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_countries_countryId = typeof get_Get_countries_countryId;
export const get_Get_countries_countryId = {
  method: z.literal("GET"),
  path: z.literal("/countries/{countryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ countryId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_countries_countryId = typeof patch_Patch_countries_countryId;
export const patch_Patch_countries_countryId = {
  method: z.literal("PATCH"),
  path: z.literal("/countries/{countryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ countryId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_countries_countryId = typeof delete_Delete_countries_countryId;
export const delete_Delete_countries_countryId = {
  method: z.literal("DELETE"),
  path: z.literal("/countries/{countryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ countryId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_languages = typeof get_Get_languages;
export const get_Get_languages = {
  method: z.literal("GET"),
  path: z.literal("/languages"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(ReferenceCatalogItemDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_languages = typeof post_Post_languages;
export const post_Post_languages = {
  method: z.literal("POST"),
  path: z.literal("/languages"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_languages_languageId = typeof get_Get_languages_languageId;
export const get_Get_languages_languageId = {
  method: z.literal("GET"),
  path: z.literal("/languages/{languageId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ languageId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_languages_languageId = typeof patch_Patch_languages_languageId;
export const patch_Patch_languages_languageId = {
  method: z.literal("PATCH"),
  path: z.literal("/languages/{languageId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ languageId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: ReferenceCatalogItemDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_languages_languageId = typeof delete_Delete_languages_languageId;
export const delete_Delete_languages_languageId = {
  method: z.literal("DELETE"),
  path: z.literal("/languages/{languageId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ languageId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.object({ message: z.string() }) }).partial()), 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_platforms = typeof get_Get_platforms;
export const get_Get_platforms = {
  method: z.literal("GET"),
  path: z.literal("/platforms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(PlatformDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_platforms = typeof post_Post_platforms;
export const post_Post_platforms = {
  method: z.literal("POST"),
  path: z.literal("/platforms"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: PlatformDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_platforms_platformId = typeof get_Get_platforms_platformId;
export const get_Get_platforms_platformId = {
  method: z.literal("GET"),
  path: z.literal("/platforms/{platformId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ platformId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: PlatformDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_platforms_platformId = typeof patch_Patch_platforms_platformId;
export const patch_Patch_platforms_platformId = {
  method: z.literal("PATCH"),
  path: z.literal("/platforms/{platformId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ platformId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: PlatformDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_platforms_platformId = typeof delete_Delete_platforms_platformId;
export const delete_Delete_platforms_platformId = {
  method: z.literal("DELETE"),
  path: z.literal("/platforms/{platformId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ platformId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_platforms_platformId_document_categories = typeof get_Get_platforms_platformId_document_categories;
export const get_Get_platforms_platformId_document_categories = {
  method: z.literal("GET"),
  path: z.literal("/platforms/{platformId}/document-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional(), path: z.object({ platformId: z.string() }) },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(PlatformDocumentCategoryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 500: ErrorEnvelope },
};

export type post_Post_platforms_platformId_document_categories = typeof post_Post_platforms_platformId_document_categories;
export const post_Post_platforms_platformId_document_categories = {
  method: z.literal("POST"),
  path: z.literal("/platforms/{platformId}/document-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ platformId: z.string() }), body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: PlatformDocumentCategoryDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_platform_categories = typeof get_Get_platform_categories;
export const get_Get_platform_categories = {
  method: z.literal("GET"),
  path: z.literal("/platform-categories"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(PlatformCategoryDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type patch_Patch_platform_document_categories_categoryId = typeof patch_Patch_platform_document_categories_categoryId;
export const patch_Patch_platform_document_categories_categoryId = {
  method: z.literal("PATCH"),
  path: z.literal("/platform-document-categories/{categoryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ categoryId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: PlatformDocumentCategoryDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_platform_document_categories_categoryId = typeof delete_Delete_platform_document_categories_categoryId;
export const delete_Delete_platform_document_categories_categoryId = {
  method: z.literal("DELETE"),
  path: z.literal("/platform-document-categories/{categoryId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ categoryId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_Get_platform_documents = typeof get_Get_platform_documents;
export const get_Get_platform_documents = {
  method: z.literal("GET"),
  path: z.literal("/platform-documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { query: z.object({ search: z.string().min(1), page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25) }).partial().optional() },
  responses: { 200: ApiResponse.and(z.object({ data: z.array(PlatformDocumentDto) }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
};

export type post_Post_platform_documents = typeof post_Post_platform_documents;
export const post_Post_platform_documents = {
  method: z.literal("POST"),
  path: z.literal("/platform-documents"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { body: JsonObject },
  responses: { 201: ApiResponse.and(z.object({ data: PlatformDocumentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type patch_Patch_platform_documents_documentId = typeof patch_Patch_platform_documents_documentId;
export const patch_Patch_platform_documents_documentId = {
  method: z.literal("PATCH"),
  path: z.literal("/platform-documents/{documentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ documentId: z.string() }), body: JsonObject },
  responses: { 200: ApiResponse.and(z.object({ data: PlatformDocumentDto }).partial()), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type delete_Delete_platform_documents_documentId = typeof delete_Delete_platform_documents_documentId;
export const delete_Delete_platform_documents_documentId = {
  method: z.literal("DELETE"),
  path: z.literal("/platform-documents/{documentId}"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: { path: z.object({ documentId: z.string() }) },
  responses: { 204: z.unknown(), 400: ErrorEnvelope, 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 404: ErrorEnvelope, 409: ErrorEnvelope, 500: ErrorEnvelope },
};

export type get_ExportAdminRelationships = typeof get_ExportAdminRelationships;
export const get_ExportAdminRelationships = {
  method: z.literal("GET"),
  path: z.literal("/admin/export-relationships"),
  requestFormat: z.literal("json"),
  responseFormat: z.literal("json"),
  parameters: z.never(),
  responses: { 200: z.unknown(), 401: z.union([AuthMiddlewareError, ErrorEnvelope]), 403: z.union([AuthMiddlewareError, ErrorEnvelope]), 500: ErrorEnvelope },
  responseHeaders: { 200: z.object({ "Content-Disposition": z.string() }) },
};

// </Endpoints>

  
     // <EndpointByMethod>
     export const EndpointByMethod = {
     get: {
           "/health": get_GetHealth,
"/users/by-platform/{platformId}": get_Get_users_by_platform_platformId,
"/users/by-context/{clientId}": get_Get_users_by_context_clientId,
"/profile": get_Get_profile,
"/admin/role-platforms": get_Get_admin_role_platforms,
"/admin/role-platforms/{roleId}": get_Get_admin_role_platforms_roleId,
"/operating-systems": get_Get_operating_systems,
"/operating-systems/{id}": get_Get_operating_systems_id,
"/licenses": get_Get_licenses,
"/licenses/{id}": get_Get_licenses_id,
"/shift-tickets/my-tickets": get_Get_shift_tickets_my_tickets,
"/shift-tickets/search/{ticketNumber}": get_Get_shift_tickets_search_ticketNumber,
"/shift-tickets/search-users": get_Get_shift_tickets_search_users,
"/shift-tickets/vms-by-client/{clientId}": get_Get_shift_tickets_vms_by_client_clientId,
"/shift-tickets/{ticketId}/relations": get_Get_shift_tickets_ticketId_relations,
"/incidents/my-incidents": get_Get_incidents_my_incidents,
"/incidents/search/{ticketNumber}": get_Get_incidents_search_ticketNumber,
"/incidents/search-users": get_Get_incidents_search_users,
"/incidents/vms-by-clients": get_Get_incidents_vms_by_clients,
"/incidents/devices-by-clients": get_Get_incidents_devices_by_clients,
"/master-activities": get_Get_master_activities,
"/master-activities/{id}": get_Get_master_activities_id,
"/scheduled-activities/pending-approvals": get_Get_scheduled_activities_pending_approvals,
"/scheduled-activities/my-changes": get_Get_scheduled_activities_my_changes,
"/scheduled-activities/{id}/approval-evidence": get_Get_scheduled_activities_id_approval_evidence,
"/overtime/rate-types": get_Get_overtime_rate_types,
"/planned-interruptions": get_Get_planned_interruptions,
"/bcp-tasks/{taskId}/vms": get_Get_bcp_tasks_taskId_vms,
"/audit-logs": get_Get_audit_logs,
"/audit-logs/{id}": get_Get_audit_logs_id,
"/notifications/unread-count": get_Get_notifications_unread_count,
"/notifications": get_Get_notifications,
"/backup-policies/export": get_Get_backup_policies_export,
"/backup-policies/client/{clientId}": get_Get_backup_policies_client_clientId,
"/backup-policies": get_Get_backup_policies,
"/backup-policies/{policyId}": get_Get_backup_policies_policyId,
"/backup-policies/{policyId}/documents": get_Get_backup_policies_policyId_documents,
"/auth/me": get_GetCurrentUser,
"/clients": get_Get_clients,
"/clients/{clientId}": get_Get_clients_clientId,
"/clients/{clientId}/users": get_Get_clients_clientId_users,
"/clients/{clientId}/assignment-context": get_Get_clients_clientId_assignment_context,
"/clients/{clientId}/datacenter-assignments": get_Get_clients_clientId_datacenter_assignments,
"/clients/{clientId}/datacenters/unified": get_Get_clients_clientId_datacenters_unified,
"/clients/{clientId}/delete-impact": get_Get_clients_clientId_delete_impact,
"/enterprises": get_Get_enterprises,
"/enterprises/{enterpriseId}": get_Get_enterprises_enterpriseId,
"/enterprises/{enterpriseId}/delivery-units": get_Get_enterprises_enterpriseId_delivery_units,
"/enterprises/{enterpriseId}/practice-units": get_Get_enterprises_enterpriseId_practice_units,
"/delivery-units": get_Get_delivery_units,
"/delivery-units/{unitId}": get_Get_delivery_units_unitId,
"/practice-units": get_Get_practice_units,
"/practice-units/{unitId}": get_Get_practice_units_unitId,
"/clients/{clientId}/companies": get_Get_clients_clientId_companies,
"/clients/{clientId}/companies/{companyId}": get_Get_clients_clientId_companies_companyId,
"/clients/{clientId}/owned-datacenters": get_Get_clients_clientId_owned_datacenters,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}": get_Get_clients_clientId_owned_datacenters_ownedDatacenterId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms": get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks": get_Get_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks,
"/clients/{clientId}/windows": get_Get_clients_clientId_windows,
"/clients/{clientId}/window-groups": get_Get_clients_clientId_window_groups,
"/roles": get_Get_roles,
"/roles/catalog": get_Get_roles_catalog,
"/roles/{roleId}": get_Get_roles_roleId,
"/roles/{roleId}/users": get_Get_roles_roleId_users,
"/permissions/catalog": get_Get_permissions_catalog,
"/permissions/matrix": get_Get_permissions_matrix,
"/user-client-assignments": get_Get_user_client_assignments,
"/users": get_Get_users,
"/users/{userId}": get_Get_users_userId,
"/users/{userId}/client-assignments": get_Get_users_userId_client_assignments,
"/users/{userId}/client-assignments/{clientId}": get_Get_users_userId_client_assignments_clientId,
"/users/{userId}/wbs-coding-assignments": get_Get_users_userId_wbs_coding_assignments,
"/opportunity-catalogs/phases": get_Get_opportunity_catalogs_phases,
"/opportunity-catalogs/step-templates": get_Get_opportunity_catalogs_step_templates,
"/opportunity-catalogs/practices": get_Get_opportunity_catalogs_practices,
"/opportunity-catalogs/deal-types": get_Get_opportunity_catalogs_deal_types,
"/opportunity-catalogs/offering-codes": get_Get_opportunity_catalogs_offering_codes,
"/clients/{clientId}/opportunities": get_Get_clients_clientId_opportunities,
"/opportunities": get_Get_opportunities,
"/opportunities/{opportunityId}": get_Get_opportunities_opportunityId,
"/opportunities/{opportunityId}/export-excel": get_Get_opportunities_opportunityId_export_excel,
"/opportunities/{opportunityId}/steps": get_Get_opportunities_opportunityId_steps,
"/opportunities/{opportunityId}/action-items": get_Get_opportunities_opportunityId_action_items,
"/opportunities/{opportunityId}/reviews": get_Get_opportunities_opportunityId_reviews,
"/opportunities/{opportunityId}/costs": get_Get_opportunities_opportunityId_costs,
"/opportunities/{opportunityId}/practices": get_Get_opportunities_opportunityId_practices,
"/opportunities/{opportunityId}/attachments": get_Get_opportunities_opportunityId_attachments,
"/bcp-plans": get_Get_bcp_plans,
"/bcp-plans/{planId}": get_Get_bcp_plans_planId,
"/bcp-tests": get_Get_bcp_tests,
"/bcp-tests/{testId}": get_Get_bcp_tests_testId,
"/bcp-tasks": get_Get_bcp_tasks,
"/bcp-tasks/{taskId}": get_Get_bcp_tasks_taskId,
"/bcp-evidences": get_Get_bcp_evidences,
"/bcp-evidences/{evidenceId}": get_Get_bcp_evidences_evidenceId,
"/bcp-findings": get_Get_bcp_findings,
"/bcp-findings/{findingId}": get_Get_bcp_findings_findingId,
"/shift-tickets": get_Get_shift_tickets,
"/shift-tickets/{ticketId}": get_Get_shift_tickets_ticketId,
"/shift-tickets/{ticketId}/rca": get_Get_shift_tickets_ticketId_rca,
"/incidents/sla-config/{clientId}": get_Get_incidents_sla_config_clientId,
"/incidents": get_Get_incidents,
"/incidents/{incidentId}": get_Get_incidents_incidentId,
"/incidents/{incidentId}/rca": get_Get_incidents_incidentId_rca,
"/clients/{clientId}/standby-assignments": get_Get_clients_clientId_standby_assignments,
"/users/{userId}/standby-assignments": get_Get_users_userId_standby_assignments,
"/users/{userId}/standby-context": get_Get_users_userId_standby_context,
"/standby-assignments/stats": get_Get_standby_assignments_stats,
"/standby-assignments/export": get_Get_standby_assignments_export,
"/standby-assignments/export-report": get_Get_standby_assignments_export_report,
"/standby-assignments/{assignmentId}": get_Get_standby_assignments_assignmentId,
"/ticketing-tools": get_Get_ticketing_tools,
"/enterprises/{enterpriseId}/ticketing-tools": get_Get_enterprises_enterpriseId_ticketing_tools,
"/clients/{clientId}/ticketing-tools": get_Get_clients_clientId_ticketing_tools,
"/activity-categories": get_Get_activity_categories,
"/clients/{clientId}/infrastructure-baseline": get_Get_clients_clientId_infrastructure_baseline,
"/clients/{clientId}/baseline-history": get_Get_clients_clientId_baseline_history,
"/clients/{clientId}/baseline-history/periods": get_Get_clients_clientId_baseline_history_periods,
"/baseline-snapshots/{id}/download": get_Get_baseline_snapshots_id_download,
"/resource-snapshots": get_Get_resource_snapshots,
"/device-snapshots": get_Get_device_snapshots,
"/cloud-providers": get_Get_cloud_providers,
"/cloud-accounts": get_Get_cloud_accounts,
"/cloud-accounts/{accountId}": get_Get_cloud_accounts_accountId,
"/cloud-services": get_Get_cloud_services,
"/cloud-volumes": get_Get_cloud_volumes,
"/clients/{clientId}/cloud-summary": get_Get_clients_clientId_cloud_summary,
"/consumption-conflicts": get_Get_consumption_conflicts,
"/consumption-conflicts/{id}": get_Get_consumption_conflicts_id,
"/datacenters": get_ListDatacenters,
"/datacenters/{datacenterId}": get_GetDatacenter,
"/datacenters/{datacenterId}/retirement-impact": get_GetDatacenterRetirementImpact,
"/device-models": get_Get_device_models,
"/device-models/{deviceModelId}": get_Get_device_models_deviceModelId,
"/device-categories": get_Get_device_categories,
"/device-instances": get_Get_device_instances,
"/device-instances/{deviceInstanceId}": get_Get_device_instances_deviceInstanceId,
"/racks/{rackId}/device-instances": get_Get_racks_rackId_device_instances,
"/clients/{clientId}/datacenters/{datacenterId}/rooms/{roomId}/racks/{rackId}/device-instances": get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks_rackId_device_instances,
"/racks": get_Get_racks,
"/racks/{rackId}": get_Get_racks_rackId,
"/clients/{clientId}/datacenters/{datacenterId}/rooms/{roomId}/racks": get_Get_clients_clientId_datacenters_datacenterId_rooms_roomId_racks,
"/rooms": get_Get_rooms,
"/rooms/{roomId}": get_Get_rooms_roomId,
"/clients/{clientId}/datacenters/{datacenterId}/rooms": get_Get_clients_clientId_datacenters_datacenterId_rooms,
"/subsystems": get_Get_subsystems,
"/database-systems": get_Get_database_systems,
"/sap-products": get_Get_sap_products,
"/clients/{clientId}/vm-clusters": get_Get_clients_clientId_vm_clusters,
"/clients/{clientId}/vm-clusters/{clusterId}": get_Get_clients_clientId_vm_clusters_clusterId,
"/clients/{clientId}/vm-clusters/{clusterId}/context": get_Get_clients_clientId_vm_clusters_clusterId_context,
"/clients/{clientId}/vm-clusters/{clusterId}/available-vms": get_Get_clients_clientId_vm_clusters_clusterId_available_vms,
"/device-instances/{deviceInstanceId}/virtual-machines": get_Get_device_instances_deviceInstanceId_virtual_machines,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}": get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId,
"/virtual-machines": get_Get_virtual_machines,
"/virtual-machines/{vmId}": get_Get_virtual_machines_vmId,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations": get_Get_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations,
"/vm-dr-relationships": get_Get_vm_dr_relationships,
"/vm-dr-relationships/{relationshipId}": get_Get_vm_dr_relationships_relationshipId,
"/virtual-machines/{vmId}/documents": get_Get_virtual_machines_vmId_documents,
"/clients/{clientId}/work-schedule": get_Get_clients_clientId_work_schedule,
"/country-holidays": get_Get_country_holidays,
"/availability-holidays": get_Get_availability_holidays,
"/availability-configurations": get_Get_availability_configurations,
"/availability-configurations/{configurationId}": get_Get_availability_configurations_configurationId,
"/availability-configurations/{configurationId}/tiers": get_Get_availability_configurations_configurationId_tiers,
"/availability-tiers/{tierId}/vms": get_Get_availability_tiers_tierId_vms,
"/availability-interruptions": get_Get_availability_interruptions,
"/availability-reports": get_Get_availability_reports,
"/availability-reports/{reportId}": get_Get_availability_reports_reportId,
"/availability-reports/{reportId}/pdf": get_Get_availability_reports_reportId_pdf,
"/scheduled-activities/{activityId}/execution": get_Get_scheduled_activities_activityId_execution,
"/overtime/by-activity/{activityId}": get_Get_overtime_by_activity_activityId,
"/overtime/by-user/{userId}": get_Get_overtime_by_user_userId,
"/overtime/summary": get_Get_overtime_summary,
"/overtime/export/{activityId}": get_Get_overtime_export_activityId,
"/overtime/by-user-with-standby/{userId}": get_Get_overtime_by_user_with_standby_userId,
"/overtime/standby-report": get_Get_overtime_standby_report,
"/scheduled-activities/{id}/rca": get_Get_scheduled_activities_id_rca,
"/operational-plans": get_Get_operational_plans,
"/operational-plans/{id}": get_Get_operational_plans_id,
"/scheduled-activities": get_Get_scheduled_activities,
"/scheduled-activities/report": get_Get_scheduled_activities_report,
"/scheduled-activities/audit": get_Get_scheduled_activities_audit,
"/scheduled-activities/client-approvers/{clientId}": get_Get_scheduled_activities_client_approvers_clientId,
"/scheduled-activities/search-for-baseline": get_Get_scheduled_activities_search_for_baseline,
"/scheduled-activities/{activityId}/export-work-plan": get_Get_scheduled_activities_activityId_export_work_plan,
"/scheduled-activities/{id}": get_Get_scheduled_activities_id,
"/scheduled-activities/{activityId}/tasks": get_Get_scheduled_activities_activityId_tasks,
"/change-templates": get_Get_change_templates,
"/change-templates/{templateId}": get_Get_change_templates_templateId,
"/change-batches": get_Get_change_batches,
"/change-batches/{batchId}": get_Get_change_batches_batchId,
"/scheduled-activities/{activityId}/validation-reports": get_Get_scheduled_activities_activityId_validation_reports,
"/scheduled-activities/{activityId}/validation-reports/latest": get_Get_scheduled_activities_activityId_validation_reports_latest,
"/search": get_SearchInfrastructure,
"/app-settings/theme": get_GetApplicationTheme,
"/countries": get_Get_countries,
"/countries/{countryId}": get_Get_countries_countryId,
"/languages": get_Get_languages,
"/languages/{languageId}": get_Get_languages_languageId,
"/platforms": get_Get_platforms,
"/platforms/{platformId}": get_Get_platforms_platformId,
"/platforms/{platformId}/document-categories": get_Get_platforms_platformId_document_categories,
"/platform-categories": get_Get_platform_categories,
"/platform-documents": get_Get_platform_documents,
"/admin/export-relationships": get_ExportAdminRelationships
         },
patch: {
           "/profile/language": patch_Patch_profile_language,
"/profile/avatar": patch_Patch_profile_avatar,
"/clients/{clientId}": patch_Patch_clients_clientId,
"/clients/{clientId}/datacenter-assignments/{datacenterId}": patch_Patch_clients_clientId_datacenter_assignments_datacenterId,
"/enterprises/{enterpriseId}": patch_Patch_enterprises_enterpriseId,
"/enterprises/{enterpriseId}/delivery-units/{unitId}": patch_Patch_enterprises_enterpriseId_delivery_units_unitId,
"/enterprises/{enterpriseId}/practice-units/{unitId}": patch_Patch_enterprises_enterpriseId_practice_units_unitId,
"/delivery-units/{unitId}": patch_Patch_delivery_units_unitId,
"/practice-units/{unitId}": patch_Patch_practice_units_unitId,
"/clients/{clientId}/companies/{companyId}": patch_Patch_clients_clientId_companies_companyId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}": patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}": patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks/{rackId}": patch_Patch_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId,
"/clients/{clientId}/windows/{windowId}": patch_Patch_clients_clientId_windows_windowId,
"/clients/{clientId}/window-groups/{groupId}": patch_Patch_clients_clientId_window_groups_groupId,
"/roles/{roleId}": patch_Patch_roles_roleId,
"/users/{userId}/wbs-coding-assignments/{assignmentId}": patch_Patch_users_userId_wbs_coding_assignments_assignmentId,
"/standby-assignments/{assignmentId}": patch_Patch_standby_assignments_assignmentId,
"/ticketing-tools/{toolId}": patch_Patch_ticketing_tools_toolId,
"/activity-categories/{categoryId}": patch_Patch_activity_categories_categoryId,
"/cloud-accounts/{accountId}": patch_Patch_cloud_accounts_accountId,
"/cloud-services/{serviceId}": patch_Patch_cloud_services_serviceId,
"/cloud-volumes/{volumeId}": patch_Patch_cloud_volumes_volumeId,
"/datacenters/{datacenterId}": patch_UpdateDatacenter,
"/device-models/{deviceModelId}": patch_Patch_device_models_deviceModelId,
"/device-instances/{deviceInstanceId}": patch_Patch_device_instances_deviceInstanceId,
"/racks/{rackId}": patch_Patch_racks_rackId,
"/racks/{rackId}/status": patch_Patch_racks_rackId_status,
"/rooms/{roomId}": patch_Patch_rooms_roomId,
"/rooms/{roomId}/status": patch_Patch_rooms_roomId_status,
"/subsystems/{subsystemId}": patch_Patch_subsystems_subsystemId,
"/database-systems/{databaseSystemId}": patch_Patch_database_systems_databaseSystemId,
"/sap-products/{sapProductId}": patch_Patch_sap_products_sapProductId,
"/clients/{clientId}/vm-clusters/{clusterId}": patch_Patch_clients_clientId_vm_clusters_clusterId,
"/clients/{clientId}/vm-clusters/{clusterId}/members/{virtualMachineId}": patch_Patch_clients_clientId_vm_clusters_clusterId_members_virtualMachineId,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}": patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId,
"/virtual-machines/{vmId}": patch_Patch_virtual_machines_vmId,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}": patch_Patch_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId,
"/vm-dr-relationships/{relationshipId}": patch_Patch_vm_dr_relationships_relationshipId,
"/virtual-machines/{vmId}/documents/{documentId}": patch_Patch_virtual_machines_vmId_documents_documentId,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/progress": patch_Patch_scheduled_activities_activityId_execution_tasks_taskLogId_progress,
"/scheduled-activities/{id}/execution": patch_Patch_scheduled_activities_id_execution,
"/change-batches/{batchId}": patch_Patch_change_batches_batchId,
"/countries/{countryId}": patch_Patch_countries_countryId,
"/languages/{languageId}": patch_Patch_languages_languageId,
"/platforms/{platformId}": patch_Patch_platforms_platformId,
"/platform-document-categories/{categoryId}": patch_Patch_platform_document_categories_categoryId,
"/platform-documents/{documentId}": patch_Patch_platform_documents_documentId
         },
put: {
           "/admin/role-platforms/{roleId}": put_Put_admin_role_platforms_roleId,
"/operating-systems/{id}": put_Put_operating_systems_id,
"/licenses/{id}": put_Put_licenses_id,
"/master-activities/{id}": put_Put_master_activities_id,
"/scheduled-activities/{id}/extend-execution": put_Put_scheduled_activities_id_extend_execution,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}": put_Put_scheduled_activities_activityId_execution_tasks_taskLogId,
"/notifications/read-all": put_Put_notifications_read_all,
"/notifications/{id}/read": put_Put_notifications_id_read,
"/backup-policies/{policyId}": put_Put_backup_policies_policyId,
"/backup-policies/{policyId}/document": put_Put_backup_policies_policyId_document,
"/clients/{clientId}/datacenter-assignments": put_Put_clients_clientId_datacenter_assignments,
"/users/{userId}": put_Put_users_userId,
"/users/{userId}/client-assignments": put_Put_users_userId_client_assignments,
"/users/{userId}/client-assignments/{clientId}": put_Put_users_userId_client_assignments_clientId,
"/users/{userId}/roles": put_Put_users_userId_roles,
"/users/{userId}/wbs-assignments": put_Put_users_userId_wbs_assignments,
"/opportunities/{opportunityId}": put_Put_opportunities_opportunityId,
"/opportunities/{opportunityId}/steps/{stepId}": put_Put_opportunities_opportunityId_steps_stepId,
"/opportunities/{opportunityId}/action-items/{itemId}": put_Put_opportunities_opportunityId_action_items_itemId,
"/opportunities/{opportunityId}/reviews/{reviewId}": put_Put_opportunities_opportunityId_reviews_reviewId,
"/opportunities/{opportunityId}/costs/{costId}": put_Put_opportunities_opportunityId_costs_costId,
"/opportunities/{opportunityId}/practices": put_Put_opportunities_opportunityId_practices,
"/bcp-plans/{planId}": put_Put_bcp_plans_planId,
"/bcp-tests/{testId}": put_Put_bcp_tests_testId,
"/bcp-tasks/{taskId}": put_Put_bcp_tasks_taskId,
"/bcp-evidences/{evidenceId}": put_Put_bcp_evidences_evidenceId,
"/bcp-findings/{findingId}": put_Put_bcp_findings_findingId,
"/shift-tickets/activities/{activityId}/end": put_Put_shift_tickets_activities_activityId_end,
"/shift-tickets/{ticketId}/close": put_Put_shift_tickets_ticketId_close,
"/shift-tickets/{ticketId}/cancel": put_Put_shift_tickets_ticketId_cancel,
"/shift-tickets/{ticketId}/reopen": put_Put_shift_tickets_ticketId_reopen,
"/incidents/sla-config/{clientId}": put_Put_incidents_sla_config_clientId,
"/incidents/activities/{activityId}/end": put_Put_incidents_activities_activityId_end,
"/incidents/{incidentId}/end-participation": put_Put_incidents_incidentId_end_participation,
"/incidents/{incidentId}/resolve": put_Put_incidents_incidentId_resolve,
"/incidents/{incidentId}/close": put_Put_incidents_incidentId_close,
"/incidents/{incidentId}/impact-clock/start": put_Put_incidents_incidentId_impact_clock_start,
"/incidents/{incidentId}/impact-clock/stop": put_Put_incidents_incidentId_impact_clock_stop,
"/consumption-conflicts/{id}/resolve": put_Put_consumption_conflicts_id_resolve,
"/racks/{rackId}/clients": put_Put_racks_rackId_clients,
"/rooms/{roomId}/clients": put_Put_rooms_roomId_clients,
"/clients/{clientId}/work-schedule": put_Put_clients_clientId_work_schedule,
"/availability-configurations/{configurationId}": put_Put_availability_configurations_configurationId,
"/availability-tiers/{tierId}": put_Put_availability_tiers_tierId,
"/availability-interruptions/{interruptionId}": put_Put_availability_interruptions_interruptionId,
"/scheduled-activities/{activityId}/execution/evidence/{evidenceId}": put_Put_scheduled_activities_activityId_execution_evidence_evidenceId,
"/scheduled-activities/{activityId}/execution/findings/{findingId}": put_Put_scheduled_activities_activityId_execution_findings_findingId,
"/scheduled-activities/{activityId}/execution/report-url": put_Put_scheduled_activities_activityId_execution_report_url,
"/scheduled-activities/{id}/rca": put_Put_scheduled_activities_id_rca,
"/operational-plans/{id}": put_Put_operational_plans_id,
"/scheduled-activities/{id}": put_Put_scheduled_activities_id,
"/scheduled-activities/{activityId}/tasks/{taskId}": put_Put_scheduled_activities_activityId_tasks_taskId,
"/change-templates/{templateId}": put_Put_change_templates_templateId,
"/app-settings/theme": put_PutApplicationTheme
         },
post: {
           "/operating-systems": post_Post_operating_systems,
"/licenses": post_Post_licenses,
"/shift-tickets/{ticketId}/relations": post_Post_shift_tickets_ticketId_relations,
"/master-activities": post_Post_master_activities,
"/scheduled-activities/{id}/approval-evidence": post_Post_scheduled_activities_id_approval_evidence,
"/scheduled-activities/{id}/check-task-dates": post_Post_scheduled_activities_id_check_task_dates,
"/scheduled-activities/{id}/adjust-task-dates": post_Post_scheduled_activities_id_adjust_task_dates,
"/scheduled-activities/{activityId}/execution/tasks": post_Post_scheduled_activities_activityId_execution_tasks,
"/overtime/entries": post_Post_overtime_entries,
"/bcp-tasks/{taskId}/vms": post_Post_bcp_tasks_taskId_vms,
"/backup-policies/alerts/run": post_Post_backup_policies_alerts_run,
"/backup-policies": post_Post_backup_policies,
"/backup-policies/{policyId}/documents": post_Post_backup_policies_policyId_documents,
"/auth/password/sign-in": post_PasswordSignIn,
"/auth/otp/request": post_RequestOtp,
"/auth/otp/sign-in": post_OtpSignIn,
"/auth/password/change": post_ChangePassword,
"/auth/session/refresh": post_RefreshSession,
"/auth/session/sign-out": post_SignOut,
"/auth/password/recovery/request": post_RequestPasswordRecovery,
"/auth/password/recovery/complete": post_CompletePasswordRecovery,
"/auth/password/force-change": post_ForcePasswordChange,
"/clients": post_Post_clients,
"/clients/{clientId}/datacenter-assignments": post_Post_clients_clientId_datacenter_assignments,
"/clients/{clientId}/deactivate": post_Post_clients_clientId_deactivate,
"/enterprises": post_Post_enterprises,
"/enterprises/{enterpriseId}/deactivate": post_Post_enterprises_enterpriseId_deactivate,
"/enterprises/{enterpriseId}/delivery-units": post_Post_enterprises_enterpriseId_delivery_units,
"/enterprises/{enterpriseId}/practice-units": post_Post_enterprises_enterpriseId_practice_units,
"/enterprises/{enterpriseId}/delivery-units/{unitId}/deactivate": post_Post_enterprises_enterpriseId_delivery_units_unitId_deactivate,
"/enterprises/{enterpriseId}/practice-units/{unitId}/deactivate": post_Post_enterprises_enterpriseId_practice_units_unitId_deactivate,
"/delivery-units": post_Post_delivery_units,
"/practice-units": post_Post_practice_units,
"/clients/{clientId}/companies": post_Post_clients_clientId_companies,
"/clients/{clientId}/companies/{companyId}/datacenter-links": post_Post_clients_clientId_companies_companyId_datacenter_links,
"/clients/{clientId}/owned-datacenters": post_Post_clients_clientId_owned_datacenters,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms": post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks": post_Post_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks,
"/clients/{clientId}/windows": post_Post_clients_clientId_windows,
"/clients/{clientId}/window-groups": post_Post_clients_clientId_window_groups,
"/roles": post_Post_roles,
"/users": post_Post_users,
"/users/{userId}/deactivate": post_Post_users_userId_deactivate,
"/users/{userId}/reactivate": post_Post_users_userId_reactivate,
"/users/{userId}/wbs-coding-assignments": post_Post_users_userId_wbs_coding_assignments,
"/users/{userId}/welcome-email": post_Post_users_userId_welcome_email,
"/clients/{clientId}/opportunities": post_Post_clients_clientId_opportunities,
"/opportunities/{opportunityId}/steps": post_Post_opportunities_opportunityId_steps,
"/opportunities/{opportunityId}/action-items": post_Post_opportunities_opportunityId_action_items,
"/opportunities/{opportunityId}/reviews": post_Post_opportunities_opportunityId_reviews,
"/opportunities/{opportunityId}/costs": post_Post_opportunities_opportunityId_costs,
"/opportunities/{opportunityId}/attachments": post_Post_opportunities_opportunityId_attachments,
"/bcp-plans": post_Post_bcp_plans,
"/bcp-tests": post_Post_bcp_tests,
"/bcp-tasks": post_Post_bcp_tasks,
"/bcp-evidences": post_Post_bcp_evidences,
"/bcp-findings": post_Post_bcp_findings,
"/shift-tickets": post_Post_shift_tickets,
"/shift-tickets/{ticketId}/enroll": post_Post_shift_tickets_ticketId_enroll,
"/shift-tickets/{ticketId}/activities": post_Post_shift_tickets_ticketId_activities,
"/shift-tickets/{ticketId}/rca": post_Post_shift_tickets_ticketId_rca,
"/shift-tickets/{ticketId}/rca/generate-pdf": post_Post_shift_tickets_ticketId_rca_generate_pdf,
"/shift-tickets/rca/{rcaId}/findings": post_Post_shift_tickets_rca_rcaId_findings,
"/shift-tickets/rca/findings/{findingId}/actions": post_Post_shift_tickets_rca_findings_findingId_actions,
"/incidents": post_Post_incidents,
"/incidents/{incidentId}/participate": post_Post_incidents_incidentId_participate,
"/incidents/{incidentId}/activities": post_Post_incidents_incidentId_activities,
"/incidents/{incidentId}/rca": post_Post_incidents_incidentId_rca,
"/incidents/{incidentId}/rca/generate-pdf": post_Post_incidents_incidentId_rca_generate_pdf,
"/incidents/rca/{rcaId}/findings": post_Post_incidents_rca_rcaId_findings,
"/incidents/rca/findings/{findingId}/actions": post_Post_incidents_rca_findings_findingId_actions,
"/incidents/rca/{rcaId}/contributing-factors": post_Post_incidents_rca_rcaId_contributing_factors,
"/incidents/rca/{rcaId}/lessons": post_Post_incidents_rca_rcaId_lessons,
"/incidents/rca/{rcaId}/versions": post_Post_incidents_rca_rcaId_versions,
"/standby-assignments/bulk": post_Post_standby_assignments_bulk,
"/standby-assignments/weekly-copy": post_Post_standby_assignments_weekly_copy,
"/standby-assignments/week-copy-draft": post_Post_standby_assignments_week_copy_draft,
"/standby-assignments": post_Post_standby_assignments,
"/standby-assignments/{assignmentId}/toggle": post_Post_standby_assignments_assignmentId_toggle,
"/ticketing-tools": post_Post_ticketing_tools,
"/enterprises/{enterpriseId}/ticketing-tools": post_Post_enterprises_enterpriseId_ticketing_tools,
"/clients/{clientId}/ticketing-tools": post_Post_clients_clientId_ticketing_tools,
"/activity-categories": post_Post_activity_categories,
"/resource-snapshots": post_Post_resource_snapshots,
"/resource-snapshots/bulk": post_Post_resource_snapshots_bulk,
"/device-snapshots": post_Post_device_snapshots,
"/device-snapshots/bulk": post_Post_device_snapshots_bulk,
"/cloud-accounts": post_Post_cloud_accounts,
"/cloud-services": post_Post_cloud_services,
"/cloud-volumes": post_Post_cloud_volumes,
"/cloud-volumes/bulk-upsert": post_Post_cloud_volumes_bulk_upsert,
"/consumption-conflicts": post_Post_consumption_conflicts,
"/datacenters": post_CreateDatacenter,
"/device-models": post_Post_device_models,
"/device-instances": post_Post_device_instances,
"/device-instances/{deviceInstanceId}/move": post_Post_device_instances_deviceInstanceId_move,
"/racks": post_Post_racks,
"/rooms": post_Post_rooms,
"/subsystems": post_Post_subsystems,
"/database-systems": post_Post_database_systems,
"/sap-products": post_Post_sap_products,
"/clients/{clientId}/vm-clusters": post_Post_clients_clientId_vm_clusters,
"/clients/{clientId}/vm-clusters/{clusterId}/members": post_Post_clients_clientId_vm_clusters_clusterId_members,
"/device-instances/{deviceInstanceId}/virtual-machines": post_Post_device_instances_deviceInstanceId_virtual_machines,
"/virtual-machines": post_Post_virtual_machines,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations": post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}/version-change": post_Post_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId_version_change,
"/vm-dr-relationships": post_Post_vm_dr_relationships,
"/virtual-machines/{vmId}/documents": post_Post_virtual_machines_vmId_documents,
"/country-holidays": post_Post_country_holidays,
"/availability-holidays": post_Post_availability_holidays,
"/availability-configurations": post_Post_availability_configurations,
"/availability-configurations/{configurationId}/activate": post_Post_availability_configurations_configurationId_activate,
"/availability-configurations/{configurationId}/deactivate": post_Post_availability_configurations_configurationId_deactivate,
"/availability-configurations/{configurationId}/tiers": post_Post_availability_configurations_configurationId_tiers,
"/availability-configurations/{configurationId}/auto-classify": post_Post_availability_configurations_configurationId_auto_classify,
"/availability-tiers/{tierId}/vms": post_Post_availability_tiers_tierId_vms,
"/availability-interruptions": post_Post_availability_interruptions,
"/availability-reports/calculate": post_Post_availability_reports_calculate,
"/scheduled-activities/{activityId}/execution": post_Post_scheduled_activities_activityId_execution,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/timers": post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/timers/{timerId}/stop": post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_timers_timerId_stop,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/evidence": post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_evidence,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}/findings": post_Post_scheduled_activities_activityId_execution_tasks_taskLogId_findings,
"/scheduled-activities/{activityId}/execution/rollback": post_Post_scheduled_activities_activityId_execution_rollback,
"/scheduled-activities/{activityId}/execution/complete": post_Post_scheduled_activities_activityId_execution_complete,
"/scheduled-activities/{activityId}/execution/fail": post_Post_scheduled_activities_activityId_execution_fail,
"/overtime/calculate/{activityId}": post_Post_overtime_calculate_activityId,
"/scheduled-activities/{id}/rca": post_Post_scheduled_activities_id_rca,
"/operational-plans": post_Post_operational_plans,
"/scheduled-activities": post_Post_scheduled_activities,
"/scheduled-activities/{activityId}/scope-items": post_Post_scheduled_activities_activityId_scope_items,
"/scheduled-activities/{id}/transitions": post_Post_scheduled_activities_id_transitions,
"/scheduled-activities/{id}/approval-decisions": post_Post_scheduled_activities_id_approval_decisions,
"/scheduled-activities/{id}/cancel": post_Post_scheduled_activities_id_cancel,
"/scheduled-activities/{id}/execution": post_Post_scheduled_activities_id_execution,
"/scheduled-activities/{activityId}/tasks": post_Post_scheduled_activities_activityId_tasks,
"/scheduled-activities/{activityId}/tasks/reorder": post_Post_scheduled_activities_activityId_tasks_reorder,
"/scheduled-activities/{activityId}/tasks/cascade-times": post_Post_scheduled_activities_activityId_tasks_cascade_times,
"/change-templates": post_Post_change_templates,
"/change-templates/{templateId}/vms": post_Post_change_templates_templateId_vms,
"/change-templates/from-activity/{activityId}": post_Post_change_templates_from_activity_activityId,
"/change-templates/{templateId}/apply/{activityId}": post_Post_change_templates_templateId_apply_activityId,
"/change-batches": post_Post_change_batches,
"/change-batches/{batchId}/cancel": post_Post_change_batches_batchId_cancel,
"/scheduled-activities/{activityId}/validation-runs": post_Post_scheduled_activities_activityId_validation_runs,
"/countries": post_Post_countries,
"/languages": post_Post_languages,
"/platforms": post_Post_platforms,
"/platforms/{platformId}/document-categories": post_Post_platforms_platformId_document_categories,
"/platform-documents": post_Post_platform_documents
         },
delete: {
           "/operating-systems/{id}": delete_Delete_operating_systems_id,
"/licenses/{id}": delete_Delete_licenses_id,
"/shift-tickets/relations/{relationId}": delete_Delete_shift_tickets_relations_relationId,
"/shift-tickets/{ticketId}/leave": delete_Delete_shift_tickets_ticketId_leave,
"/master-activities/{id}": delete_Delete_master_activities_id,
"/scheduled-activities/{id}/approval-evidence/{evidenceId}": delete_Delete_scheduled_activities_id_approval_evidence_evidenceId,
"/scheduled-activities/{activityId}/execution/tasks/{taskLogId}": delete_Delete_scheduled_activities_activityId_execution_tasks_taskLogId,
"/overtime/entries/{id}": delete_Delete_overtime_entries_id,
"/bcp-tasks/{taskId}/vms/{vmId}": delete_Delete_bcp_tasks_taskId_vms_vmId,
"/backup-policies/{policyId}": delete_Delete_backup_policies_policyId,
"/backup-policies/documents/{documentId}": delete_Delete_backup_policies_documents_documentId,
"/clients/{clientId}": delete_Delete_clients_clientId,
"/clients/{clientId}/datacenter-assignments/{datacenterId}": delete_Delete_clients_clientId_datacenter_assignments_datacenterId,
"/enterprises/{enterpriseId}": delete_Delete_enterprises_enterpriseId,
"/delivery-units/{unitId}": delete_Delete_delivery_units_unitId,
"/practice-units/{unitId}": delete_Delete_practice_units_unitId,
"/clients/{clientId}/companies/{companyId}": delete_Delete_clients_clientId_companies_companyId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}": delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}": delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId,
"/clients/{clientId}/owned-datacenters/{ownedDatacenterId}/rooms/{roomId}/racks/{rackId}": delete_Delete_clients_clientId_owned_datacenters_ownedDatacenterId_rooms_roomId_racks_rackId,
"/clients/{clientId}/windows/{windowId}": delete_Delete_clients_clientId_windows_windowId,
"/clients/{clientId}/window-groups/{groupId}": delete_Delete_clients_clientId_window_groups_groupId,
"/roles/{roleId}": delete_Delete_roles_roleId,
"/users/{userId}/client-assignments/{clientId}": delete_Delete_users_userId_client_assignments_clientId,
"/users/{userId}/wbs-coding-assignments/{assignmentId}": delete_Delete_users_userId_wbs_coding_assignments_assignmentId,
"/opportunities/{opportunityId}": delete_Delete_opportunities_opportunityId,
"/opportunities/{opportunityId}/steps/{stepId}": delete_Delete_opportunities_opportunityId_steps_stepId,
"/opportunities/{opportunityId}/action-items/{itemId}": delete_Delete_opportunities_opportunityId_action_items_itemId,
"/opportunities/{opportunityId}/reviews/{reviewId}": delete_Delete_opportunities_opportunityId_reviews_reviewId,
"/opportunities/{opportunityId}/costs/{costId}": delete_Delete_opportunities_opportunityId_costs_costId,
"/opportunities/{opportunityId}/attachments/{attachmentId}": delete_Delete_opportunities_opportunityId_attachments_attachmentId,
"/bcp-plans/{planId}": delete_Delete_bcp_plans_planId,
"/bcp-tests/{testId}": delete_Delete_bcp_tests_testId,
"/bcp-tasks/{taskId}": delete_Delete_bcp_tasks_taskId,
"/bcp-evidences/{evidenceId}": delete_Delete_bcp_evidences_evidenceId,
"/bcp-findings/{findingId}": delete_Delete_bcp_findings_findingId,
"/standby-assignments/{assignmentId}": delete_Delete_standby_assignments_assignmentId,
"/ticketing-tools/{toolId}": delete_Delete_ticketing_tools_toolId,
"/enterprises/{enterpriseId}/ticketing-tools/{assignmentId}": delete_Delete_enterprises_enterpriseId_ticketing_tools_assignmentId,
"/clients/{clientId}/ticketing-tools/{assignmentId}": delete_Delete_clients_clientId_ticketing_tools_assignmentId,
"/resource-snapshots/{id}": delete_Delete_resource_snapshots_id,
"/device-snapshots/{id}": delete_Delete_device_snapshots_id,
"/cloud-accounts/{accountId}": delete_Delete_cloud_accounts_accountId,
"/cloud-services/{serviceId}": delete_Delete_cloud_services_serviceId,
"/cloud-volumes/{volumeId}": delete_Delete_cloud_volumes_volumeId,
"/datacenters/{datacenterId}": delete_DeleteDatacenter,
"/device-models/{deviceModelId}": delete_Delete_device_models_deviceModelId,
"/device-instances/{deviceInstanceId}": delete_Delete_device_instances_deviceInstanceId,
"/racks/{rackId}": delete_Delete_racks_rackId,
"/rooms/{roomId}": delete_Delete_rooms_roomId,
"/subsystems/{subsystemId}": delete_Delete_subsystems_subsystemId,
"/database-systems/{databaseSystemId}": delete_Delete_database_systems_databaseSystemId,
"/sap-products/{sapProductId}": delete_Delete_sap_products_sapProductId,
"/clients/{clientId}/vm-clusters/{clusterId}": delete_Delete_clients_clientId_vm_clusters_clusterId,
"/clients/{clientId}/vm-clusters/{clusterId}/members/{virtualMachineId}": delete_Delete_clients_clientId_vm_clusters_clusterId_members_virtualMachineId,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}": delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId,
"/virtual-machines/{vmId}": delete_Delete_virtual_machines_vmId,
"/device-instances/{deviceInstanceId}/virtual-machines/{virtualMachineId}/software-associations/{associationId}": delete_Delete_device_instances_deviceInstanceId_virtual_machines_virtualMachineId_software_associations_associationId,
"/vm-dr-relationships/{relationshipId}": delete_Delete_vm_dr_relationships_relationshipId,
"/virtual-machines/{vmId}/documents/{documentId}": delete_Delete_virtual_machines_vmId_documents_documentId,
"/country-holidays/{holidayId}": delete_Delete_country_holidays_holidayId,
"/availability-holidays/{holidayId}": delete_Delete_availability_holidays_holidayId,
"/availability-configurations/{configurationId}": delete_Delete_availability_configurations_configurationId,
"/availability-tiers/{tierId}": delete_Delete_availability_tiers_tierId,
"/availability-tiers/{tierId}/vms/{vmId}": delete_Delete_availability_tiers_tierId_vms_vmId,
"/availability-interruptions/{interruptionId}": delete_Delete_availability_interruptions_interruptionId,
"/scheduled-activities/{activityId}/execution/evidence/{evidenceId}": delete_Delete_scheduled_activities_activityId_execution_evidence_evidenceId,
"/scheduled-activities/{activityId}/execution/findings/{findingId}": delete_Delete_scheduled_activities_activityId_execution_findings_findingId,
"/scheduled-activities/{id}/rca": delete_Delete_scheduled_activities_id_rca,
"/operational-plans/{id}": delete_Delete_operational_plans_id,
"/scheduled-activities/{id}": delete_Delete_scheduled_activities_id,
"/scheduled-activities/{activityId}/scope-items/{scopeItemId}": delete_Delete_scheduled_activities_activityId_scope_items_scopeItemId,
"/scheduled-activities/{activityId}/tasks/{taskId}": delete_Delete_scheduled_activities_activityId_tasks_taskId,
"/change-templates/{templateId}": delete_Delete_change_templates_templateId,
"/change-templates/{templateId}/vms/{virtualMachineId}": delete_Delete_change_templates_templateId_vms_virtualMachineId,
"/countries/{countryId}": delete_Delete_countries_countryId,
"/languages/{languageId}": delete_Delete_languages_languageId,
"/platforms/{platformId}": delete_Delete_platforms_platformId,
"/platform-document-categories/{categoryId}": delete_Delete_platform_document_categories_categoryId,
"/platform-documents/{documentId}": delete_Delete_platform_documents_documentId
         }
     }
     export type EndpointByMethod = typeof EndpointByMethod;
     // </EndpointByMethod>
     

    // <EndpointByMethod.Shorthands>
    export type GetEndpoints = EndpointByMethod["get"]
export type PatchEndpoints = EndpointByMethod["patch"]
export type PutEndpoints = EndpointByMethod["put"]
export type PostEndpoints = EndpointByMethod["post"]
export type DeleteEndpoints = EndpointByMethod["delete"]
    // </EndpointByMethod.Shorthands>
    
  
  